import * as React from 'react';
import axios from 'axios';
import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { Helmet } from 'react-helmet-async';
import toast from 'react-hot-toast';
import { getGetConversationsQueryKey } from '@/api/generated/conversations/conversations';
import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
} from '@tanstack/react-query';
import {
  invalidateAllServiceOptionGroups,
  usePrefetchAllServiceOptionGroups,
} from '@/components/calendar/appointment-form';
import { initialSelectedOptions } from '@/components/calendar/edit-appointment/edit-appointment-service-options';
import { buildUpdateAppointmentPayload } from '@/components/calendar/appointment-form/build-update-appointment-payload';
import { buildCreateAppointmentPayload } from '@/components/calendar/appointment-form/build-create-appointment-payload';
import { sameStaffOverlapMessage } from '@/components/calendar/edit-appointment/same-staff-overlap-error';
import {
  createHighlightedAppointmentStore,
  HighlightedAppointmentProvider,
} from '@/components/calendar/highlighted-appointment-store';
import { getGetClientsClientIdGiftCardsQueryKey } from '@/api/generated/clients/clients';
import {
  getGetGiftCardsIdQueryKey,
  getGetGiftCardsQueryKey,
} from '@/api/generated/gift-cards/gift-cards';
import type {
  GetAppointmentGroupsGroupId200,
  AppointmentGroupDetail,
  AppointmentAttribution,
  VisitTurnOutcome,
} from '@/api/generated/schemas';
import { canAddToGroup } from '@/components/calendar/appointment-actions-menu-rules';
import { isDoneUndoableFrom, uncancelRestoreStatus } from '@repo/shared/appointments';
import type { SelectedServiceData } from '@repo/shared/appointment-form';
import { cn } from '@/lib/utils';
import { OperationsViewOnlyBanner, PageCanvas, SplitLayout } from '@/components/layout';
import { Modal, ModalFooter } from '@/components/ui/modal';
import {
  AppointmentConflictModal,
  extractAppointmentConflicts,
  type AppointmentConflictDetail,
} from '@/components/calendar/appointment-conflict-modal';
import { Button } from '@/components/ui/button';
import {
  CalendarGridSkeleton,
  CalendarPageSkeleton,
} from '@/components/calendar/calendar-grid-skeleton';
import {
  ClientDetailDrawer,
  ClientQuickChatDrawer,
  type QuickChatClient,
} from '@/features/clients';
import {
  CalendarHeader,
  StaffColumnHeader,
  CalendarTimeGrid,
  NewAppointmentForm,
  GroupAppointmentForm,
  useBookAppointmentGroup,
  groupVersionParam,
  handleGroupedAppointmentError,
  GroupAppointmentDetailPanel,
  RemoveFromGroupDialog,
  ResendNotificationsDialog,
  groupDetailQueryKey,
  seedGroupDetailCache,
  useAppointmentGroupDetail,
  ordinalClientLabel,
  useGroupReschedule,
  GroupRescheduleForm,
  AppointmentDetailPanel,
  AppointmentDetailSkeleton,
  EditAppointmentForm,
  TimeBlockDetailPanel,
  EditTimeBlockForm,
  StaffFilterPanel,
  DiscardChangesModal,
  WaitingClientsFab,
  WeekDayColumnHeader,
  WeekTimeGrid,
  CheckoutPanel,
  PaymentPanel,
  SaleDetailsPanel,
  type CalendarViewMode,
  type HeaderStaffItem,
  type StaffMember,
  type StaffMenuAction,
  type Appointment,
  type WorkShift,
  type WeekDay,
  type WeekDayData,
  type PreviewAppointment,
  type PreviewChangeData,
  type PreviewService,
  type AppointmentFormData,
  type GroupMemberInput,
  type GroupSwitchPayload,
  type AppointmentDetailData,
  type EditAppointmentSaveData,
  type EditPreviewChangeData,
  type TimeBlockDetailData,
  type EditTimeBlockSaveData,
  type EditTimeBlockPreviewData,
  type SaleData,
  type CheckoutDraftPayload,
  type PaymentMethod,
  NewTimeBlockForm,
  type TimeBlockFormData,
  type TimeBlockPreviewData,
} from '@/components/calendar';
import type {
  AppointmentDragEndData,
  PreviewDragEndData,
} from '@/components/calendar/use-calendar-drag-drop';
import {
  CancelDeleteAppointmentModal,
  type CancellationType,
} from '@/components/calendar/cancel-delete-appointment-modal';
import {
  getMinutesFromMidnightInTz,
  createDateInTz,
  formatDateStrInTz,
} from '@/lib/timezone-utils';
import { addDaysToDateKey, addSalonDays, anchorSalonDateKey, moveToSalonDay, salonDateKey, salonWeekStart } from '@/lib/salon-date-anchor';
import { nextBookingSlotStart, pickDefaultBookingStaff } from '@/components/calendar/calendar-default-booking-slot';
import {
  rebookClient,
  rebookFirstTechId,
  rebookServices,
  type RebookVisit,
} from '@/components/calendar/rebook-visit-prefill';
import {
  resolveCallerBookingPrefill,
  type CallerBookingPrefill,
} from '@/features/caller-card/caller-card-booking-intent';
import { useSalonTimezone } from '@/hooks/use-salon-timezone';
import { MarketingPageState } from '@/features/marketing/marketing-page-state';
import {
  calendarPrizeBadgeFromAward,
  calendarPrizeCardLabel,
  type CalendarPrizeBadgeView,
} from '@/features/prizes/staff/prize-desk-format';
import {
  calendarReferralBadgeFromReward,
  calendarReferralCardLabel,
  type ReferralDeskBadge,
} from '@/features/referrals/staff/referral-desk-format';
import { statusFillColor } from '@/components/calendar/appointment-status-tone';
import type { SplitMode } from '@/components/calendar/split-services-modal';
import {
  abandonOpenSaleForDone,
  LEGACY_CHECKOUT_BLOCKED_MESSAGE,
  LEGACY_CHECKOUT_FORBIDDEN_MESSAGE,
} from '@/components/calendar/abandon-open-sale-then-complete';
import {
  dismissDoneUndoToast,
  showDoneUndoToast,
  showNoShowUndoToast,
} from '@/components/calendar/appointment-status-undo-toast';
import { READABLE_TOAST_OPTIONS } from '@/components/calendar/calendar-readable-toast';
import {
  normalizeUncancelTarget,
  type UncancelTarget,
} from '@/components/calendar/uncancel-target';
import { apiClient } from '@/api/axios-instance';
import { useGetStaff } from '@/api/generated/staff/staff';
import { useGetLocations } from '@/api/generated/locations/locations';
import { useGetOrganizationCalendarSettings } from '@/api/generated/organization/organization';
import {
  appointmentCardColorStatus,
  calendarCardMarkerFlags,
  isWalkInBookingSource,
  getCalendarHourHeight,
  STATUS_TONE_MAP,
  resolveCalendarDisplaySettings,
  resolveCalendarGridHours,
  isSameTimeLanes,
  orderLinesByLane,
  sameTimeLineMarker,
  toVisitLane,
  type VisitLane,
  formLineTimeOrder,
  timeOrderPositions,
} from '@repo/shared/calendar';
import { useCalendarGridWindow } from '@/components/calendar/use-calendar-grid-window';
import { useCalendarDayGridGeometry } from '@/components/calendar/use-calendar-day-grid-geometry';
import { CALENDAR_DAY_SCROLLER_PHONE_SNAP_CLASS } from '@/components/calendar/calendar-day-grid-geometry';
import { resolvePreviewCards } from '@/components/calendar/calendar-preview-cards';
import { autoHiddenStaffIds, autoHideStaffKey, staffIdsWithAppointmentCards } from '@/components/calendar/calendar-auto-hidden-staff-ids';
import { useGroupBookingSettings } from '@/features/group-booking/use-group-booking-settings';
import { useGetOrganizationCancellationSettings } from '@/api/generated/organization/organization';
import { getOrganizationCancellationSettingsOrgQueryKey } from '@/api/organization-query-keys';
import { useRealtimeCalendar } from '@/hooks/use-realtime-calendar';
import { calendarGridLiveRefresh, gridShowsOtherStatus } from '@/components/calendar/calendar-grid-live-refresh';
import {
  CALENDAR_GRID_KEYS,
  cancelGridReadsBeforeWrite,
  refreshCalendarAfterBroadcastWrite,
  scheduleCalendarRefresh,
  useCalendarRealtimeLive,
} from '@/hooks/calendar-realtime-refresh';
import {
  CALENDAR_STAFF_LIST_PARAMS,
  calendarDayQueryOptions,
  calendarSettingsQueryConfig,
  keepCalendarColumnRows,
} from '@/components/calendar/calendar-page-query-options';
import { useStableHandler } from '@/hooks/use-stable-handler';
import { CalendarGridRefreshBar } from '@/components/calendar/calendar-grid-refresh-bar';
import { readSalonTimezoneHint, writeSalonTimezoneHint } from '@/lib/salon-timezone-hint-storage';
import { usePanelStatusFollowsGrid } from '@/components/calendar/use-panel-status-follows-grid';
import type { AppointmentCardHold } from '@/features/no-show-protection/card/appointment-card-row-copy';
import { usePanelNoShowProtectionFollowsGrid } from '@/features/no-show-protection/use-panel-no-show-protection-follows-grid';
import type { ClientOwedFee, NoShowFeeView } from '@/features/no-show-protection/fees/no-show-fee-format';
import { toastNoShowFeeLock } from '@/features/no-show-protection/fees/no-show-fee-errors';
import { ArrivalToastHost } from '@/features/check-in-arrivals/arrival-toast-host';
import { useVisitTurnNotice } from '@/features/visits/use-visit-turn-notice';
import { useCanWriteOperational, useOperationsViewOnly, useOrg } from '@/providers';
import { guardCalendarRequest } from '@/components/calendar/calendar-request-guard';
import {
  type BatchScheduleData,
  findStaffScheduleDay,
  STAFF_SCHEDULES_BATCH_KEY,
  staffSchedulesBatchQueryOptions,
} from '@/components/calendar/staff-schedules-batch-query';
import { useCalendarPermissionAccess } from '@/hooks/use-calendar-permission-access';
import { useCalendarPanelEscapeClose } from '@/hooks/use-calendar-panel-escape-close';
import { useRestoreFocusOnCalendarPanelClose } from '@/hooks/use-restore-focus-on-calendar-panel-close';
import { useRevealCalendarPanelColumnOnOpen } from '@/hooks/use-reveal-calendar-panel-column-on-open';
import { useMediaQuery } from '@/hooks/use-media-query';
import { BREAKPOINT_QUERY } from '@/hooks/breakpoint-queries';
import {
  calendarColumnKeyForAnchor,
} from '@/components/calendar/calendar-scroll-column-into-view';
import {
  resolveCalendarPanelAnchor,
  type CalendarGridTarget,
} from '@/components/calendar/calendar-panel-anchor';
import { CalendarFeesReview } from '@/features/no-show-protection/review/calendar-fees-review';
import { NewOnlineBookingsPill } from '@/components/calendar/new-online-bookings-pill';
import { useSalonSetup } from '@/hooks/use-salon-setup';
import { invalidateFeesReview } from '@/features/no-show-protection/review/use-fees-review';
import { useClientPermissionAccess } from '@/hooks/use-client-permission-access';
import { getGetStaffMeAccessQueryKey } from '@/api/generated/staff/staff';
import {
  getStaffStaffIdSchedulesWeek,
  usePostStaffStaffIdSchedulesOverrides,
} from '@/api/generated/staff-schedules/staff-schedules';
import { buildSaleItemsPatchPayload, hasSaleItemsPatchChanges } from '@/lib/sale-items-patch';
import {
  useSaleMutations,
  isHandledSaleMutationError,
  SaleStaleError,
  isGroupSale,
  saleCompletionInvalidationKeys,
} from '@/features/sales/checkout';
import type { Client } from '@/features/clients/components/client-detail-drawer-types';
import { updateCachedClientLists } from '@/features/clients/components/client-detail-drawer-utils';
import { formatDocumentTitle } from '@/lib/document-title';
import { getApiErrorMessage, isForbiddenError, LocalActionError } from '@/lib/error-utils';
import { applyCalendarLineDrop } from '@/components/calendar/same-time-drag';
import { applySavedScheduleToCache } from '@/components/calendar/saved-appointment-schedule-cache';

function invalidateGiftCardCachesForSale(
  queryClient: QueryClient,
  sale: SaleData,
  redeemedGiftCardId?: string
) {
  const giftCardLineItems = sale.lineItems.filter((item) => item.itemType === 'GIFT_CARD');
  const shouldRefreshGiftCards = giftCardLineItems.length > 0 || Boolean(redeemedGiftCardId);
  if (!shouldRefreshGiftCards) return;

  const giftCardIds = new Set<string>();
  const clientIds = new Set<string>();
  const saleClientId = sale.client?.id ?? sale.clientId ?? null;

  if (redeemedGiftCardId) giftCardIds.add(redeemedGiftCardId);
  if (saleClientId) clientIds.add(saleClientId);

  for (const item of giftCardLineItems) {
    if (item.giftCardId) giftCardIds.add(item.giftCardId);
    if (item.giftCardOwnerClientId) clientIds.add(item.giftCardOwnerClientId);
  }

  queryClient.invalidateQueries({ queryKey: getGetGiftCardsQueryKey() });
  giftCardIds.forEach((id) => {
    queryClient.invalidateQueries({ queryKey: getGetGiftCardsIdQueryKey(id) });
  });
  clientIds.forEach((clientId) => {
    queryClient.invalidateQueries({ queryKey: getGetClientsClientIdGiftCardsQueryKey(clientId) });
  });
}

type CalendarSearch = { visit?: string; book?: string; rebook?: string };

const searchId = (value: unknown) => (typeof value === 'string' && value ? value : undefined);

export const Route = createFileRoute('/_auth/$orgId/calendar')({
  // Deep links, consumed on arrival: `?visit=<appointmentId>` opens that visit's panel on its day (Turns ›
  // "Visit on Calendar", Caller card › Open appointment); `?book=<callId>` opens a New Appointment for that
  // call's caller (Caller card › New appointment — the call id only, never the phone number); `?rebook=<appointmentId>`
  // opens a New Appointment with that past visit's client and services (client profile › Rebook appointment).
  validateSearch: (search: Record<string, unknown>): CalendarSearch => ({
    visit: searchId(search.visit),
    book: searchId(search.book),
    rebook: searchId(search.rebook),
  }),
  component: CalendarPage,
});

// Staff color assignment based on index
const STAFF_COLORS: Array<'teal' | 'orange' | 'pink' | 'teal'> = ['teal', 'orange', 'pink', 'teal'];

/** Map appointment color names to token-backed background values. */
const COLOR_BG_MAP: Record<string, string> = {
  teal: 'var(--c-service-mint-bg)',
  purple: 'var(--c-service-lilac-bg)',
  pink: 'var(--c-service-peach-bg)',
  orange: 'var(--c-service-butter-bg)',
  gray: 'var(--c-status-completed-bg)',
};

/** Build 7 WeekDay objects starting from the given date
 *  When orgTimezone provided, uses org TZ for date strings and isToday flag */
function buildWeekDays(weekStart: Date, orgTimezone?: string): WeekDay[] {
  const shortDayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const todayStr = salonDateKey(new Date(), orgTimezone);
  return Array.from({ length: 7 }, (_, i) => {
    // Whole salon days (DST days are 23/25h), each anchored at salon noon
    const d = addSalonDays(weekStart, i, orgTimezone);
    const dateStr = salonDateKey(d, orgTimezone);
    // Get day name and number in org timezone
    const dayParts = orgTimezone
      ? new Intl.DateTimeFormat('en-US', {
          timeZone: orgTimezone,
          weekday: 'short',
          day: 'numeric',
        }).formatToParts(d)
      : null;
    const dayName = dayParts
      ? dayParts.find((p) => p.type === 'weekday')?.value || shortDayNames[d.getDay()]
      : shortDayNames[d.getDay()];
    const dayNumber = dayParts
      ? parseInt(dayParts.find((p) => p.type === 'day')?.value || String(d.getDate()))
      : d.getDate();
    return {
      dateStr,
      dayName,
      dayNumber,
      isToday: dateStr === todayStr,
    };
  });
}

/** Shape returned by GET /calendar/day API */
interface CalendarAppointmentFromApi {
  hasHiddenServices?: boolean;
  id: string;
  startTime: string;
  endTime: string;
  status: string;
  appointmentType: string;
  bookingSource: string;
  /** Booked by the AI phone assistant (`bookingSource` stays ONLINE): headset instead of the globe. */
  bookedByPhoneAssistant?: boolean;
  primaryClient: {
    id: string;
    firstName: string;
    lastName: string | null;
    avatarUrl: string | null;
    phone: string | null;
    email: string | null;
    createdAt: string;
    isNew: boolean;
    notes: string | null;
    /** Staff-only Heads-up (⚠ on the card); optional: an older cached day has none. */
    headsUpNote?: string | null;
    /** Client was deleted (archived) after booking */
    isDeleted: boolean;
  } | null;
  services: Array<{
    id: string;
    serviceId: string;
    serviceName: string;
    staffId: string;
    staffName: string;
    startTime: string;
    endTime: string;
    duration: number;
    /** Prep/cleanup minutes snapshotted at booking (optional: optimistic cache rows may omit them). */
    bufferBeforeMinutes?: number;
    bufferAfterMinutes?: number;
    priceCents: number;
    isStaffRequested: boolean;
    /** Same-time lane (0 = main, 1 = beside lane 0); optimistic rows built from an older detail may omit it. */
    lane?: VisitLane;
    /** Resolved booked add-ons, in booked order */
    selectedOptions: AppointmentDetailData['services'][number]['selectedOptions'];
  }>;
  notes: string | null;
  bookedAt: string;
  bookedBy: { id: string; name: string } | null;
  lastModifiedAt: string | null;
  lastModifiedBy: { id: string; name: string } | null;
  /** Cancellation classification (NORMAL/LATE/NO_SHOW) or null for active appts */
  cancellationType?: 'NORMAL' | 'LATE' | 'NO_SHOW' | null;
  /** Status held before cancellation — drives the original card color while faded */
  statusBeforeCancellation?: string | null;
  /** True for visible cancelled/no-show appts that render faded & read-only */
  isFadedCancel?: boolean;
  /** The visit has lines in lane 0 and lane 1 (hands & feet at the same time) — computed server-side over all lines */
  isSameTimeVisit?: boolean;
  /** Marketing campaign this booking is attributed to; null when unattributed */
  attributedCampaign?: { id: string; name: string } | null;
  /** New Client Offer badge while a claim is booked on / redeemed by this visit; null otherwise */
  offer?: { label: string; offerKind?: 'NEW_CLIENT' | 'VOUCHER' } | null;
  /** Monthly Prize trophy (the client's running prize, this visit's month state); null otherwise */
  prize?: CalendarPrizeBadgeView | null;
  /** Referral reward heart (the client's reward on this visit); null when none, cancelled, or services are hidden */
  referral?: ReferralDeskBadge | null;
  /** Group membership hint (glyph/navigation) when part of a group; null otherwise */
  group?: { id: string; version: number; memberCount: number } | null;
  // Detail-panel fields carried on the grid so the panel paints complete from this cache.
  /** Where this client came from; null when never decided or services are hidden from the viewer */
  attribution: AppointmentAttribution | null;
  /** New Client Offer claim (status, used/missed, Undo state); null when none or services are hidden */
  offerClaim: NonNullable<AppointmentDetailData['offerClaim']> | null;
  /** Monthly Prize row (ready / used by this visit / already used); null when none or services are hidden */
  prizeAward: NonNullable<AppointmentDetailData['prizeAward']> | null;
  /** Referral reward row (ready / used by this visit / saved for later); null when none or services are hidden */
  referralReward?: AppointmentDetailData['referralReward'];
  /** No-show protection card state (secured / requested / none); null/absent when protection is off and no hold */
  cardHold?: AppointmentCardHold | null;
  /** No-show / late-cancel fee (null when none applies; absent on optimistic rows) */
  noShowFee?: NoShowFeeView | null;
  /** Declined fees the client owes as cardholder ("Owes $25 no-show fee from Oct 11") */
  clientOwedFees?: ClientOwedFee[];
  /** Latest WAITING-row notify timestamp; null when never notified */
  notifiedAt: string | null;
  /** Latest automatic-reassignment note ("Moved from … to honor a request"); null when never moved */
  reassignmentNote: string | null;
  /** Name an online booker typed when it names someone other than the client ("Booked as"); else null */
  bookedAsName: string | null;
  /** Check-in off: when the client came in by Hayven Tap that salon day (green ✓); absent/null otherwise */
  cameInAt?: string | null;
}

/**
 * Transform API CalendarAppointment[] (from GET /calendar/day) into Appointment[] cards for grid.
 * Each service becomes a separate card positioned under its assigned staff column.
 */
function transformCalendarAppointments(
  apiAppointments: CalendarAppointmentFromApi[],
  canEdit: (appointment: CalendarAppointmentFromApi) => boolean,
  /** The salon runs no Hayven Check-in: cards grey out once their time passes (nobody marks a status). */
  { closesQuietly }: { closesQuietly: boolean }
): Appointment[] {
  return apiAppointments.flatMap((apt) => {
    const clientName = apt.primaryClient
      ? `${apt.primaryClient.firstName} ${apt.primaryClient.lastName ?? ''}`.trim()
      : 'Walk-in';
    const totalServices = apt.services.length;

    // Cancelled/no-show cards keep the color they had before cancellation (faded +
    // struck through), so resolve color from statusBeforeCancellation when faded.
    const colorStatus = appointmentCardColorStatus(apt);
    const visitEndTime = new Date(apt.endTime);
    // Lines the viewer sees, for the ⇉ marker's partner names (tooltip + aria label; never from hidden lines).
    const laneLines = apt.services.map((svc) => ({
      id: svc.id, lane: svc.lane, staffId: svc.staffId, staffName: svc.staffName,
      start: new Date(svc.startTime).getTime(), end: new Date(svc.endTime).getTime(),
    }));
    // "(n/N)" counts the lines in the order they happen, not in stored (lane, sortOrder) order.
    const positions = timeOrderPositions(laneLines);

    // Use per-service startTime/endTime from API (supports gaps between services)
    return apt.services.map((svc, idx) => {
      const sameTime = sameTimeLineMarker(laneLines, svc.id, apt.isSameTimeVisit ?? false);
      return {
        id: `${apt.id}-svc-${svc.id}`,
        appointmentId: apt.id,
        serviceLineId: svc.id,
        canDrag: canEdit(apt),
        staffId: svc.staffId,
        serviceType: svc.serviceName,
        clientName,
        startTime: new Date(svc.startTime),
        endTime: new Date(svc.endTime),
        status:
          colorStatus === 'CONFIRMED'
            ? ('confirmed' as const)
            : colorStatus === 'UNCONFIRMED'
              ? ('unconfirmed' as const)
              : colorStatus === 'WAITING'
                ? ('waiting' as const)
                : colorStatus === 'IN_SERVICE'
                  ? ('in-service' as const)
                  : colorStatus === 'CHECKING_OUT'
                    ? ('checking-out' as const)
                    : colorStatus === 'COMPLETED'
                      ? ('completed' as const)
                      : undefined,
        color: 'teal' as const,
        // Status tone (pale fill + status edge); cancelled/no-show keeps its pre-cancel tone.
        statusTone: STATUS_TONE_MAP[colorStatus],
        isRequested: svc.isStaffRequested,
        ...calendarCardMarkerFlags(apt, svc),
        // The came-in check is a no-check-in fact: a check-in salon (where the API also sends `cameInAt`) keeps its
        // cards as they were.
        cameIn: closesQuietly && Boolean(apt.cameInAt),
        headsUpNote: apt.primaryClient?.headsUpNote ?? null,
        visitEndTime,
        isRecurring: false,
        isBreak: false,
        isFadedCancel: apt.isFadedCancel ?? false,
        closesQuietly,
        serviceIndex: totalServices > 1 ? positions[idx] : undefined,
        totalServices: totalServices > 1 ? totalServices : undefined,
        campaignName: apt.attributedCampaign?.name,
        offerLabel: apt.offer?.label,
        offerKind: apt.offer?.offerKind,
        prizeLabel: calendarPrizeCardLabel(apt.prize),
        referralLabel: calendarReferralCardLabel(apt.referral),
        // Group membership hint (drives the group glyph); undefined when ungrouped.
        groupId: apt.group?.id,
        groupVersion: apt.group?.version,
        groupMemberCount: apt.group?.memberCount,
        isSameTimeVisit: sameTime.isSameTime,
        sameTimePartnerNames: sameTime.partnerNames,
        // Booking-time snapshot (not the service's current value) — drawn as prep/cleanup zones.
        bufferBeforeMinutes: svc.bufferBeforeMinutes,
        bufferAfterMinutes: svc.bufferAfterMinutes,
      };
    });
  });
}

/**
 * Loose shape for the day/week calendar query caches. Optimistic updates only ever
 * touch `appointments`, so the rest of the payload is preserved untyped via spread.
 */
type CalendarCacheData = {
  appointments?: CalendarAppointmentFromApi[];
} & Record<string, unknown>;

/** Return a new cache with `patch` merged into `appointmentId`'s grid row. */
function patchAppointmentInCache(
  old: CalendarCacheData | undefined,
  appointmentId: string,
  patch: Partial<CalendarAppointmentFromApi>
): CalendarCacheData | undefined {
  if (!old?.appointments) return old;
  return {
    ...old,
    appointments: old.appointments.map((apt) =>
      apt.id === appointmentId ? { ...apt, ...patch } : apt
    ),
  };
}

/**
 * Optimistic status patch. Entering WAITING starts a fresh wait with no client notified yet (the
 * server reads `notifiedAt` from the latest WAITING row), so a stale "Notified …" never carries over.
 */
function statusPatch(status: string): { status: string; notifiedAt?: null } {
  return status === 'WAITING' ? { status, notifiedAt: null } : { status };
}

/** Return a new cache with `appointmentId`'s status patched — for optimistic status changes. */
function patchAppointmentStatusInCache(
  old: CalendarCacheData | undefined,
  appointmentId: string,
  status: string
): CalendarCacheData | undefined {
  return patchAppointmentInCache(old, appointmentId, statusPatch(status));
}

/** Return a new cache with `created` appended (deduped by id) — for optimistic inserts. */
function insertAppointmentIntoCache(
  old: CalendarCacheData | undefined,
  created: CalendarAppointmentFromApi
): CalendarCacheData | undefined {
  if (!old?.appointments) return old;
  if (old.appointments.some((apt) => apt.id === created.id)) return old;
  return { ...old, appointments: [...old.appointments, created] };
}

/**
 * Map an appointment detail payload (POST /appointments, GET /appointments/:id) into the
 * calendar-grid appointment shape so it can be injected straight into the day cache without
 * a refetch round-trip.
 */
function detailToCalendarAppointment(d: AppointmentDetailData): CalendarAppointmentFromApi {
  return {
    id: d.id,
    hasHiddenServices: d.hasHiddenServices,
    startTime: d.startTime,
    endTime: d.endTime,
    status: d.status,
    appointmentType: '',
    bookingSource: d.bookingSource ?? 'STAFF',
    bookedByPhoneAssistant: d.bookedByPhoneAssistant ?? false,
    primaryClient: d.primaryClient
      ? {
          id: d.primaryClient.id,
          firstName: d.primaryClient.firstName,
          lastName: d.primaryClient.lastName,
          avatarUrl: d.primaryClient.avatarUrl,
          phone: d.primaryClient.phone,
          email: d.primaryClient.email,
          createdAt: d.primaryClient.createdAt,
          // Detail payload carries no isNew flag; the background refetch reconciles
          // the "new client" dot moments later.
          isNew: false,
          notes: d.primaryClient.notes,
          isDeleted: d.primaryClient.isDeleted ?? false,
        }
      : null,
    services: d.services.map((svc) => ({
      id: svc.id,
      serviceId: svc.service.id,
      serviceName: svc.service.name,
      staffId: svc.staff.id,
      staffName: svc.staff.name,
      startTime: svc.startTime,
      endTime: svc.endTime,
      duration: svc.duration,
      priceCents: svc.priceCents,
      isStaffRequested: svc.isStaffRequested,
      lane: svc.lane,
      selectedOptions: svc.selectedOptions,
    })),
    notes: d.notes,
    bookedAt: d.bookedAt,
    bookedBy: d.bookedBy,
    lastModifiedAt: d.lastModifiedAt,
    lastModifiedBy: d.lastModifiedBy,
    cancellationType: d.cancellationType ?? null,
    statusBeforeCancellation: d.statusBeforeCancellation ?? null,
    isFadedCancel: false,
    isSameTimeVisit: d.isSameTimeVisit ?? false,
    // Server details always carry `group` (null for singles); undefined only on a detail built
    // without it, which then leaves a row's own group hint alone.
    group:
      d.group === undefined
        ? undefined
        : d.group && { id: d.group.id, version: d.group.version, memberCount: d.group.memberCount },
    offer:
      d.offerClaim && (d.offerClaim.status === 'BOOKED' || d.offerClaim.status === 'REDEEMED')
        ? { label: d.offerClaim.label }
        : null,
    attribution: d.attribution ?? null,
    offerClaim: d.offerClaim ?? null,
    prize: calendarPrizeBadgeFromAward(d.prizeAward),
    prizeAward: d.prizeAward ?? null,
    referral: calendarReferralBadgeFromReward(d.referralReward),
    referralReward: d.referralReward ?? null,
    // Create/update responses carry it too; absent (older payload) leaves the field off the row.
    cardHold: d.cardHold,
    noShowFee: d.noShowFee,
    clientOwedFees: d.clientOwedFees,
    notifiedAt: d.notifiedAt ?? null,
    reassignmentNote: d.reassignmentNote ?? null,
    bookedAsName: d.bookedAsName ?? null,
  };
}

/**
 * Build the local checkout PREVIEW sale for a single appointment (id/version 0 — no persisted sale
 * exists until "Go to Payments"). Shared by the detail-panel Checkout action and the group member
 * ••• Checkout (individual endpoint), so both open the identical single checkout panel.
 */
function buildLocalCheckoutSalePreview(appointment: AppointmentDetailData): SaleData {
  const subtotalCents = appointment.services.reduce((sum, svc) => sum + svc.priceCents, 0);
  const now = new Date().toISOString();
  return {
    id: '', // No sale ID yet - will be created when clicking "GO TO PAYMENTS"
    organizationId: '',
    locationId: null,
    clientId: appointment.primaryClient?.id ?? null,
    appointmentId: appointment.id,
    version: 0, // local preview only; no persisted sale exists until "GO TO PAYMENTS"
    saleNumber: 0,
    status: 'OPEN',
    subtotalCents,
    discountCents: 0,
    taxCents: 0,
    tipCents: 0,
    totalCents: subtotalCents,
    openedAt: now,
    completedAt: null,
    notes: null,
    lineItems: appointment.services.map((svc, idx) => ({
      id: svc.id,
      itemType: 'SERVICE' as const,
      itemName: svc.service.name,
      description: null,
      quantity: 1,
      unitPriceCents: svc.priceCents,
      priceCents: svc.priceCents,
      discountCents: 0,
      totalCents: svc.priceCents,
      priceModified: false,
      priceModifiedReason: null,
      discountPercent: null,
      staffId: svc.staff.id,
      staffName: svc.staff.name,
      serviceId: svc.service.id,
      // The visit this line bills — lets checkout give the visit's New Client Offer on it.
      appointmentId: appointment.id,
      appointmentServiceId: svc.id,
      sortOrder: idx,
    })),
    payments: [],
    client: appointment.primaryClient
      ? {
          id: appointment.primaryClient.id,
          firstName: appointment.primaryClient.firstName,
          lastName: appointment.primaryClient.lastName,
          phone: appointment.primaryClient.phone,
        }
      : null,
    createdAt: now,
    updatedAt: now,
  };
}

/** Side panel mode: 'none' = nothing open (no panel, grid takes the full width), else the open view. */
type PanelMode =
  | 'none'
  | 'new-form'
  | 'new-group-form'
  | 'new-time-block'
  | 'group-detail'
  | 'group-reschedule'
  | 'detail'
  | 'detail-loading'
  | 'edit'
  | 'time-block-detail'
  | 'edit-time-block'
  | 'checkout'
  | 'payment'
  | 'sale-details';

// Tablets keep the panel as an inset card; phones and desktop fill it flush (the PageCanvas rule).
const calendarPanelCardClass =
  'h-full overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border bg-clovie-surface shadow-float max-sm:rounded-none max-sm:border-0 max-sm:shadow-none lg:rounded-none lg:border-0 lg:shadow-none';

function CalendarPanelCard({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) {
  return (
    <div className="h-full bg-clovie-canvas p-0 sm:p-3 lg:p-0">
      <div className={cn(calendarPanelCardClass, className)}>{children}</div>
    </div>
  );
}

// buildSaleItemsPatchPayload & hasSaleItemsPatchChanges moved to @/lib/sale-items-patch

function CalendarPage() {
  const { orgId } = Route.useParams();
  // Signed in to this salon's workspace: may message clients and use the staff header menu.
  const hasOperationalAccess = useCanWriteOperational();
  // Agency team accounts see the calendar but change nothing on it (the API refuses their writes).
  // Page-local on purpose: the shared provider value still lets them work in Clients, Messages, Settings.
  const operationsViewOnly = useOperationsViewOnly();
  const canWriteOperational = hasOperationalAccess && !operationsViewOnly;
  const calendarAccess = useCalendarPermissionAccess();
  const clientAccess = useClientPermissionAccess();
  // Hayven Check-in (`setup.checkIn`, the API's attendance guard reads the same). Off once known: a booking screen (no
  // waiting list, no no-show fees, passed bookings grey, "N new online bookings"); unknown keeps today's calendar.
  const { setup: salonSetup, isResolved: setupResolved } = useSalonSetup();
  const checkInOff = setupResolved && !salonSetup.checkIn;
  // orgId from the URL is the 6-digit shortCode (used for navigation).
  // Realtime channels are keyed by the Clerk org id, which the API broadcasts on
  // (e.g. org:{clerkOrgId}:calendar) — so subscribe with that, not the shortCode.
  const { orgId: clerkOrgId, shortCode } = useOrg();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { visit: linkedVisitId, book: bookCallId, rebook: rebookVisitId } = Route.useSearch();
  // All sale writes go through this hook: identity for shared/group sales, byte-identical bodies
  // for individual sales, plus centralized retry/stale handling (see features/sales/checkout).
  const saleMutations = useSaleMutations();

  // Subscribe to real-time calendar updates from other clients/tabs
  useRealtimeCalendar(clerkOrgId);
  // The grids poll fast only while that feed is down (see calendarGridLiveRefresh).
  const gridLiveRefresh = calendarGridLiveRefresh(useCalendarRealtimeLive());

  // Warm every service's add-on groups up front so "+ Add Option" shows instantly in the
  // New/Edit forms. Same write gate as those forms (the endpoint rejects read-only roles).
  usePrefetchAllServiceOptionGroups(canWriteOperational);

  // State for calendar controls
  const [selectedDate, setSelectedDate] = React.useState(() => new Date());
  const [viewMode, setViewMode] = React.useState<CalendarViewMode>('day');

  // Week view: selected staff ID (auto-set to first staff when entering week mode)
  const [selectedWeekStaffId, setSelectedWeekStaffId] = React.useState<string | null>(null);

  // Staff filter state: null = "All Staff" mode, Set = specific staff IDs visible
  const [visibleStaffIds, setVisibleStaffIds] = React.useState<Set<string> | null>(null);
  const [isFilterPanelOpen, setIsFilterPanelOpen] = React.useState(false);

  // State for new appointment form (when user clicks empty slot, or books from a call)
  const [newAppointment, setNewAppointment] = React.useState<{
    staffId: string;
    staffName: string;
    dateTime: Date;
    /**
     * Booking from a call or a rebooking: who (and, rebooking, which services) the form starts with; `key` remounts
     * the form per link (read on mount).
     */
    prefill?: Omit<CallerBookingPrefill, 'clientLoadFailed'> & { services?: SelectedServiceData[]; key: number };
  } | null>(null);

  // State for preview data from form (services and client)
  const [previewData, setPreviewData] = React.useState<PreviewChangeData | null>(null);

  // State for the group booking draft (front-end-only until Book — Phase 06)
  const [newGroupAppointment, setNewGroupAppointment] = React.useState<{
    staffId: string;
    staffName: string;
    dateTime: Date;
    firstMember?: GroupMemberInput;
    firstClient?: GroupSwitchPayload['client'];
  } | null>(null);
  // Merged member previews the group form emits for the calendar grid
  const [groupPreviews, setGroupPreviews] = React.useState<PreviewAppointment[] | null>(null);
  const [isGroupFormDirty, setIsGroupFormDirty] = React.useState(false);

  // Currently selected appointment for detail panel
  const [selectedAppointmentDetail, setSelectedAppointmentDetail] =
    React.useState<AppointmentDetailData | null>(null);
  // An appointment opened with no grid-cache stub (member outside the loaded window, Waiting FAB) is
  // fetched from the server; until it lands the panel shows a detail skeleton instead of staying on
  // the default view and popping in. The ref is the source of truth for "is this fetch still
  // wanted" — a late response for a superseded open is dropped (never shows the wrong appointment).
  const [pendingDetailId, setPendingDetailId] = React.useState<string | null>(null);
  const pendingDetailIdRef = React.useRef<string | null>(null);
  const cancelPendingDetail = React.useCallback(() => {
    pendingDetailIdRef.current = null;
    setPendingDetailId(null);
  }, []);

  // Booked group detail panel (Phase 08): the open group's id, and — while a single member's
  // appointment is open in "member mode" — the group to return to via the header back chevron.
  const [selectedGroupId, setSelectedGroupId] = React.useState<string | null>(null);
  // Blocks that light up while a group panel member card or an appointment panel service row is
  // hovered (both panels report null on leave / unmount, so it never outlives the open panel). A
  // store, not page state: a hover re-renders only the affected cards, not this page + grid.
  const [highlightStore] = React.useState(createHighlightedAppointmentStore);
  // Group panel member hover lights every block of that member's appointment.
  const highlightGroupMember = React.useCallback(
    (appointmentId: string | null) =>
      highlightStore.set(appointmentId ? { appointmentId } : null),
    [highlightStore]
  );
  const [groupMemberBack, setGroupMemberBack] = React.useState<{
    groupId: string;
    title: string;
  } | null>(null);
  // True while the group panel's add-member editor holds unsaved edits (guards external navigation).
  const [isGroupDetailDirty, setIsGroupDetailDirty] = React.useState(false);
  // Phase 10 member management. "Remove from Group" target (group + member appointment), and the
  // non-primary member queued for the Cancel / Delete modal (opened from the group panel).
  const [removeMemberTarget, setRemoveMemberTarget] = React.useState<{
    groupId: string;
    appointmentId: string;
  } | null>(null);
  const [memberCancelTarget, setMemberCancelTarget] = React.useState<{
    groupId: string;
    appointmentId: string;
    name: string;
  } | null>(null);
  // Phase 13 resend notifications target: the group, plus the member appointment for member mode
  // (null = whole-group resend from the group header menu).
  const [resendTarget, setResendTarget] = React.useState<{
    groupId: string;
    appointmentId: string | null;
  } | null>(null);

  // Appointment being edited (edit form open)
  const [editingAppointment, setEditingAppointment] = React.useState<AppointmentDetailData | null>(
    null
  );

  // Preview data from edit form (for calendar grid preview)
  const [editPreviewData, setEditPreviewData] = React.useState<EditPreviewChangeData | null>(null);

  // State for new time block form
  const [newTimeBlock, setNewTimeBlock] = React.useState<{
    staffId: string;
    staffName: string;
    dateTime: Date;
  } | null>(null);
  const [timeBlockPreviewData, setTimeBlockPreviewData] =
    React.useState<TimeBlockPreviewData | null>(null);

  // Time block detail/edit state
  const [selectedTimeBlockDetail, setSelectedTimeBlockDetail] =
    React.useState<TimeBlockDetailData | null>(null);
  const [editingTimeBlock, setEditingTimeBlock] = React.useState<TimeBlockDetailData | null>(null);
  const [isEditTimeBlockDirty, setIsEditTimeBlockDirty] = React.useState(false);
  const [editTimeBlockDragStartOverride, setEditTimeBlockDragStartOverride] =
    React.useState<Date | null>(null);
  const [editTimeBlockDragStaffOverride, setEditTimeBlockDragStaffOverride] = React.useState<
    string | null
  >(null);
  const [editTimeBlockPreview, setEditTimeBlockPreview] =
    React.useState<EditTimeBlockPreviewData | null>(null);

  // Checkout state
  const [checkoutSale, setCheckoutSale] = React.useState<SaleData | null>(null);
  const [checkoutAppointment, setCheckoutAppointment] =
    React.useState<AppointmentDetailData | null>(null);

  // Payment panel state (shown after "Go to Payments")
  const [isPaymentView, setIsPaymentView] = React.useState(false);
  const [paymentSaleNotes, setPaymentSaleNotes] = React.useState('');
  const [isGoingToPayments, setIsGoingToPayments] = React.useState(false);

  // Completed sale view state (shown after payment is processed)
  const [completedSale, setCompletedSale] = React.useState<SaleData | null>(null);
  const [isProcessingPayment, setIsProcessingPayment] = React.useState(false);

  // Track checkout panel dirty state
  const [isCheckoutDirty, setIsCheckoutDirty] = React.useState(false);
  // True while a group-checkout recipient add/remove (PUT /sales/:id/appointments) is in flight.
  const [isMutatingRecipients, setIsMutatingRecipients] = React.useState(false);

  // While a group-sourced sale is open in the checkout panel, read its group so the panel can offer
  // add-recipient chips + redaction-safe name fallbacks (remove/add route to updateAppointments).
  const openGroupSaleGroupId =
    checkoutSale && isGroupSale(checkoutSale) ? (checkoutSale.sourceGroupId ?? null) : null;
  const { data: checkoutGroupData } = useAppointmentGroupDetail(openGroupSaleGroupId);
  const checkoutGroup = checkoutGroupData?.data;

  // Client detail drawer state (opened when clicking client name)
  const [drawerClientId, setDrawerClientId] = React.useState<string | null>(null);
  // Track payment panel dirty state
  const [isPaymentDirty, setIsPaymentDirty] = React.useState(false);

  // Track whether new appointment form or edit form has unsaved changes
  const [isNewFormDirty, setIsNewFormDirty] = React.useState(false);
  const [isEditFormDirty, setIsEditFormDirty] = React.useState(false);
  const [isTimeBlockFormDirty, setIsTimeBlockFormDirty] = React.useState(false);
  const [showDiscardModal, setShowDiscardModal] = React.useState(false);
  // Pending booking held open when the API reports a staff scheduling conflict,
  // so the user can confirm "Book anyway" (re-submits with allowConflict).
  const [conflictPrompt, setConflictPrompt] = React.useState<{
    formData: AppointmentFormData;
    conflicts: AppointmentConflictDetail[];
  } | null>(null);
  // Same "Book anyway" hold for edits/drag-drop reschedules (re-submits the
  // update with allowConflict). Kept separate because the pending payload shape
  // (EditAppointmentSaveData) and mutation differ from the create flow above.
  const [editConflictPrompt, setEditConflictPrompt] = React.useState<{
    data: EditAppointmentSaveData;
    conflicts: AppointmentConflictDetail[];
  } | null>(null);

  // The grid card last clicked or dropped (its own column, which for a multi-tech or group booking
  // isn't the first service's tech): the side panel keeps this column in view as it opens.
  const [gridTarget, setGridTarget] = React.useState<CalendarGridTarget | null>(null);

  // Drag-and-drop overrides for edit form (pre-applied service/time changes)
  const [dragOverrides, setDragOverrides] = React.useState<{
    services: EditAppointmentSaveData['services'];
    startTime: Date;
  } | null>(null);

  // Whether the edit form was opened via drag-and-drop (enables re-dragging)
  const [isDragEditMode, setIsDragEditMode] = React.useState(false);
  // Incrementing key to force EditAppointmentForm re-mount on subsequent drags
  const [dragEditKey, setDragEditKey] = React.useState(0);

  // Confirmation state for "Set to Not Working" action
  const [notWorkingConfirm, setNotWorkingConfirm] = React.useState<StaffMember | null>(null);

  // Client detail drawer state
  const [clientDetailDrawerClient, setClientDetailDrawerClient] = React.useState<Client | null>(
    null
  );

  // Client message (quick chat) drawer state — opened from the appointment panel
  const [chatClient, setChatClient] = React.useState<QuickChatClient | null>(null);

  // Track appointment that originated the sale view (for client info in sale details panel)
  const [saleSourceAppointment, setSaleSourceAppointment] =
    React.useState<AppointmentDetailData | null>(null);

  const permissionEpoch = React.useRef(0);
  const calendarMounted = React.useRef(true);
  React.useEffect(() => {
    calendarMounted.current = true;
    return () => {
      calendarMounted.current = false;
    };
  }, []);
  const currentCalendarOrg = React.useRef(shortCode);
  currentCalendarOrg.current = shortCode;
  const previousCalendarOrg = React.useRef(shortCode);
  const previousCalendarAccess = React.useRef(calendarAccess);
  const currentCalendarAccess = React.useRef(calendarAccess);
  currentCalendarAccess.current = calendarAccess;
  const resetProtectedCalendarState = React.useCallback(() => {
    permissionEpoch.current += 1;
    setSelectedAppointmentDetail(null);
    cancelPendingDetail();
    setEditingAppointment(null);
    setEditPreviewData(null);
    setDragOverrides(null);
    setIsDragEditMode(false);
    setEditConflictPrompt(null);
    setConflictPrompt(null);
    setNewAppointment(null);
    setPreviewData(null);
    setNewTimeBlock(null);
    setTimeBlockPreviewData(null);
    setSelectedTimeBlockDetail(null);
    setEditingTimeBlock(null);
    setEditTimeBlockPreview(null);
    setEditTimeBlockDragStartOverride(null);
    setEditTimeBlockDragStaffOverride(null);
    setNotWorkingConfirm(null);
    setSelectedWeekStaffId(null);
    setVisibleStaffIds(null);
    setCheckoutSale(null);
    setCheckoutAppointment(null);
    setCompletedSale(null);
    setSaleSourceAppointment(null);
    setDrawerClientId(null);
    setClientDetailDrawerClient(null);
    setChatClient(null);
    setIsNewFormDirty(false);
    setIsEditFormDirty(false);
    setIsTimeBlockFormDirty(false);
    setIsEditTimeBlockDirty(false);
    setIsCheckoutDirty(false);
    setIsPaymentDirty(false);
    setShowDiscardModal(false);
    setIsPaymentView(false);
  }, [cancelPendingDetail]);
  React.useEffect(() => {
    if (
      previousCalendarAccess.current === calendarAccess &&
      previousCalendarOrg.current === shortCode
    )
      return;
    previousCalendarOrg.current = shortCode;
    previousCalendarAccess.current = calendarAccess;
    resetProtectedCalendarState();
    void queryClient.cancelQueries({ queryKey: ['calendar'] });
    void queryClient.resetQueries({ queryKey: ['calendar'] });
  }, [calendarAccess, shortCode, queryClient, resetProtectedCalendarState]);

  const handleCalendarDenial = React.useCallback(
    (error: unknown) => {
      if ((error as { response?: { status?: number } })?.response?.status !== 403) return false;
      resetProtectedCalendarState();
      void queryClient.invalidateQueries({ queryKey: getGetStaffMeAccessQueryKey() });
      void queryClient.cancelQueries({ queryKey: ['calendar'] });
      void queryClient.resetQueries({ queryKey: ['calendar'] });
      return true;
    },
    [queryClient, resetProtectedCalendarState]
  );

  /** True while the access snapshot captured at request start is still the calendar's current one. */
  const captureCalendarAccessSnapshot = React.useCallback(() => {
    const epoch = permissionEpoch.current;
    const access = currentCalendarAccess.current;
    const organization = currentCalendarOrg.current;
    return () =>
      calendarMounted.current &&
      epoch === permissionEpoch.current &&
      access === currentCalendarAccess.current &&
      organization === currentCalendarOrg.current;
  }, []);

  const calendarRequest = React.useCallback(
    <T,>(request: Promise<T>) =>
      guardCalendarRequest(request, captureCalendarAccessSnapshot(), handleCalendarDenial),
    [captureCalendarAccessSnapshot, handleCalendarDenial]
  );

  /**
   * Same stale-access guard, but a refusal stays local: used for sale calls whose 403 only means
   * "this staff member can't touch sales", never that the calendar itself was revoked.
   */
  const calendarSideRequest = React.useCallback(
    <T,>(request: Promise<T>) =>
      guardCalendarRequest(request, captureCalendarAccessSnapshot(), () => undefined),
    [captureCalendarAccessSnapshot]
  );

  const { data: locationsData, refetch: refetchLocations } = useGetLocations();
  // Every grid position, form time and "today" is on the salon clock; the page waits for it below.
  const { timezone: orgTimezone, isLoading: isLoadingTimezone } = useSalonTimezone();
  // Calendar reads only operational policy values, never full settings or message templates.
  // The grid waits for these (visible-hours mode), so a failed read retries once, not three times
  // with backoff: after that the grid paints with the default window.
  const { data: calendarSettings, isLoading: isLoadingCalendarSettings } =
    useGetOrganizationCalendarSettings({ query: calendarSettingsQueryConfig(shortCode) });
  const canNotifyWaitingClient = calendarSettings?.data.canNotifyWaitingClient ?? false;
  // Display Preferences (visible hours + zoom). Sanitized: the DB has no range constraints.
  const calendarDisplay = React.useMemo(
    () => resolveCalendarDisplaySettings(calendarSettings?.data),
    [calendarSettings]
  );
  const calendarHourHeight = getCalendarHourHeight(calendarDisplay.zoomLevel);
  // Day grid gutter + staff column widths for this screen (two techs per screen on phones).
  const dayGridGeometry = useCalendarDayGridGeometry();

  // Group reschedule flow (Phase 12): a menu entry or a block drag moves the whole group in one
  // mutation. On success we jump the calendar to the new day (if it changed); the panel then falls
  // back to the group detail (selectedGroupId stays set through the flow).
  const onGroupRescheduled = React.useCallback(
    ({ dateChanged, newStartISO }: { dateChanged: boolean; newStartISO: string }) => {
      if (dateChanged) setSelectedDate(addSalonDays(new Date(newStartISO), 0, orgTimezone));
    },
    [orgTimezone]
  );
  const groupReschedule = useGroupReschedule({
    timezone: orgTimezone,
    onCompleted: onGroupRescheduled,
  });
  // Exit reschedule whenever the focused group changes or clears (click elsewhere, close, slot click):
  // every panel-switching action moves selectedGroupId, so this is the single place to drop the mode.
  const {
    isActive: isReschedulingGroup,
    groupId: reschedulingGroupId,
    cancel: cancelGroupReschedule,
  } = groupReschedule;
  React.useEffect(() => {
    if (isReschedulingGroup && reschedulingGroupId !== selectedGroupId) {
      cancelGroupReschedule();
    }
  }, [selectedGroupId, isReschedulingGroup, reschedulingGroupId, cancelGroupReschedule]);
  // Calendar's resolved location (first/primary) — reused as the group draft's location.
  const resolvedLocationId = locationsData?.data?.[0]?.id;

  // Group booking availability gates the single form's "Group Appointment" mode-switch item.
  const { data: groupSettingsData } = useGroupBookingSettings();
  const groupBookingEnabled = groupSettingsData?.data?.enabled ?? false;

  // Book-the-group submission: one POST per Book, idempotent retries, staff-conflict prompt, and
  // error → toast mapping. Success toast + ['calendar'] invalidation live inside the hook.
  const groupBooking = useBookAppointmentGroup({
    timezone: orgTimezone,
    onCreated: (created) => {
      // Seed the group-detail cache from the create response and open the booked group panel
      // directly (no extra GET), closing the draft form.
      seedGroupDetailCache(queryClient, created.group);
      setNewGroupAppointment(null);
      setGroupPreviews(null);
      setIsGroupFormDirty(false);
      setSelectedAppointmentDetail(null);
      setGroupMemberBack(null);
      setSelectedGroupId(created.group.id);
    },
  });

  // Late-cancellation threshold drives the staff cancel modal's default selection.
  const { data: cancellationSettingsData } = useGetOrganizationCancellationSettings({
    query: {
      queryKey: getOrganizationCancellationSettingsOrgQueryKey(shortCode),
      staleTime: 1000 * 60 * 5,
    },
  });
  const lateCancellationThresholdHours =
    cancellationSettingsData?.data.lateCancellationThresholdHours;

  // Panel mode
  const panelMode: PanelMode = completedSale
    ? 'sale-details'
    : isPaymentView && checkoutSale
      ? 'payment'
      : checkoutSale
        ? 'checkout'
        : groupReschedule.isActive
          ? 'group-reschedule'
          : editingAppointment
          ? 'edit'
          : editingTimeBlock
            ? 'edit-time-block'
            : selectedGroupId
              ? 'group-detail'
              : selectedAppointmentDetail
                ? 'detail'
                : pendingDetailId
                  ? 'detail-loading'
                  : selectedTimeBlockDetail
                ? 'time-block-detail'
                : newGroupAppointment
                  ? 'new-group-form'
                  : newAppointment
                    ? 'new-form'
                    : newTimeBlock
                      ? 'new-time-block'
                      : 'none';

  // Leaving the detail skeleton for any other panel (the detail landed, or staff opened something
  // else) retires the pending fetch, so its late response can't reopen a superseded appointment.
  const prevPanelModeRef = React.useRef(panelMode);
  React.useEffect(() => {
    if (prevPanelModeRef.current === 'detail-loading' && panelMode !== 'detail-loading') {
      cancelPendingDetail();
    }
    prevPanelModeRef.current = panelMode;
  }, [panelMode, cancelPendingDetail]);
  // The skeleton sits at the detail's priority, above the new-appointment / time-block surfaces. If
  // staff open one of those while it shows, they've moved on — retire the fetch so the skeleton
  // doesn't cover what they just opened (a surface already open when the fetch began doesn't count).
  const lowerSurfacesRef = React.useRef<unknown[]>([]);
  React.useEffect(() => {
    const next = [newAppointment, newTimeBlock, selectedTimeBlockDetail, newGroupAppointment];
    const opened = next.some((surface, i) => surface && surface !== lowerSurfacesRef.current[i]);
    lowerSurfacesRef.current = next;
    if (opened) cancelPendingDetail();
  }, [newAppointment, newTimeBlock, selectedTimeBlockDetail, newGroupAppointment, cancelPendingDetail]);

  // Handle preview change from form
  const handlePreviewChange = React.useCallback(
    (data: PreviewChangeData) => {
      setPreviewData(data);
      // Navigate calendar to the form's date so preview card is visible (day view only)
      // Only update selectedDate when the calendar date actually changes (avoids unnecessary re-renders
      // and prevents date-jumping bugs from timezone mismatches)
      // Compare BOTH dates in org timezone for consistency
      if (data.startTime && viewMode === 'day') {
        // Same salon day keeps the same object (no re-render); else noon of the new salon day
        setSelectedDate((prev) => moveToSalonDay(prev, data.startTime!, orgTimezone));
      }
    },
    [viewMode, orgTimezone]
  );

  // Calculate preview appointment with services and client
  const previewAppointment: PreviewAppointment | null = React.useMemo(() => {
    if (!newAppointment) return null;

    // Build services array for preview with calculated start times
    const previewServices: PreviewService[] = [];
    if (previewData?.services && previewData.services.length > 0) {
      const totalServices = previewData.services.length;
      const positions = formLineTimeOrder(previewData.services);

      previewData.services.forEach((service, idx) => {
        const duration = service.overrideDuration ?? service.duration;
        const overrideStartTime = service.overrideStartTime;

        previewServices.push({
          instanceId: service.instanceId,
          name: service.name,
          duration: duration,
          index: totalServices > 1 ? positions[idx] : undefined,
          totalServices: totalServices > 1 ? totalServices : undefined,
          overrideStartTime: overrideStartTime,
          staffId: service.staffId,
          requestNote: service.requestNote,
        });
      });
    }

    return {
      staffId: newAppointment.staffId,
      startTime: newAppointment.dateTime,
      clientName: previewData?.clientName,
      services: previewServices.length > 0 ? previewServices : undefined,
    };
  }, [newAppointment, previewData]);

  // Time block preview on calendar grid (peach block with duration-based height)
  const timeBlockPreview: PreviewAppointment | null = React.useMemo(() => {
    if (!newTimeBlock) return null;
    return {
      staffId: newTimeBlock.staffId,
      startTime: timeBlockPreviewData?.startTime ?? newTimeBlock.dateTime,
      isTimeBlock: true,
      durationMinutes: timeBlockPreviewData?.durationMinutes ?? 30,
    };
  }, [newTimeBlock, timeBlockPreviewData]);

  // Handle preview change from edit form
  const handleEditPreviewChange = React.useCallback(
    (data: EditPreviewChangeData) => {
      setEditPreviewData(data);
      // Only update selectedDate when the calendar date actually changes
      // Compare BOTH dates in org timezone for consistency
      if (data.startTime && viewMode === 'day') {
        // Same salon day keeps the same object (no re-render); else noon of the new salon day
        setSelectedDate((prev) => moveToSalonDay(prev, data.startTime!, orgTimezone));
      }
    },
    [viewMode, orgTimezone]
  );

  // Format date for API calls - use org timezone to ensure correct date matching
  // (e.g., admin in Vietnam viewing calendar should query by business timezone date)
  const dateStr = salonDateKey(selectedDate, orgTimezone);

  // Fetch staff list (only service providers who are active)
  const { data: staffData, isLoading: isLoadingStaff } = useGetStaff(CALENDAR_STAFF_LIST_PARAMS);

  // Get staff list from response
  const staffList = React.useMemo(() => {
    return (staffData?.data?.items ?? []).filter((member) =>
      calendarAccess.canViewStaff(member.id)
    );
  }, [staffData, calendarAccess]);

  // Batch fetch schedules for all staff in a single API call (2 DB queries vs N × 3)
  // Shared with the appointment forms' staff availability (same cache entry per date).
  // keepPreviousData makes `isPending` true only on the very first load (drives the full-page
  // loader); on date navigation the previous day's data is a placeholder, flagged by
  // `isPlaceholderData`, while the new date loads.
  const {
    data: batchSchedules,
    isPending: isLoadingSchedules,
    isPlaceholderData: isSchedulesPlaceholder,
  } = useQuery({
    ...staffSchedulesBatchQueryOptions(dateStr, calendarRequest),
    placeholderData: keepPreviousData,
    // dateStr is the salon day only once the salon timezone is known
    enabled: !isLoadingTimezone,
  });
  // Until the selected date's schedules arrive every staff member would read as "not working",
  // so the working/hidden state is unknown — never auto-hide or show "Nobody to show here" then.
  const isSchedulesLoading = isLoadingSchedules || isSchedulesPlaceholder;

  // Transform staff data with batch schedules. Read the day the schedules are for: while a jumped-to date
  // loads, the previous day's schedules stay on screen (dimmed) under the previous day's grid.
  const scheduleDateStr = batchSchedules?.startDate ?? dateStr;
  const staff: StaffMember[] = React.useMemo(() => {
    return staffList.map((staffMember, index) => {
      const daySchedule = findStaffScheduleDay(batchSchedules, staffMember.id, scheduleDateStr);

      return {
        id: staffMember.id,
        name: staffMember.firstName,
        avatarUrl: staffMember.avatarUrl ?? undefined,
        color: STAFF_COLORS[index % STAFF_COLORS.length],
        // Unknown (schedules failed to load) stays undefined: still treated as off, but no "Day off" chip.
        isWorking: batchSchedules ? (daySchedule?.isWorking ?? false) : undefined,
        shifts: (daySchedule?.shifts ?? []) as WorkShift[],
      };
    });
  }, [staffList, batchSchedules, scheduleDateStr]);

  // ---- Week view data ----

  // Week view: seven salon days starting on the selected salon day
  const weekStart = React.useMemo(
    () => salonWeekStart(selectedDate, orgTimezone),
    [selectedDate, orgTimezone]
  );
  const weekStartStr = salonDateKey(weekStart, orgTimezone);
  const weekEndStr = salonDateKey(addSalonDays(weekStart, 6, orgTimezone), orgTimezone);
  const weekDayHeaders: WeekDay[] = React.useMemo(
    () => buildWeekDays(weekStart, orgTimezone),
    [weekStart, orgTimezone]
  );

  // Auto-select first staff when entering week mode or when staff list loads
  React.useEffect(() => {
    if (viewMode === 'week' && staffList.length > 0 && !selectedWeekStaffId) {
      setSelectedWeekStaffId(staffList[0].id);
    }
  }, [viewMode, staffList, selectedWeekStaffId]);

  // Handle view mode change - auto-select first staff for week mode
  const handleViewModeChange = React.useCallback(
    (mode: CalendarViewMode) => {
      // If any form is dirty, show discard modal before switching
      if (
        (newAppointment && isNewFormDirty) ||
        (newGroupAppointment && isGroupFormDirty) ||
        (editingAppointment && isEditFormDirty) ||
        (editingTimeBlock && isEditTimeBlockDirty)
      ) {
        setShowDiscardModal(true);
        return;
      }
      setViewMode(mode);
      if (mode === 'week' && staffList.length > 0) {
        setSelectedWeekStaffId((prev) => prev ?? staffList[0].id);
      }
      // Close any open panels when switching modes
      setNewAppointment(null);
      setPreviewData(null);
      setNewGroupAppointment(null);
      setGroupPreviews(null);
      setIsGroupFormDirty(false);
      setSelectedAppointmentDetail(null);
      setSelectedTimeBlockDetail(null);
      setEditingAppointment(null);
      setEditPreviewData(null);
      setEditingTimeBlock(null);
      setEditTimeBlockPreview(null);
      setDragOverrides(null);
      setIsDragEditMode(false);
      setIsNewFormDirty(false);
      setIsEditFormDirty(false);
      setIsEditTimeBlockDirty(false);
      setEditTimeBlockDragStartOverride(null);
      setEditTimeBlockDragStaffOverride(null);
    },
    [
      staffList,
      newAppointment,
      isNewFormDirty,
      newGroupAppointment,
      isGroupFormDirty,
      editingAppointment,
      isEditFormDirty,
      editingTimeBlock,
      isEditTimeBlockDirty,
    ]
  );

  // Set staff as not working for a specific date (schedule override)
  const setNotWorkingMutation = usePostStaffStaffIdSchedulesOverrides({
    mutation: {
      onSuccess: (_data, variables) => {
        const hiddenStaffId = variables.staffId;
        // A column with appointments never hides (the auto-hide effect would bring it back anyway)
        const keepsColumn = staffIdsWithAppointmentCards(allDayCards).has(hiddenStaffId);
        toast.success(keepsColumn ? 'Set to not working. Their column stays — they have appointments this day.' : 'Set to not working');
        // Refetch batch schedules so calendar grid updates the work/non-work background
        queryClient.invalidateQueries({ queryKey: [STAFF_SCHEDULES_BATCH_KEY] });
        queryClient.invalidateQueries({ queryKey: ['staff-schedule-week'] });
        if (keepsColumn) return;
        // Auto-hide the staff column from filter (remove from visible set)
        setVisibleStaffIds((prev) => {
          if (prev === null) {
            // Was showing all staff — create a set with everyone except the hidden one
            return new Set(staff.filter((s) => s.id !== hiddenStaffId).map((s) => s.id));
          }
          // Already filtered — just remove this staff from the set
          const next = new Set(prev);
          next.delete(hiddenStaffId);
          return next;
        });
      },
      onError: (error: unknown) => {
        handleCalendarDenial(error);
        const message = getApiErrorMessage(error, 'Failed to set not working');
        toast.error(message);
      },
    },
  });

  // Handle staff column header menu actions (dropdown in day view)
  const handleStaffMenuAction = React.useCallback(
    (staffMember: StaffMember, action: StaffMenuAction) => {
      if (
        ['edit-schedule', 'set-not-working'].includes(action) &&
        !calendarAccess.canEditSchedule(staffMember.id)
      )
        return;
      if (action === 'show-week-view') {
        // Check dirty state before switching
        if (
          (newAppointment && isNewFormDirty) ||
          (newGroupAppointment && isGroupFormDirty) ||
          (editingAppointment && isEditFormDirty) ||
          (editingTimeBlock && isEditTimeBlockDirty)
        ) {
          setShowDiscardModal(true);
          return;
        }
        // Switch to week view with this staff pre-selected
        setSelectedWeekStaffId(staffMember.id);
        setViewMode('week');
        // Close any open panels
        setNewAppointment(null);
        setPreviewData(null);
        setNewGroupAppointment(null);
        setGroupPreviews(null);
        setIsGroupFormDirty(false);
        setSelectedAppointmentDetail(null);
        setSelectedTimeBlockDetail(null);
        setEditingAppointment(null);
        setEditPreviewData(null);
        setEditingTimeBlock(null);
        setEditTimeBlockPreview(null);
        setDragOverrides(null);
        setIsDragEditMode(false);
        setIsNewFormDirty(false);
        setIsEditFormDirty(false);
        setIsEditTimeBlockDirty(false);
      } else if (action === 'edit-schedule') {
        // Navigate to staff settings page with work-hours tab pre-selected (via history state, no query params)
        navigate({
          to: '/$orgId/settings/staff',
          params: { orgId },
          state: { staffId: staffMember.id, tab: 'work-hours' } as Record<string, unknown>,
        });
      } else if (action === 'set-not-working') {
        // Show confirmation modal before setting not working
        setNotWorkingConfirm(staffMember);
      }
      // print-day-schedule - not yet implemented
    },
    [
      newAppointment,
      isNewFormDirty,
      newGroupAppointment,
      isGroupFormDirty,
      editingAppointment,
      isEditFormDirty,
      editingTimeBlock,
      isEditTimeBlockDirty,
      navigate,
      orgId,
      calendarAccess,
    ]
  );

  // Fetch week calendar data from API (only when in week mode)
  const { data: weekCalendarData, isLoading: isLoadingWeekCalendar } = useQuery({
    queryKey: ['calendar', 'week', selectedWeekStaffId, weekStartStr],
    queryFn: async () => {
      const response = await calendarRequest(
        apiClient.get('/calendar/week', {
          params: { staffId: selectedWeekStaffId, startDate: weekStartStr },
        })
      );
      return response.data.data as {
        staff: { id: string; name: string; avatarUrl: string | null; color: string | null };
        weekStart: string;
        weekEnd: string;
        appointments: CalendarAppointmentFromApi[];
        timeBlocks: Array<{
          id: string;
          staffId: string;
          startTime: string;
          endTime: string;
          reason: string;
          title: string | null;
          isPrivate: boolean;
        }>;
        businessHoursByDate?: Record<string, { open: string; close: string } | null>;
      };
    },
    enabled: viewMode === 'week' && !!selectedWeekStaffId && !isLoadingTimezone,
    staleTime: 30_000,
    ...gridLiveRefresh,
  });

  // Transform week appointments into Appointment[] cards
  // Filter to only include services assigned to the selected staff member
  const weekAppointments: Appointment[] = React.useMemo(() => {
    if (!weekCalendarData?.appointments || !selectedWeekStaffId) return [];
    const staffFilteredAppointments = weekCalendarData.appointments
      .map((apt) => ({
        ...apt,
        services: apt.services.filter((svc) => svc.staffId === selectedWeekStaffId),
      }))
      .filter((apt) => apt.services.length > 0);
    return transformCalendarAppointments(staffFilteredAppointments, (apt) => {
      const whole = weekCalendarData.appointments.find((item) => item.id === apt.id);
      return Boolean(whole && calendarAccess.canEditAppointment(whole));
    }, { closesQuietly: checkInOff });
  }, [weekCalendarData, selectedWeekStaffId, calendarAccess, checkInOff]);

  // Transform week time blocks into break cards
  const weekTimeBlockCards: Appointment[] = React.useMemo(() => {
    if (!weekCalendarData?.timeBlocks || !selectedWeekStaffId) return [];
    return weekCalendarData.timeBlocks
      .filter((tb) => tb.staffId === selectedWeekStaffId)
      .map((tb) => ({
        id: `tb-${tb.id}`,
        canDrag: calendarAccess.canManageTimeBlock(tb.staffId),
        staffId: tb.staffId,
        serviceType: '',
        clientName: tb.title || 'Break',
        startTime: new Date(tb.startTime),
        endTime: new Date(tb.endTime),
        color: 'gray' as const,
        isBreak: true,
      }));
  }, [weekCalendarData, selectedWeekStaffId, calendarAccess]);

  // Merge week appointments + time blocks
  const allWeekCards: Appointment[] = React.useMemo(() => {
    return [...weekAppointments, ...weekTimeBlockCards];
  }, [weekAppointments, weekTimeBlockCards]);

  // Fetch staff schedules for the week (7-day range) for work hour backgrounds
  const weekScheduleQuery = useQuery({
    queryKey: ['staff-schedule-week', selectedWeekStaffId, weekStartStr, weekEndStr],
    queryFn: () =>
      getStaffStaffIdSchedulesWeek(selectedWeekStaffId!, {
        startDate: weekStartStr,
        endDate: weekEndStr,
      }),
    enabled: viewMode === 'week' && !!selectedWeekStaffId && !isLoadingTimezone,
  });

  // Build WeekDayData array with per-day schedule info + resolved business hours.
  // businessHours: object → shade after-hours; null → closed; undefined → unknown.
  const weekDaysData: WeekDayData[] = React.useMemo(() => {
    const scheduleDays = weekScheduleQuery.data?.data?.days ?? [];
    const hoursByDate = weekCalendarData?.businessHoursByDate;
    return weekDayHeaders.map((dayHeader) => {
      const daySchedule = scheduleDays.find((d) => d.date === dayHeader.dateStr);
      return {
        dateStr: dayHeader.dateStr,
        isWorking: daySchedule?.isWorking ?? false,
        shifts: (daySchedule?.shifts ?? []) as WorkShift[],
        businessHours: hoursByDate ? (hoursByDate[dayHeader.dateStr] ?? null) : undefined,
      };
    });
  }, [weekDayHeaders, weekScheduleQuery.data, weekCalendarData?.businessHoursByDate]);

  // Header staff list for the week-mode staff selector dropdown
  const headerStaffList: HeaderStaffItem[] = React.useMemo(() => {
    return staffList.map((s) => ({
      id: s.id,
      name: s.firstName,
      avatarUrl: s.avatarUrl ?? undefined,
    }));
  }, [staffList]);

  // ---- End week view data ----

  // Calculate edit preview appointment (replaces original cards on grid during edit)
  const editPreviewAppointment: PreviewAppointment | null = React.useMemo(() => {
    if (!editingAppointment || !editPreviewData) return null;

    const previewServices: PreviewService[] = [];
    if (editPreviewData.services.length > 0) {
      const totalServices = editPreviewData.services.length;
      const positions = formLineTimeOrder(editPreviewData.services);
      editPreviewData.services.forEach((service, idx) => {
        previewServices.push({
          instanceId: service.instanceId,
          name: service.name,
          duration: service.overrideDuration ?? service.duration,
          index: totalServices > 1 ? positions[idx] : undefined,
          totalServices: totalServices > 1 ? totalServices : undefined,
          overrideStartTime: service.overrideStartTime,
          staffId: service.staffId,
          requestNote: service.requestNote,
        });
      });
    }

    // Use status-specific color (preserves original appointment status color during edit),
    // fall back to staff color only when no status color exists
    const firstStaffId = editPreviewData.staffId || editingAppointment.services[0]?.staff.id || '';
    const statusColor = statusFillColor(editingAppointment.status);
    const staffIndex = staff.findIndex((s) => s.id === firstStaffId);
    const staffColor = staffIndex >= 0 ? staff[staffIndex].color : 'teal';
    const previewBgColor = statusColor || COLOR_BG_MAP[staffColor] || COLOR_BG_MAP.teal;

    return {
      staffId: firstStaffId,
      startTime: editPreviewData.startTime ?? new Date(editingAppointment.startTime),
      clientName: editPreviewData.clientName,
      services: previewServices.length > 0 ? previewServices : undefined,
      previewBgColor,
    };
  }, [editingAppointment, editPreviewData, staff]);

  // Use edit preview when editing, then new appointment preview, then time block preview
  const activePreviewAppointment = editPreviewAppointment || previewAppointment || timeBlockPreview;

  // Fetch day calendar data (appointments + time blocks) from API. It starts as soon as the salon timezone names
  // the day, in the same wave as the staff list: the server already limits it to the staff this viewer may see.
  // keepPreviousData: a jump (Today, the date picker) keeps the last day on screen, dimmed, until the new one lands.
  const {
    data: dayCalendarResponse,
    isLoading: isLoadingDayCalendar,
    isPlaceholderData: isDayPlaceholder,
  } = useQuery({
    ...calendarDayQueryOptions<CalendarAppointmentFromApi>(dateStr, calendarRequest),
    placeholderData: keepPreviousData,
    // Access is already loaded with the shell; the salon timezone names the day.
    enabled: calendarAccess.ready && !isLoadingTimezone,
    ...gridLiveRefresh,
  });

  // The day keeps only this page's columns' rows (active service providers the viewer may see) — the server's
  // default list also covers staff who don't take bookings.
  const dayCalendarData = React.useMemo(
    () => keepCalendarColumnRows(dayCalendarResponse, staffList.map((member) => member.id)),
    [dayCalendarResponse, staffList]
  );

  // The open panel's Card row and fee block follow the grid: a client saving their card, a Stripe webhook settling a
  // charge / refund, or a fee decided elsewhere (realtime → grid refetch) flips them live.
  usePanelNoShowProtectionFollowsGrid(
    selectedAppointmentDetail?.id ?? null,
    dayCalendarData?.appointments,
    weekCalendarData?.appointments,
    setSelectedAppointmentDetail
  );

  // Transform API appointments into calendar grid cards
  const appointments: Appointment[] = React.useMemo(() => {
    const apiAppointments = dayCalendarData?.appointments ?? [];
    return transformCalendarAppointments(apiAppointments, calendarAccess.canEditAppointment, { closesQuietly: checkInOff });
  }, [dayCalendarData, calendarAccess, checkInOff]);

  // Transform API time blocks into break cards for the calendar grid
  const timeBlockCards: Appointment[] = React.useMemo(() => {
    const apiTimeBlocks = dayCalendarData?.timeBlocks ?? [];
    return apiTimeBlocks.map((tb) => ({
      id: `tb-${tb.id}`,
      canDrag: calendarAccess.canManageTimeBlock(tb.staffId),
      staffId: tb.staffId,
      serviceType: '',
      clientName: tb.title || 'Break',
      startTime: new Date(tb.startTime),
      endTime: new Date(tb.endTime),
      color: 'gray' as const,
      isBreak: true,
    }));
  }, [dayCalendarData, calendarAccess]);

  // Merge appointments + time block cards for the day view grid
  const allDayCards: Appointment[] = React.useMemo(() => {
    return [...appointments, ...timeBlockCards];
  }, [appointments, timeBlockCards]);

  // Auto-hide staff who are off on the selected date — but never a column holding an appointment.
  // The key only changes when someone's working / off-with-bookings state flips, so a realtime refetch
  // doesn't undo a manual staff filter. Empty while schedules or the day's cards load (or still show the previous
  // day during a jump), so the effect waits for real data of one day instead of hiding everyone (or hiding an
  // off-day tech whose bookings haven't arrived).
  const autoHideKey =
    isSchedulesLoading || isLoadingDayCalendar || isDayPlaceholder ? '' : autoHideStaffKey(staff, allDayCards);
  React.useEffect(() => {
    if (!autoHideKey) return;
    const hiddenIds = new Set(autoHiddenStaffIds(staff, allDayCards));
    // Nobody to hide — show all; otherwise show everyone else
    setVisibleStaffIds(hiddenIds.size === 0 ? null : new Set(staff.filter((s) => !hiddenIds.has(s.id)).map((s) => s.id)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoHideKey]);

  // Cold open: while /locations is on its way, the salon timezone this device saw last names "today", so today's
  // schedules + grid are read in the same wave as /locations and /staff. Only warms the cache: the grid still waits
  // for the real timezone, and a stale hint just leaves one unused entry.
  React.useEffect(() => {
    if (orgTimezone) {
      writeSalonTimezoneHint(clerkOrgId, orgTimezone);
      return;
    }
    if (!isLoadingTimezone || !calendarAccess.ready) return;
    const hintedTimezone = readSalonTimezoneHint(clerkOrgId);
    if (!hintedTimezone) return;
    const hintedToday = salonDateKey(new Date(), hintedTimezone);
    void queryClient.prefetchQuery(calendarDayQueryOptions(hintedToday, calendarRequest));
    void queryClient.prefetchQuery(staffSchedulesBatchQueryOptions(hintedToday, calendarRequest));
  }, [orgTimezone, isLoadingTimezone, calendarAccess.ready, clerkOrgId, queryClient, calendarRequest]);

  // Prefetch adjacent dates (prev/next day) for instant navigation — once per shown day, after it has loaded
  // (not on every background refetch of it).
  const isDayLoaded = !!dayCalendarResponse && !isDayPlaceholder;
  React.useEffect(() => {
    if (!isDayLoaded || viewMode !== 'day') return;
    for (const d of [addDaysToDateKey(dateStr, -1), addDaysToDateKey(dateStr, 1)]) {
      queryClient.prefetchQuery(calendarDayQueryOptions(d, calendarRequest));
      queryClient.prefetchQuery(staffSchedulesBatchQueryOptions(d, calendarRequest));
    }
  }, [isDayLoaded, dateStr, viewMode, queryClient, calendarRequest]);

  // When editing an appointment, override service card positions/staff on real cards
  // so cards stay visible and draggable (instead of hiding + showing non-draggable preview).
  // Uses editPreviewData (updated on every form change) as source of truth for positions,
  // falling back to dragOverrides for initial drag state.
  const editAdjustedAppointments: Appointment[] = React.useMemo(() => {
    if (!editingAppointment) return allDayCards;
    // Prefer editPreviewData (live form state) over dragOverrides (initial drag state)
    const serviceOverrides = editPreviewData?.services ?? dragOverrides?.services;
    if (!serviceOverrides) return allDayCards;

    const totalServices = serviceOverrides.length;
    const clientName = editingAppointment.primaryClient
      ? `${editingAppointment.primaryClient.firstName} ${editingAppointment.primaryClient.lastName ?? ''}`.trim()
      : 'Walk-in';
    const statusTone = STATUS_TONE_MAP[editingAppointment.status];
    const statusValue =
      editingAppointment.status === 'CONFIRMED'
        ? ('confirmed' as const)
        : editingAppointment.status === 'UNCONFIRMED'
          ? ('unconfirmed' as const)
          : editingAppointment.status === 'WAITING'
            ? ('waiting' as const)
            : editingAppointment.status === 'IN_SERVICE'
              ? ('in-service' as const)
              : editingAppointment.status === 'CHECKING_OUT'
                ? ('checking-out' as const)
                : editingAppointment.status === 'COMPLETED'
                  ? ('completed' as const)
                  : undefined;

    // Track which overrides matched existing cards
    const matchedInstanceIds = new Set<string>();
    // The same-time chip follows the form live (its Same time switch and Moves), not the saved visit.
    const previewLaneLines = serviceOverrides.map((svc) => ({
      id: svc.instanceId, lane: svc.lane, staffId: svc.staffId ?? '', staffName: svc.staffName ?? '',
      start: svc.overrideStartTime ?? 0, end: (svc.overrideStartTime ?? 0) + (svc.overrideDuration ?? svc.duration ?? 30),
    }));
    // "(n/N)" follows the form's live times, so the preview numbers match the cards after Save.
    const positions = formLineTimeOrder(serviceOverrides);
    const previewSameTime = (instanceId: string) => {
      const marker = sameTimeLineMarker(previewLaneLines, instanceId, isSameTimeLanes(serviceOverrides));
      return { isSameTimeVisit: marker.isSameTime, sameTimePartnerNames: marker.partnerNames };
    };

    const adjusted = allDayCards.map((apt) => {
      // Match by service card ID: "{appointmentId}-svc-{serviceId}"
      const override = serviceOverrides.find(
        (svc) => apt.id === `${editingAppointment.id}-svc-${svc.instanceId}`
      );
      if (!override) return apt;
      matchedInstanceIds.add(override.instanceId);
      // Apply override to this card's position, staff, and service index.
      // overrideStartTime is in org-timezone minutes, so convert to UTC properly.
      const newMinutes = override.overrideStartTime ?? 0;
      const hours = Math.floor(newMinutes / 60);
      const mins = newMinutes % 60;
      // selectedDate is a salon-day instant: read its day on the salon clock
      const newStart = orgTimezone
        ? createDateInTz(selectedDate, hours, mins, orgTimezone)
        : (() => {
            const d = new Date(apt.startTime);
            d.setHours(hours, mins, 0, 0);
            return d;
          })();
      const duration = override.overrideDuration ?? override.duration ?? 30;
      const newEnd = new Date(newStart);
      newEnd.setMinutes(newEnd.getMinutes() + duration);
      const svcIdx = serviceOverrides.indexOf(override);
      return {
        ...apt,
        staffId: override.staffId || apt.staffId,
        startTime: newStart,
        endTime: newEnd,
        serviceIndex: totalServices > 1 ? positions[svcIdx] : undefined,
        totalServices: totalServices > 1 ? totalServices : undefined,
        ...previewSameTime(override.instanceId),
      };
    });

    // Create cards for newly added services (not in allDayCards yet)
    serviceOverrides.forEach((svc, idx) => {
      if (matchedInstanceIds.has(svc.instanceId)) return;
      // overrideStartTime is in org-timezone minutes, so convert to UTC properly.
      const newMinutes = svc.overrideStartTime ?? 0;
      const hours = Math.floor(newMinutes / 60);
      const mins = newMinutes % 60;
      const newStart = orgTimezone
        ? createDateInTz(selectedDate, hours, mins, orgTimezone)
        : (() => {
            const refDate = editPreviewData?.startTime ?? new Date(editingAppointment.startTime);
            const d = new Date(refDate);
            d.setHours(hours, mins, 0, 0);
            return d;
          })();
      const duration = svc.overrideDuration ?? svc.duration ?? 30;
      const newEnd = new Date(newStart);
      newEnd.setMinutes(newEnd.getMinutes() + duration);
      adjusted.push({
        id: `${editingAppointment.id}-svc-${svc.instanceId}`,
        appointmentId: editingAppointment.id,
        serviceLineId: svc.instanceId,
        staffId: svc.staffId || editingAppointment.services[0]?.staff.id || '',
        serviceType: svc.name,
        clientName,
        startTime: newStart,
        endTime: newEnd,
        status: statusValue,
        color: 'teal' as const,
        statusTone,
        isBreak: false,
        isWalkIn: isWalkInBookingSource(editingAppointment.bookingSource),
        serviceIndex: totalServices > 1 ? positions[idx] : undefined,
        totalServices: totalServices > 1 ? totalServices : undefined,
        ...previewSameTime(svc.instanceId),
      });
    });

    // The edited visit ends where its last previewed line ends ("running ~N min over" reads from it).
    const editedEnd = adjusted.reduce<Date | undefined>(
      (end, card) =>
        card.appointmentId === editingAppointment.id && (!end || card.endTime > end) ? card.endTime : end,
      undefined
    );
    return adjusted.map((card) =>
      card.appointmentId === editingAppointment.id ? { ...card, visitEndTime: editedEnd } : card
    );
  }, [allDayCards, dragOverrides, editingAppointment, editPreviewData, orgTimezone, selectedDate]);

  // When editing a time block, adjust the card's position/size on the grid.
  // Prefers live form preview (onPreviewChange) over initial drag overrides.
  const effectiveDayCards: Appointment[] = React.useMemo(() => {
    if (editingAppointment) return editAdjustedAppointments;
    if (editingTimeBlock) {
      // Live form preview takes priority, then drag overrides, then skip
      const preview = editTimeBlockPreview;
      const hasDragOverride = editTimeBlockDragStartOverride != null;
      if (!preview && !hasDragOverride) return allDayCards;
      return allDayCards.map((card) => {
        if (card.id !== `tb-${editingTimeBlock.id}`) return card;
        if (preview) {
          return {
            ...card,
            staffId: preview.staffId,
            startTime: preview.startTime,
            endTime: preview.endTime,
          };
        }
        // Drag override fallback (before form emits first preview)
        const effectiveStaff = editTimeBlockDragStaffOverride || editingTimeBlock.staffId;
        const originalDuration =
          (new Date(editingTimeBlock.endTime).getTime() -
            new Date(editingTimeBlock.startTime).getTime()) /
          60000;
        const newStart = new Date(editTimeBlockDragStartOverride!);
        const newEnd = new Date(newStart);
        newEnd.setMinutes(newEnd.getMinutes() + originalDuration);
        return { ...card, staffId: effectiveStaff, startTime: newStart, endTime: newEnd };
      });
    }
    return allDayCards;
  }, [
    allDayCards,
    editAdjustedAppointments,
    editingAppointment,
    editingTimeBlock,
    editTimeBlockPreview,
    editTimeBlockDragStartOverride,
    editTimeBlockDragStaffOverride,
  ]);

  // Create appointment mutation
  const createAppointmentMutation = useMutation({
    mutationFn: async ({
      formData,
      allowConflict,
    }: {
      formData: AppointmentFormData;
      /** Skip the staff double-booking guard (set after the user confirms "Book anyway"). */
      allowConflict?: boolean;
    }) => {
      if (
        !formData.services?.length ||
        !formData.services.every((svc) => calendarAccess.canBook(svc.staffId || formData.staffId))
      )
        throw new LocalActionError("You don't have permission to book appointments for this team member.");
      const payload = buildCreateAppointmentPayload({ formData, timezone: orgTimezone, allowConflict });

      const response = await calendarRequest(apiClient.post('/appointments', payload));
      return response.data.data as AppointmentDetailData;
    },
    onSuccess: async (appointmentDetail) => {
      setConflictPrompt(null);
      toast.success('Appointment created');
      // A grid read that started before this booking committed must not land over it.
      const stoppedRead = await cancelGridReadsBeforeWrite(queryClient);
      // Show detail panel with API response (backend returns correct CONFIRMED status)
      setSelectedAppointmentDetail(appointmentDetail);
      // Inject the created appointment straight into the day cache from the POST
      // response so the real card appears the instant the request resolves — no
      // second refetch round-trip. The preview card bridged the gap until now, so
      // swapping here is seamless (no vanish-then-reappear flicker).
      const created = detailToCalendarAppointment(appointmentDetail);
      queryClient.setQueriesData<CalendarCacheData>(
        { queryKey: ['calendar', 'day', dateStr] },
        (old) => insertAppointmentIntoCache(old, created)
      );
      // Real card is now in cache — swap out the preview.
      setNewAppointment(null);
      setPreviewData(null);
      // Server-derived fields (isNew) and the week view reconcile in the background, without blocking the swap
      // above: this write's realtime echo is that one read (or a local one without a live feed).
      refreshCalendarAfterBroadcastWrite(queryClient, stoppedRead);
      // Invalidate inbox data so automated messages show up
      // Delayed: automated messages are processed async after appointment creation
      setTimeout(() => {
        queryClient.invalidateQueries({ queryKey: getGetConversationsQueryKey() });
        // Also invalidate all conversation message queries (pattern: /conversations/*/messages)
        queryClient.invalidateQueries({
          predicate: (query) =>
            typeof query.queryKey[0] === 'string' &&
            (query.queryKey[0] as string).includes('/messages'),
        });
      }, 3000);
    },
    onError: (error: unknown, variables) => {
      // Staff double-booking: hold the booking and let the user confirm "Book anyway".
      const conflicts = extractAppointmentConflicts(error);
      if (conflicts && !variables.allowConflict) {
        setConflictPrompt({ formData: variables.formData, conflicts });
        return;
      }
      // A 400 can mean the add-on catalog changed under the open form (option deactivated,
      // group made required): refresh the pickers so staff can see and fix the selection.
      if ((error as { response?: { status?: number } }).response?.status === 400) {
        void invalidateAllServiceOptionGroups(queryClient);
      }
      toast.error(getApiErrorMessage(error, 'Failed to book appointment'));
    },
  });

  // Create time block mutation (POST /time-blocks)
  const createTimeBlockMutation = useMutation({
    mutationFn: async (formData: TimeBlockFormData) => {
      if (!calendarAccess.canManageTimeBlock(formData.staffId))
        throw new LocalActionError("You don't have permission to change time blocks for this team member.");
      const payload = {
        staffId: formData.staffId,
        startTime: formData.startTime.toISOString(),
        endTime: formData.endTime.toISOString(),
        reason: formData.reason,
        reasonNotes: formData.reasonNotes,
        title: formData.reasonNotes || undefined,
        isAllDay: false,
        isPrivate: true,
      };
      const response = await calendarRequest(apiClient.post('/time-blocks', payload));
      return response.data.data;
    },
    onSuccess: (createdTimeBlock) => {
      toast.success('Time block created');
      scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
      setNewTimeBlock(null);
      setTimeBlockPreviewData(null);
      setIsTimeBlockFormDirty(false);
      // Auto-select the newly created time block and show detail panel
      if (createdTimeBlock) {
        setSelectedTimeBlockDetail(createdTimeBlock as TimeBlockDetailData);
      }
    },
    onError: (error: Error) => {
      const message =
        getApiErrorMessage(error, 'Failed to create time block');
      toast.error(message);
    },
  });

  // Delete time block mutation
  const deleteTimeBlockMutation = useMutation({
    mutationFn: async (timeBlockId: string) => {
      const response = await calendarRequest(apiClient.delete(`/time-blocks/${timeBlockId}`));
      return response.data;
    },
    onSuccess: () => {
      toast.success('Time block deleted');
      scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
      setSelectedTimeBlockDetail(null);
      setEditingTimeBlock(null);
      setEditTimeBlockPreview(null);
      setIsEditTimeBlockDirty(false);
    },
    onError: (error: Error) => {
      const message =
        getApiErrorMessage(error, 'Failed to delete time block');
      toast.error(message);
    },
  });

  // Update time block mutation
  const updateTimeBlockMutation = useMutation({
    mutationFn: async (data: EditTimeBlockSaveData) => {
      if (
        !editingTimeBlock ||
        !calendarAccess.canManageTimeBlock(editingTimeBlock.staffId) ||
        !calendarAccess.canManageTimeBlock(data.staffId)
      )
        throw new LocalActionError("You don't have permission to change time blocks for this team member.");
      const payload = {
        staffId: data.staffId,
        startTime: data.startTime.toISOString(),
        endTime: data.endTime.toISOString(),
        reason: data.reason,
        reasonNotes: data.reasonNotes,
        title: data.reasonNotes || undefined,
      };
      const response = await calendarRequest(apiClient.patch(`/time-blocks/${data.id}`, payload));
      return response.data.data as TimeBlockDetailData;
    },
    onSuccess: (updatedDetail) => {
      toast.success('Time block updated');
      scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
      setSelectedTimeBlockDetail(updatedDetail);
      setEditingTimeBlock(null);
      setEditTimeBlockPreview(null);
      setIsEditTimeBlockDirty(false);
      setEditTimeBlockDragStartOverride(null);
      setEditTimeBlockDragStaffOverride(null);
    },
    onError: (error: Error) => {
      const message =
        getApiErrorMessage(error, 'Failed to update time block');
      toast.error(message);
    },
  });

  // Delete/cancel appointment mutation
  const deleteAppointmentMutation = useMutation({
    mutationFn: async ({
      appointmentId,
      type,
      groupContext,
    }: {
      appointmentId: string;
      type: CancellationType;
      /** Explicit group version when cancelling/deleting a member from the group panel (no open detail). */
      groupContext?: { version: number };
      /** One-tap No-show (no confirm dialog) — success offers Undo. */
      undoable?: boolean;
    }) => {
      dismissDoneUndoToast();
      if (
        !groupContext &&
        (!selectedAppointmentDetail ||
          !(type === 'delete'
            ? calendarAccess.canEditAppointment(selectedAppointmentDetail)
            : calendarAccess.canChangeStatus(
                selectedAppointmentDetail,
                type === 'no-show' ? 'NO_SHOW' : 'CANCELLED'
              )))
      )
        throw new LocalActionError("You don't have permission to change this appointment.");
      // Grouped members carry the current group version so the API accepts the individual write:
      // it goes in the query for DELETE, in the body for the status (cancel/no-show) PATCH.
      // A group-panel member cancel supplies the version explicitly (its detail panel isn't open);
      // otherwise derive it from the open appointment. Ungrouped appointments yield {} → payloads
      // stay byte-identical.
      const groupParam = groupContext
        ? { expectedGroupVersion: groupContext.version }
        : groupVersionParam(
            selectedAppointmentDetail && selectedAppointmentDetail.id === appointmentId
              ? selectedAppointmentDetail
              : null
          );
      if (type === 'delete') {
        const response = await calendarRequest(
          apiClient.delete(`/appointments/${appointmentId}`, { params: groupParam })
        );
        return response.data;
      }

      const status = type === 'no-show' ? 'NO_SHOW' : 'CANCELLED';
      const reason =
        type === 'late'
          ? 'Late cancellation'
          : type === 'no-show'
            ? 'No show'
            : 'Normal cancellation';
      // Persist the staff-selected cancellation type. NO_SHOW is derived server-side from
      // status, so only send an explicit type for CANCELLED (NORMAL/LATE).
      const body: {
        status: string;
        reason: string;
        cancellationType?: 'NORMAL' | 'LATE';
        expectedGroupVersion?: number;
      } = {
        status,
        reason,
        ...groupParam,
      };
      if (type === 'normal') body.cancellationType = 'NORMAL';
      if (type === 'late') body.cancellationType = 'LATE';
      const response = await calendarRequest(
        apiClient.patch(`/appointments/${appointmentId}/status`, body)
      );
      return response.data;
    },
    onSuccess: async (_data, variables) => {
      // Re-read the grids, the WAITING badge (a cancel / no-show / delete removes a waiting client) and the open
      // group panel — one debounced read shared with this write's realtime echo.
      scheduleCalendarRefresh(queryClient);

      // Group-panel member cancel/delete: no single detail panel is open — the refresh above re-reads the group
      // so the member card reflects the new status / removal. Stop here.
      if (variables.groupContext) return;

      // Hard delete removes the appointment entirely — close the panel. For
      // cancel/no-show (incl. "Change Cancellation Type"), keep the panel open and
      // refresh it so the status reflects the new cancellation type.
      if (variables.type === 'delete') {
        setSelectedAppointmentDetail(null);
        return;
      }
      // One-tap No-show has no confirm dialog — offer Undo (restore the prior status) when allowed.
      if (variables.undoable) {
        const restorable =
          selectedAppointmentDetail?.id === variables.appointmentId &&
          calendarAccess.canEditAppointment(selectedAppointmentDetail);
        showNoShowUndoToast(
          restorable ? () => void undoOneTapNoShow(variables.appointmentId) : undefined
        );
      }
      try {
        const response = await calendarRequest(
          apiClient.get(`/appointments/${variables.appointmentId}`)
        );
        setSelectedAppointmentDetail(response.data.data as AppointmentDetailData);
      } catch {
        setSelectedAppointmentDetail(null);
      }
    },
    onError: (error: Error, variables) => {
      if (
        handleGroupedAppointmentError(error, {
          queryClient,
          onStaleGroupVersion: () => hydrateAppointmentDetail(variables.appointmentId),
        })
      )
        return;
      if (toastNoShowFeeLock(error, () => hydrateAppointmentDetail(variables.appointmentId))) return;
      const message =
        getApiErrorMessage(error, 'Failed to cancel appointment');
      toast.error(message);
    },
  });

  // Update appointment mutation
  const updateAppointmentMutation = useMutation({
    mutationFn: async (
      data: EditAppointmentSaveData & {
        /** Skip the staff double-booking guard (set after the user confirms "Book anyway"). */
        allowConflict?: boolean;
      }
    ) => {
      if (
        !editingAppointment ||
        !calendarAccess.canEditAppointment(editingAppointment) ||
        !data.services.every((svc) => calendarAccess.canBook(svc.staffId || ''))
      )
        throw new LocalActionError("You don't have permission to book appointments for this team member.");
      // Grouped members send the current group version in the body (same-day time/service/staff
      // edits keep membership). Ungrouped edits add nothing.
      const groupParam = groupVersionParam(
        editingAppointment && editingAppointment.id === data.appointmentId ? editingAppointment : null
      );
      const payload = buildUpdateAppointmentPayload({
        data,
        appointment: editingAppointment,
        timezone: orgTimezone,
        groupParam,
      });

      const response = await calendarRequest(
        apiClient.patch(`/appointments/${data.appointmentId}`, payload)
      );
      return response.data.data as AppointmentDetailData;
    },
    onSuccess: async (updatedDetail) => {
      setEditConflictPrompt(null);
      toast.success('Appointment updated');
      // A grid read that started before this save committed would land over it (the card jumping back).
      const stoppedRead = await cancelGridReadsBeforeWrite(queryClient);
      // Write the saved schedule into the grid caches from the PATCH response: the edit form closes
      // now, and without this the card snaps back to its old slot until the refetch below lands — a
      // drag in that window would start from the old lines (the save re-creates them with new ids).
      const saved = detailToCalendarAppointment(updatedDetail);
      const savedDateStr = salonDateKey(new Date(saved.startTime), orgTimezone);
      for (const view of ['day', 'week'] as const) {
        queryClient.setQueriesData<CalendarCacheData>({ queryKey: ['calendar', view] }, (old) =>
          applySavedScheduleToCache(old, saved, savedDateStr)
        );
      }
      // The rest (server-derived fields, other views) arrives with this write's realtime echo.
      refreshCalendarAfterBroadcastWrite(queryClient, stoppedRead);
      // A grouped member's schedule edit bumps the group version — refresh the group panel cache so
      // returning to it (within the query staleTime) shows the new time and the current version.
      if (updatedDetail.group) {
        scheduleCalendarRefresh(queryClient, [groupDetailQueryKey(updatedDetail.group.id)]);
      }
      setSelectedAppointmentDetail(updatedDetail);
      setEditingAppointment(null);
      setEditPreviewData(null);
      setDragOverrides(null);
      setIsDragEditMode(false);
    },
    onError: (error: unknown, variables) => {
      // Staff double-booking on a reschedule/edit: hold the update and let the
      // user confirm "Book anyway" (re-submits with allowConflict), mirroring create.
      const conflicts = extractAppointmentConflicts(error);
      if (conflicts && !variables.allowConflict) {
        setEditConflictPrompt({ data: variables, conflicts });
        return;
      }
      // One staff member on two overlapping lines of this visit: the open edit form shows it inline
      // (a panel closed mid-save falls through to the toast so the failure is never silent).
      if (sameStaffOverlapMessage(error) && editingAppointment?.id === variables.appointmentId) return;
      if (
        handleGroupedAppointmentError(error, {
          queryClient,
          onStaleGroupVersion: () => hydrateAppointmentDetail(variables.appointmentId),
        })
      )
        return;
      toast.error(getApiErrorMessage(error, 'Failed to update appointment'));
    },
  });

  // Update appointment status mutation (check-in, in-service, checkout)
  const notifyVisitTurnOutcome = useVisitTurnNotice();
  const updateStatusMutation = useMutation({
    mutationFn: async ({
      appointmentId,
      status,
      force,
      skipNotification,
      reason,
      expectedStatus,
      source,
    }: {
      appointmentId: string;
      status: string;
      force?: boolean;
      skipNotification?: boolean;
      reason?: string;
      /** Apply only while the visit is still in this status (Undo). */
      expectedStatus?: string;
      /** A freshly re-read appointment whose group version to send (Undo), instead of the open panel's. */
      source?: AppointmentDetailData;
    }) => {
      // Grouped members carry the current group version in the status body (e.g. check-in →
      // WAITING, in-service → IN_SERVICE). Ungrouped status changes add nothing.
      const groupParam = groupVersionParam(
        source ??
          (selectedAppointmentDetail && selectedAppointmentDetail.id === appointmentId
            ? selectedAppointmentDetail
            : null)
      );
      const response = await calendarRequest(
        apiClient.patch(`/appointments/${appointmentId}/status`, {
          status,
          ...(force ? { force } : {}),
          ...(skipNotification ? { skipNotification } : {}),
          ...(reason ? { reason } : {}),
          ...(expectedStatus ? { expectedStatus } : {}),
          ...groupParam,
        })
      );
      return response.data.data as {
        id: string;
        status: string;
        previousStatus: string;
        turn?: VisitTurnOutcome;
        reopenOverlap?: boolean;
      };
    },
    onMutate: async ({ appointmentId, status }) => {
      dismissDoneUndoToast();
      // Optimistically flip the card color + panel status right away, before the server
      // responds. Cancel in-flight calendar refetches so they can't clobber the patch,
      // snapshot the caches for rollback, then patch every matching day/week cache.
      const epoch = permissionEpoch.current;
      const access = currentCalendarAccess.current;
      const organization = currentCalendarOrg.current;
      await queryClient.cancelQueries({ queryKey: ['calendar'] });
      if (
        !calendarMounted.current ||
        epoch !== permissionEpoch.current ||
        access !== currentCalendarAccess.current ||
        organization !== currentCalendarOrg.current
      )
        throw new LocalActionError('Your calendar access just changed. Refresh the page and try again.');
      const previousCaches = queryClient.getQueriesData<CalendarCacheData>({
        queryKey: ['calendar'],
      });
      queryClient.setQueriesData<CalendarCacheData>({ queryKey: ['calendar', 'day'] }, (old) =>
        patchAppointmentStatusInCache(old, appointmentId, status)
      );
      queryClient.setQueriesData<CalendarCacheData>({ queryKey: ['calendar', 'week'] }, (old) =>
        patchAppointmentStatusInCache(old, appointmentId, status)
      );
      // The detail panel is React state (not query-driven), so patch it directly.
      const previousDetail = selectedAppointmentDetail;
      if (selectedAppointmentDetail && selectedAppointmentDetail.id === appointmentId) {
        setSelectedAppointmentDetail({ ...selectedAppointmentDetail, ...statusPatch(status) });
      }
      return { previousCaches, previousDetail, epoch: permissionEpoch.current };
    },
    onError: (
      error: Error,
      variables,
      context
    ) => {
      // Roll back the optimistic patch (grid caches + detail panel).
      if (calendarMounted.current && context?.epoch === permissionEpoch.current) {
        context.previousCaches?.forEach(([key, data]) => queryClient.setQueryData(key, data));
        setSelectedAppointmentDetail((current) =>
          current?.id === variables.appointmentId ? context.previousDetail : current
        );
      }
      if (
        handleGroupedAppointmentError(error, {
          queryClient,
          onStaleGroupVersion: () => hydrateAppointmentDetail(variables.appointmentId),
        })
      )
        return;
      if (toastNoShowFeeLock(error, () => hydrateAppointmentDetail(variables.appointmentId))) return;
      const message = getApiErrorMessage(error, 'Failed to update status');
      alert(message);
    },
    onSuccess: (result, variables) => {
      // A Start whose tech isn't signed in on Turns, or an undone Done that now overlaps a booking.
      notifyVisitTurnOutcome({
        appointmentId: variables.appointmentId,
        turn: result.turn,
        reopenOverlap: result.reopenOverlap,
      });
      // One-tap Done alongside a POS has no confirm dialog: offer Undo, back to the status it had (the
      // server withdraws the review text). A repeat Done or a legacy checkout (its sale is gone) has none.
      if (variables.status !== 'COMPLETED' || !calendarAccess.alongsidePos) return;
      const previousStatus = result.previousStatus;
      if (!isDoneUndoableFrom(previousStatus)) return;
      const detail =
        selectedAppointmentDetail?.id === variables.appointmentId ? selectedAppointmentDetail : null;
      const restorable =
        detail && calendarAccess.canChangeStatus({ ...detail, status: 'COMPLETED' }, previousStatus, true);
      showDoneUndoToast(
        restorable ? () => void undoDone(variables.appointmentId, previousStatus) : undefined
      );
    },
    onSettled: (_result, error, variables) => {
      // The panel is React state, not query-driven: re-read it from the server after every status
      // write (success or failure) so it can never keep showing a status the server no longer has.
      if (selectedAppointmentDetail?.id === variables.appointmentId) {
        hydrateAppointmentDetail(variables.appointmentId);
      }
      // Reconcile with the server in the background — the grids, the WAITING FAB badge (whose list shape we don't
      // patch optimistically) and a grouped member's group panel (its version moved). A successful write broadcasts
      // and the card is already patched, so its realtime echo is that one read; a failed one (rolled back above),
      // or no live feed, re-reads here.
      if (error) scheduleCalendarRefresh(queryClient);
      else refreshCalendarAfterBroadcastWrite(queryClient);
    },
  });

  const uncancelMutation = useMutation({
    mutationFn: async (target: UncancelTarget) => {
      const { appointmentId, source } = normalizeUncancelTarget(target);
      // Grouped members send the current group version as a query param on uncancel — from the
      // explicitly re-read appointment (Undo), else from the open detail panel.
      const groupParam = groupVersionParam(
        source ??
          (selectedAppointmentDetail && selectedAppointmentDetail.id === appointmentId
            ? selectedAppointmentDetail
            : null)
      );
      const response = await calendarRequest(
        apiClient.post(`/appointments/${appointmentId}/uncancel`, undefined, {
          params: groupParam,
        })
      );
      return response.data.data as { id: string; status: string; previousStatus: string };
    },
    onSuccess: async (result, target) => {
      // Refetch calendar grid so the card returns to its active style/color
      scheduleCalendarRefresh(queryClient);
      // Un-cancelling a grouped member bumps the group version — refresh the group panel cache.
      const groupId =
        normalizeUncancelTarget(target).source?.group?.id ?? selectedAppointmentDetail?.group?.id;
      if (groupId) scheduleCalendarRefresh(queryClient, [groupDetailQueryKey(groupId)]);
      try {
        const response = await calendarRequest(apiClient.get(`/appointments/${result.id}`));
        // An Undo may land after the desk opened another visit — only refresh the panel it belongs to.
        setSelectedAppointmentDetail((current) =>
          !current || current.id === result.id ? (response.data.data as AppointmentDetailData) : current
        );
      } catch {
        // A failed or obsolete read must not restore a prior permission snapshot.
      }
    },
    onError: (error: Error, target) => {
      const { appointmentId } = normalizeUncancelTarget(target);
      if (
        handleGroupedAppointmentError(error, {
          queryClient,
          onStaleGroupVersion: () => hydrateAppointmentDetail(appointmentId),
        })
      )
        return;
      if (toastNoShowFeeLock(error, () => hydrateAppointmentDetail(appointmentId))) return;
      const message = getApiErrorMessage(error, 'Failed to un-cancel');
      alert(message);
    },
  });

  /**
   * Undo a one-tap Done: re-read the visit (its group version moved, and someone may have changed it
   * since) and put it back only while it is still Done — the server re-checks that under its lock.
   */
  const undoDone = async (appointmentId: string, previousStatus: string) => {
    try {
      const response = await calendarRequest(apiClient.get(`/appointments/${appointmentId}`));
      const fresh = response.data.data as AppointmentDetailData;
      if (fresh.status !== 'COMPLETED') {
        toast.error('This visit already changed, so there is nothing to undo', READABLE_TOAST_OPTIONS);
        return;
      }
      updateStatusMutation.mutate({
        appointmentId,
        status: previousStatus,
        force: true,
        skipNotification: true,
        reason: 'Undo Done',
        expectedStatus: 'COMPLETED',
        source: fresh,
      });
    } catch {
      toast.error("Couldn't undo Done", READABLE_TOAST_OPTIONS);
    }
  };

  /** Undo a one-tap No-show: re-read the visit (its group version moved) then restore it. */
  const undoOneTapNoShow = async (appointmentId: string) => {
    try {
      const response = await calendarRequest(apiClient.get(`/appointments/${appointmentId}`));
      uncancelMutation.mutate({ appointmentId, source: response.data.data as AppointmentDetailData });
    } catch {
      toast.error("Couldn't undo the no-show", READABLE_TOAST_OPTIONS);
    }
  };

  const notifyAppointmentMutation = useMutation({
    mutationFn: async (appointmentId: string) => {
      const response = await calendarRequest(
        apiClient.post(`/appointments/${appointmentId}/notify`, {
          notificationType: 'STAFF_READY',
          method: 'BOTH',
        })
      );
      return response.data.data as {
        appointmentId: string;
        notificationsSent: Array<{
          method: 'SMS' | 'EMAIL';
          status: 'SENT' | 'FAILED' | 'SKIPPED';
          error: string | null;
        }>;
      };
    },
    onSuccess: async (result) => {
      const sentCount = result.notificationsSent.filter((item) => item.status === 'SENT').length;
      const failed = result.notificationsSent.find((item) => item.status === 'FAILED');
      const skipped = result.notificationsSent.find((item) => item.status === 'SKIPPED');

      if (sentCount > 0) {
        toast.success(sentCount === 1 ? 'Notification sent' : 'Notifications sent');
      } else {
        toast.error(
          failed?.error || skipped?.error || 'No enabled client notifications configured'
        );
      }

      queryClient.invalidateQueries({ queryKey: getGetConversationsQueryKey() });
      // Refresh the waiting FAB list so its "Notified X ago" row updates.
      scheduleCalendarRefresh(queryClient, [['calendar', 'waiting', dateStr]]);

      // Re-fetch the detail so the panel flips to "Notified … / Send Again"
      // and the relative time resets (state lives on the server, not locally).
      if (sentCount > 0) {
        // The panel reopens from the grid cache, so stamp the notify there now (no "Notify" button
        // flashing back → no accidental second SMS) and refetch for the server's exact time.
        const notifiedPatch = { notifiedAt: new Date().toISOString() };
        for (const view of ['day', 'week'] as const) {
          queryClient.setQueriesData<CalendarCacheData>({ queryKey: ['calendar', view] }, (old) =>
            patchAppointmentInCache(old, result.appointmentId, notifiedPatch)
          );
        }
        scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
        try {
          const response = await calendarRequest(
            apiClient.get(`/appointments/${result.appointmentId}`)
          );
          const full = response.data.data as AppointmentDetailData;
          // Guard against the user closing/switching the panel mid-fetch.
          setSelectedAppointmentDetail((cur) => (cur && cur.id === full.id ? full : cur));
        } catch {
          /* best-effort — panel keeps prior state if re-fetch fails */
        }
      }
    },
    onError: (error: Error) => {
      const message = getApiErrorMessage(error, 'Failed to notify client');
      toast.error(message);
    },
  });

  // Split appointment mutation
  const splitAppointmentMutation = useMutation({
    mutationFn: async ({
      appointmentId,
      splitMode,
    }: {
      appointmentId: string;
      splitMode: string;
    }) => {
      const response = await calendarRequest(
        apiClient.post(`/appointments/${appointmentId}/split`, { splitMode })
      );
      return response.data.data as { originalAppointmentId: string; newAppointmentIds: string[] };
    },
    onSuccess: () => {
      scheduleCalendarRefresh(queryClient);
      setSelectedAppointmentDetail(null);
    },
    onError: (error: Error, variables) => {
      if (toastNoShowFeeLock(error, () => hydrateAppointmentDetail(variables.appointmentId))) return;
      const message =
        getApiErrorMessage(error, 'Failed to split appointment');
      alert(message);
    },
  });

  const legacyDoneInFlight = React.useRef(false);
  const [isClosingLegacyCheckout, setIsClosingLegacyCheckout] = React.useState(false);
  // Handle status change from detail panel (check-in, in-service, checkout)
  const handleStatusChange = React.useCallback(
    async (
      appointmentId: string,
      status: string,
      options?: { force?: boolean; skipNotification?: boolean; reason?: string }
    ) => {
      if (
        !selectedAppointmentDetail ||
        !(status.startsWith('CHECKOUT_')
          ? calendarAccess.canCheckout(selectedAppointmentDetail)
          : calendarAccess.canChangeStatus(selectedAppointmentDetail, status, options?.force))
      )
        return;
      // For CHECKOUT_CONTINUE: reopen checkout panel for CHECKING_OUT appointment (load existing sale)
      if (status === 'CHECKOUT_CONTINUE') {
        if (!selectedAppointmentDetail) return;

        try {
          // Fetch existing sale for this appointment
          const existingSale = await saleMutations.startAppointmentCheckout(appointmentId);

          // Open checkout panel with existing sale data
          setCheckoutSale(existingSale);
          setCheckoutAppointment(selectedAppointmentDetail);
          setPaymentSaleNotes(existingSale.notes || '');
          setIsPaymentView(false); // Start in checkout panel, not payment panel
          setSelectedAppointmentDetail(null);
        } catch (error) {
          const message = getApiErrorMessage(error, 'Failed to load checkout');
          toast.error(message);
        }
        return;
      }

      // Alongside a POS, Done on a visit left mid-checkout first discards its unpaid open sale
      // (the server refuses Done while it exists); a partly-paid one is never discarded.
      if (status === 'COMPLETED' && selectedAppointmentDetail.status === 'CHECKING_OUT') {
        if (!calendarAccess.canCloseLegacyCheckout(selectedAppointmentDetail)) return;
        // The sale lookup + delete run before the optimistic flip — ignore repeat taps meanwhile.
        if (legacyDoneInFlight.current) return;
        legacyDoneInFlight.current = true;
        setIsClosingLegacyCheckout(true);
        try {
          // Side-request guard: a 403 on the sale calls must not evict the whole calendar.
          const outcome = await abandonOpenSaleForDone(appointmentId, calendarSideRequest);
          if (outcome !== 'ready') {
            toast.error(LEGACY_CHECKOUT_BLOCKED_MESSAGE[outcome], READABLE_TOAST_OPTIONS);
            return;
          }
        } catch (error) {
          toast.error(
            isForbiddenError(error)
              ? LEGACY_CHECKOUT_FORBIDDEN_MESSAGE
              : getApiErrorMessage(error, 'Failed to mark as Done'),
            READABLE_TOAST_OPTIONS
          );
          return;
        } finally {
          legacyDoneInFlight.current = false;
          setIsClosingLegacyCheckout(false);
        }
      }

      // Checkout action opens a local preview; sale/status persist when user goes to payments.
      if (status === 'CHECKOUT_START') {
        if (!selectedAppointmentDetail) return;

        // Open checkout panel with local preview data (no API call)
        setCheckoutSale(buildLocalCheckoutSalePreview(selectedAppointmentDetail));
        setCheckoutAppointment(selectedAppointmentDetail);
        setIsPaymentView(false); // Start in checkout panel, not payment
        setSelectedAppointmentDetail(null);
      } else {
        updateStatusMutation.mutate({ appointmentId, status, ...options });
      }
    },
    [
      updateStatusMutation,
      selectedAppointmentDetail,
      calendarAccess,
      calendarSideRequest,
      saleMutations,
    ]
  );

  const handleNotifyAppointment = React.useCallback(
    (appointmentId: string) => {
      notifyAppointmentMutation.mutate(appointmentId);
    },
    [notifyAppointmentMutation]
  );

  // Handle split services from detail panel
  const handleSplitServices = React.useCallback(
    (appointmentId: string, mode: SplitMode) => {
      splitAppointmentMutation.mutate({ appointmentId, splitMode: mode });
    },
    [splitAppointmentMutation]
  );

  // Handle book appointment from form
  const handleBookAppointment = React.useCallback(
    (formData: AppointmentFormData) => {
      createAppointmentMutation.mutate({ formData });
    },
    [createAppointmentMutation]
  );

  // Switch from appointment form to time block form (transfers slot data)
  const handleSwitchToTimeBlock = React.useCallback(() => {
    if (!newAppointment || !calendarAccess.canManageTimeBlock(newAppointment.staffId)) return;
    setNewTimeBlock({
      staffId: newAppointment.staffId,
      staffName: newAppointment.staffName,
      dateTime: newAppointment.dateTime,
    });
    setNewAppointment(null);
    setPreviewData(null);
    setIsNewFormDirty(false);
  }, [newAppointment, calendarAccess]);

  // Handle time block preview changes from form
  const handleTimeBlockPreviewChange = React.useCallback(
    (data: TimeBlockPreviewData) => {
      setTimeBlockPreviewData(data);
      // Only update selectedDate when the calendar date actually changes
      // Compare BOTH dates in org timezone for consistency
      if (data.startTime) {
        // Same salon day keeps the same object (no re-render); else noon of the new salon day
        setSelectedDate((prev) => moveToSalonDay(prev, data.startTime!, orgTimezone));
      }
    },
    [orgTimezone]
  );

  // Handle time block creation
  const handleBookTimeBlock = React.useCallback(
    (formData: TimeBlockFormData) => {
      createTimeBlockMutation.mutate(formData);
    },
    [createTimeBlockMutation]
  );

  // Handle edit button click (pencil icon in detail panel)
  const handleEditAppointment = React.useCallback(() => {
    if (!selectedAppointmentDetail || !calendarAccess.canEditAppointment(selectedAppointmentDetail))
      return;
    setEditingAppointment(selectedAppointmentDetail);
    setSelectedAppointmentDetail(null);
  }, [selectedAppointmentDetail, calendarAccess]);

  // Handle cancel edit (back to detail panel) - checks dirty state
  const handleCancelEdit = React.useCallback(() => {
    if (editingAppointment) {
      setSelectedAppointmentDetail(editingAppointment);
    }
    setEditingAppointment(null);
    setEditPreviewData(null);
    setDragOverrides(null);
    setIsDragEditMode(false);
    setIsEditFormDirty(false);
  }, [editingAppointment]);

  const clearAppointmentEdit = React.useCallback(() => {
    setEditingAppointment(null);
    setEditPreviewData(null);
    setDragOverrides(null);
    setIsDragEditMode(false);
    setIsEditFormDirty(false);
  }, []);

  // Handle save from edit form
  const handleSaveAppointment = React.useCallback(
    (data: EditAppointmentSaveData) => {
      updateAppointmentMutation.mutate(data);
    },
    [updateAppointmentMutation]
  );

  // Build AppointmentDetailData from calendar data for instant display. The grid payload carries
  // every field the panel renders (add-ons, attribution, offer claim, notifiedAt, online-booking
  // diff), so the panel paints complete on first frame. Checks dayCalendarData (day view) then weekCalendarData.
  const buildDetailFromGridCache = React.useCallback(
    (appointmentId: string): AppointmentDetailData | null => {
      // Try day view data first, then week view data
      const apiApt =
        dayCalendarData?.appointments?.find((a) => a.id === appointmentId) ??
        weekCalendarData?.appointments?.find((a) => a.id === appointmentId);
      if (!apiApt) return null;
      return {
        id: apiApt.id,
        hasHiddenServices: apiApt.hasHiddenServices,
        startTime: apiApt.startTime,
        endTime: apiApt.endTime,
        status: apiApt.status,
        cancellationType: apiApt.cancellationType ?? null,
        statusBeforeCancellation: apiApt.statusBeforeCancellation ?? null,
        bookingSource: apiApt.bookingSource,
        bookedByPhoneAssistant: apiApt.bookedByPhoneAssistant,
        notifiedAt: apiApt.notifiedAt,
        reassignmentNote: apiApt.reassignmentNote,
        cameInAt: apiApt.cameInAt,
        primaryClient: apiApt.primaryClient
          ? {
              id: apiApt.primaryClient.id,
              firstName: apiApt.primaryClient.firstName,
              lastName: apiApt.primaryClient.lastName,
              email: apiApt.primaryClient.email ?? null,
              phone: apiApt.primaryClient.phone ?? null,
              avatarUrl: apiApt.primaryClient.avatarUrl,
              createdAt: apiApt.primaryClient.createdAt ?? '',
              notes: apiApt.primaryClient.notes ?? null,
              isDeleted: apiApt.primaryClient.isDeleted,
            }
          : null,
        services: apiApt.services.map((svc) => ({
          id: svc.id,
          service: { id: svc.serviceId, name: svc.serviceName, categoryName: null },
          staff: { id: svc.staffId, name: svc.staffName, avatarUrl: null, color: null },
          startTime: svc.startTime,
          endTime: svc.endTime,
          duration: svc.duration,
          priceCents: svc.priceCents,
          isStaffRequested: svc.isStaffRequested,
          lane: svc.lane,
          selectedOptions: svc.selectedOptions,
          notes: null,
        })),
        isSameTimeVisit: apiApt.isSameTimeVisit ?? false,
        notes: apiApt.notes,
        internalNotes: null,
        bookedAsName: apiApt.bookedAsName,
        attribution: apiApt.attribution,
        offerClaim: apiApt.offerClaim,
        prizeAward: apiApt.prizeAward,
        referralReward: apiApt.referralReward,
        cardHold: apiApt.cardHold,
        noShowFee: apiApt.noShowFee,
        clientOwedFees: apiApt.clientOwedFees,
        bookedAt: apiApt.bookedAt,
        bookedBy: apiApt.bookedBy,
        lastModifiedAt: apiApt.lastModifiedAt,
        lastModifiedBy: apiApt.lastModifiedBy,
        // Carry the group hint from the calendar cache so grouped writes have the version
        // immediately — before (or without) the async detail hydrate. Otherwise a grouped write
        // fired in the hydrate window (or after a hydrate failure) would omit the version and hit
        // GROUP_VERSION_REQUIRED. primaryAppointmentId is filled by the hydrate GET; only
        // version/truthiness are consumed from the partial.
        group: apiApt.group
          ? {
              id: apiApt.group.id,
              version: apiApt.group.version,
              primaryAppointmentId: null,
              memberCount: apiApt.group.memberCount,
            }
          : null,
      };
    },
    [dayCalendarData, weekCalendarData]
  );

  // Handle drag-and-drop: fetch detail, apply dragged service changes, open edit form.
  // Supports repeated drags: subsequent drags of the same appointment skip the API fetch
  // and just update the overrides + re-mount the form.
  // Detail panels/forms open instantly from cached calendar data, which carries every field the
  // panel renders. Still background-fetch the full detail as a silent refresh: it brings the
  // freshest state (another device may have edited since the grid loaded), the group's
  // `primaryAppointmentId`, internal notes and per-service notes. Guards against the user
  // switching panels mid-fetch.
  // Only the newest re-read may land: an older one (e.g. after Done) arriving after a newer one
  // (after its Undo) would otherwise put a stale status back on the panel.
  const latestHydrate = React.useRef(0);
  const hydrateAppointmentDetail = React.useCallback(
    (appointmentId: string) => {
      const epoch = permissionEpoch.current;
      const request = ++latestHydrate.current;
      calendarRequest(apiClient.get(`/appointments/${appointmentId}`))
        .then((res) => {
          if (epoch !== permissionEpoch.current || request !== latestHydrate.current) return;
          const full = res.data.data as AppointmentDetailData;
          // Refresh whichever surface is showing this appointment. This also serves as the group
          // stale-version recovery: re-fetching restores the freshest group version so a manual
          // retry uses it (the panel/edit form are React state, not query-driven, so invalidating
          // the calendar cache alone would leave a stale version and loop on the same error).
          setSelectedAppointmentDetail((cur) => (cur && cur.id === full.id ? full : cur));
          setEditingAppointment((cur) => (cur && cur.id === full.id ? full : cur));
          // The server's status is fresher than a card that missed its update (Waiting in the panel, Confirmed
          // green on the grid): re-read the grid. Skipped while a write is out — its own refresh settles the grid.
          if (queryClient.isMutating() === 0 && gridShowsOtherStatus(queryClient, full.id, full.status)) {
            scheduleCalendarRefresh(queryClient);
          }
        })
        .catch(() => {
          /* The request guard handles current denials; server-only fields stay absent otherwise. */
        });
    },
    [calendarRequest, queryClient]
  );

  // A change made elsewhere (kiosk / text check-in, another screen) reaches the grid first; re-read the open panel.
  usePanelStatusFollowsGrid(
    selectedAppointmentDetail?.id ?? null,
    selectedAppointmentDetail?.status,
    viewMode === 'week' ? weekCalendarData?.appointments : dayCalendarData?.appointments,
    hydrateAppointmentDetail
  );

  // Open the detail panel for an appointment the grid cache doesn't hold: skeleton now, full detail
  // from the server when it lands (dropped if the open was superseded meanwhile).
  const openDetailFromServer = React.useCallback(
    (appointmentId: string) => {
      pendingDetailIdRef.current = appointmentId;
      setPendingDetailId(appointmentId);
      // The skeleton must be what's on screen: drop the view-only surfaces that would outrank it or
      // keep showing (the previous appointment / time block) while the new one loads.
      setSelectedAppointmentDetail(null);
      setSelectedTimeBlockDetail(null);
      calendarRequest(apiClient.get(`/appointments/${appointmentId}`))
        .then((res) => {
          if (pendingDetailIdRef.current !== appointmentId) return;
          setSelectedAppointmentDetail(res.data.data as AppointmentDetailData);
        })
        .catch((error: unknown) => {
          // Denials are handled by the request guard (and an access change is not an axios error);
          // anything else — deleted appointment, network, 5xx — gets a toast instead of a silent close.
          if (pendingDetailIdRef.current !== appointmentId || !axios.isAxiosError(error)) return;
          const status = error.response?.status;
          if (status === 401 || status === 403) return;
          toast.error(status === 404 ? 'This appointment no longer exists' : "Couldn't load this appointment");
        })
        .finally(() => {
          if (pendingDetailIdRef.current === appointmentId) cancelPendingDetail();
        });
    },
    [calendarRequest, cancelPendingDetail]
  );

  const handleAppointmentDragEnd = React.useCallback(
    async (data: AppointmentDragEndData) => {
      // A dirty group draft (component-local reducer state) OR an in-progress add-member edit in the
      // open group panel must be confirmed before any drag opens/unmounts a panel — mirrors every
      // other navigation guard (click, waiting-select, slot-click, close). Without the second clause a
      // drag would silently discard the unsaved add-member edit.
      if ((newGroupAppointment && isGroupFormDirty) || (selectedGroupId && isGroupDetailDirty)) {
        setShowDiscardModal(true);
        return;
      }
      const source = data.appointmentId.startsWith('tb-')
        ? [...(dayCalendarData?.timeBlocks ?? []), ...(weekCalendarData?.timeBlocks ?? [])].find(
            (tb) => `tb-${tb.id}` === data.appointmentId
          )
        : buildDetailFromGridCache(data.appointmentId);
      if (
        !source ||
        ('staffId' in source
          ? !calendarAccess.canManageTimeBlock(source.staffId) ||
            !calendarAccess.canManageTimeBlock(data.newStaffId)
          : !calendarAccess.canEditAppointment(source) || !calendarAccess.canBook(data.newStaffId))
      )
        return;
      // A drag is a new intent: leave an open group reschedule, whose panel would hide the edit form.
      if (isReschedulingGroup) cancelGroupReschedule();
      if (!data.appointmentId.startsWith('tb-')) {
        setGridTarget({ appointmentId: data.appointmentId, staffId: data.newStaffId, at: data.newStartTime });
      }
      // --- Time block card drag (id starts with "tb-") ---
      if (data.appointmentId.startsWith('tb-')) {
        const realTimeBlockId = data.appointmentId.replace('tb-', '');
        // If already editing this time block, just update drag overrides
        if (editingTimeBlock && editingTimeBlock.id === realTimeBlockId) {
          setEditTimeBlockDragStartOverride(data.newStartTime);
          setEditTimeBlockDragStaffOverride(data.newStaffId);
          return;
        }
        // If viewing this time block detail, switch to edit mode with drag overrides
        if (selectedTimeBlockDetail && selectedTimeBlockDetail.id === realTimeBlockId) {
          setEditingTimeBlock(selectedTimeBlockDetail);
          setSelectedTimeBlockDetail(null);
          setEditTimeBlockDragStartOverride(data.newStartTime);
          setEditTimeBlockDragStaffOverride(data.newStaffId);
          return;
        }
        // First drag on a time block card: fetch detail and open edit form
        try {
          const response = await calendarRequest(apiClient.get(`/time-blocks/${realTimeBlockId}`));
          const detail = response.data.data as TimeBlockDetailData;
          if (!calendarAccess.canManageTimeBlock(detail.staffId)) return;
          // Clear any open panels
          setNewAppointment(null);
          setPreviewData(null);
          setIsNewFormDirty(false);
          setNewGroupAppointment(null);
          setGroupPreviews(null);
          setIsGroupFormDirty(false);
          setSelectedAppointmentDetail(null);
          setEditingAppointment(null);
          setEditPreviewData(null);
          setNewTimeBlock(null);
          setTimeBlockPreviewData(null);
          // Open edit time block form with drag overrides
          setEditingTimeBlock(detail);
          setEditTimeBlockDragStartOverride(data.newStartTime);
          setEditTimeBlockDragStaffOverride(data.newStaffId);
        } catch {
          // Silent fail
        }
        return;
      }

      const svcIdSuffix = data.serviceCardId.replace(`${data.appointmentId}-svc-`, '');

      // --- Grouped block drag ---
      // Dragging a group member's block moves only THAT person's appointment (new staff and/or time),
      // through the same edit form as any other card; the group keeps everyone else where they are.
      // Moving the whole group stays in the group panel's ••• → Reschedule. A group lives on one
      // salon day, so a week-view drop onto another day is refused up front with that pointer.
      const draggedSource = buildDetailFromGridCache(data.appointmentId);
      if (
        draggedSource?.group &&
        salonDateKey(data.newStartTime, orgTimezone) !==
          salonDateKey(new Date(draggedSource.startTime), orgTimezone)
      ) {
        toast.error(
          'Everyone in a group stays on the same day. To move the whole group, open it and choose Reschedule.',
          READABLE_TOAST_OPTIONS
        );
        return;
      }

      const newDropMinutes = orgTimezone
        ? getMinutesFromMidnightInTz(data.newStartTime, orgTimezone)
        : data.newStartTime.getHours() * 60 + data.newStartTime.getMinutes();

      // --- Subsequent drag (already in drag-edit mode for the same appointment) ---
      if (
        isDragEditMode &&
        editingAppointment &&
        editingAppointment.id === data.appointmentId &&
        dragOverrides
      ) {
        // Start from the form's current lines (Same time switch, Moves, add-ons made since the first drag),
        // since the form remounts with these overrides.
        const currentLines = editPreviewData?.services ?? dragOverrides.services;
        const dropped = applyCalendarLineDrop(currentLines, {
          instanceId: svcIdSuffix,
          newStaffId: data.newStaffId,
          newStaffName:
            staff.find((s) => s.id === data.newStaffId)?.name ||
            currentLines.find((svc) => svc.instanceId === svcIdSuffix)?.staffName ||
            '',
          newStartMinutes: newDropMinutes,
        });
        if (dropped.refusal !== undefined) {
          toast.error(dropped.refusal, READABLE_TOAST_OPTIONS);
          return;
        }
        const updatedServices = dropped.lines;
        const earliestMinutes = Math.min(...updatedServices.map((s) => s.overrideStartTime ?? 0));
        const newStartTime = orgTimezone
          ? createDateInTz(
              data.newStartTime,
              Math.floor(earliestMinutes / 60),
              earliestMinutes % 60,
              orgTimezone
            )
          : (() => {
              const d = new Date(data.newStartTime);
              d.setHours(Math.floor(earliestMinutes / 60), earliestMinutes % 60, 0, 0);
              return d;
            })();

        setDragOverrides({ services: updatedServices, startTime: newStartTime });
        setDragEditKey((prev) => prev + 1); // Force form re-mount with new values
        return;
      }

      // --- First drag or different appointment (need to check dirty state + fetch) ---
      if ((newAppointment && isNewFormDirty) || (editingAppointment && isEditFormDirty)) {
        setShowDiscardModal(true);
        return;
      }

      // Use cached calendar data for instant optimistic update (prevents snap-back glitch)
      const cachedDetail = buildDetailFromGridCache(data.appointmentId);
      if (!cachedDetail) return; // No cached data - cannot proceed

      // Build the lines from cached data immediately (synchronous - no API wait), in form order
      // (lane 0, then lane 1), then apply the drop: a same-time visit moves as a pair.
      const cachedLines = orderLinesByLane(cachedDetail.services).map((svc) => {
        const svcDate = new Date(svc.startTime);
        return {
          instanceId: svc.id,
          id: svc.service.id,
          name: svc.service.name,
          price: svc.priceCents,
          duration: svc.duration,
          staffId: svc.staff.id,
          staffName: svc.staff.name,
          requestNote: (svc.isStaffRequested ? 'this-person' : 'none') as 'this-person' | 'none',
          overrideStartTime: orgTimezone
            ? getMinutesFromMidnightInTz(svcDate, orgTimezone)
            : svcDate.getHours() * 60 + svcDate.getMinutes(),
          overrideDuration: svc.duration,
          lane: toVisitLane(svc.lane),
          selectedOptions: initialSelectedOptions(svc),
        };
      });
      const draggedLine = cachedLines.find((svc) => svc.instanceId === svcIdSuffix);
      const dropped = applyCalendarLineDrop(cachedLines, {
        instanceId: svcIdSuffix,
        newStaffId: data.newStaffId,
        newStaffName: staff.find((s) => s.id === data.newStaffId)?.name || draggedLine?.staffName || '',
        newStartMinutes: newDropMinutes,
      });
      if (dropped.refusal !== undefined) {
        toast.error(dropped.refusal, READABLE_TOAST_OPTIONS);
        return;
      }
      const updatedServices = dropped.lines;
      const earliestMinutes = Math.min(...updatedServices.map((s) => s.overrideStartTime ?? 0));
      const newStartTime = orgTimezone
        ? createDateInTz(
            data.newStartTime,
            Math.floor(earliestMinutes / 60),
            earliestMinutes % 60,
            orgTimezone
          )
        : (() => {
            const d = new Date(data.newStartTime);
            d.setHours(Math.floor(earliestMinutes / 60), earliestMinutes % 60, 0, 0);
            return d;
          })();

      // Clear any open forms
      setNewAppointment(null);
      setPreviewData(null);
      setIsNewFormDirty(false);
      setNewGroupAppointment(null);
      setGroupPreviews(null);
      setIsGroupFormDirty(false);
      setSelectedAppointmentDetail(null);

      // Open edit form with pre-applied drag changes IMMEDIATELY (card stays at new position)
      setEditingAppointment(cachedDetail);
      setDragOverrides({ services: updatedServices, startTime: newStartTime });
      setIsDragEditMode(true);
      setDragEditKey((prev) => prev + 1);
      // Silent refresh: the form already shows the stub's add-ons; the fresh detail re-bases rows
      // the staff hasn't touched, in case the grid cache is stale.
      hydrateAppointmentDetail(data.appointmentId);
    },
    [
      calendarRequest,
      hydrateAppointmentDetail,
      newAppointment,
      isNewFormDirty,
      newGroupAppointment,
      isGroupFormDirty,
      editingAppointment,
      isEditFormDirty,
      staff,
      isDragEditMode,
      dragOverrides,
      editPreviewData,
      editingTimeBlock,
      selectedTimeBlockDetail,
      orgTimezone,
      buildDetailFromGridCache,
      calendarAccess,
      dayCalendarData,
      weekCalendarData,
      isReschedulingGroup,
      cancelGroupReschedule,
      selectedGroupId,
      isGroupDetailDirty,
    ]
  );

  // Handle preview card drag-and-drop: updates form time/staff when preview is dragged to new slot
  const handlePreviewDragEnd = React.useCallback(
    (data: PreviewDragEndData) => {
      if (
        !(data.type === 'timeblock'
          ? calendarAccess.canManageTimeBlock(data.newStaffId)
          : calendarAccess.canBook(data.newStaffId))
      )
        return;
      // Compute minutes from midnight in org timezone (or browser-local if no timezone)
      const newMinutes = orgTimezone
        ? getMinutesFromMidnightInTz(data.newStartTime, orgTimezone)
        : data.newStartTime.getHours() * 60 + data.newStartTime.getMinutes();

      if (data.type === 'timeblock' && newTimeBlock) {
        // Update time block form position and staff
        const staffMember = staff.find((s) => s.id === data.newStaffId);
        setNewTimeBlock({
          staffId: data.newStaffId,
          staffName: staffMember?.name || newTimeBlock.staffName,
          dateTime: data.newStartTime,
        });
      } else if (data.type === 'appointment' && (newAppointment || newGroupAppointment)) {
        const staffMember = staff.find((s) => s.id === data.newStaffId);
        if (data.instanceId) {
          // Dragging a specific service preview card — update that service's time + staff.
          // In group mode this targets the member currently being edited (only draggable one).
          setPreviewDragUpdate({
            instanceId: data.instanceId,
            newStaffId: data.newStaffId,
            newStaffName: staffMember?.name || '',
            newStartMinutes: newMinutes,
          });
        } else if (newAppointment) {
          // Dragging the default (no-service) preview — update appointment position + staff
          setNewAppointment({
            ...newAppointment,
            staffId: data.newStaffId,
            staffName: staffMember?.name || newAppointment.staffName,
            dateTime: data.newStartTime,
          });
        }
      }
    },
    [newTimeBlock, newAppointment, newGroupAppointment, staff, orgTimezone, calendarAccess]
  );

  // State for passing drag updates to NewAppointmentForm (specific service drag)
  const [previewDragUpdate, setPreviewDragUpdate] = React.useState<{
    instanceId: string;
    newStaffId: string;
    newStaffName: string;
    newStartMinutes: number;
  } | null>(null);

  // Handle time block click - fetch detail from API and show detail panel
  const handleTimeBlockClick = React.useCallback(
    async (timeBlockId: string) => {
      // If any form is dirty, show discard modal
      if (
        (newAppointment && isNewFormDirty) ||
        (newGroupAppointment && isGroupFormDirty) ||
        (editingAppointment && isEditFormDirty) ||
        (editingTimeBlock && isEditTimeBlockDirty) ||
        (newTimeBlock && isTimeBlockFormDirty)
      ) {
        setShowDiscardModal(true);
        return;
      }
      try {
        const response = await calendarRequest(apiClient.get(`/time-blocks/${timeBlockId}`));
        const detail = response.data.data as TimeBlockDetailData;
        setSelectedTimeBlockDetail(detail);
        // Close any open forms/panels
        setNewAppointment(null);
        setPreviewData(null);
        setIsNewFormDirty(false);
        setNewGroupAppointment(null);
        setGroupPreviews(null);
        setIsGroupFormDirty(false);
        setNewTimeBlock(null);
        setTimeBlockPreviewData(null);
        setIsTimeBlockFormDirty(false);
        setSelectedAppointmentDetail(null);
        setEditingAppointment(null);
        setEditPreviewData(null);
        setEditingTimeBlock(null);
        setEditTimeBlockPreview(null);
      } catch {
        // Silent fail
      }
    },
    [
      calendarRequest,
      newAppointment,
      isNewFormDirty,
      newGroupAppointment,
      isGroupFormDirty,
      editingAppointment,
      isEditFormDirty,
      editingTimeBlock,
      isEditTimeBlockDirty,
      newTimeBlock,
      isTimeBlockFormDirty,
    ]
  );

  // Handle edit time block (pencil icon in detail panel)
  const handleEditTimeBlock = React.useCallback(() => {
    if (
      !selectedTimeBlockDetail ||
      !calendarAccess.canManageTimeBlock(selectedTimeBlockDetail.staffId)
    )
      return;
    setEditingTimeBlock(selectedTimeBlockDetail);
    setSelectedTimeBlockDetail(null);
  }, [selectedTimeBlockDetail, calendarAccess]);

  // Handle cancel edit time block - checks dirty state
  const handleCancelEditTimeBlock = React.useCallback(() => {
    if (editingTimeBlock) {
      setSelectedTimeBlockDetail(editingTimeBlock);
    }
    setEditingTimeBlock(null);
    setEditTimeBlockPreview(null);
    setIsEditTimeBlockDirty(false);
    setEditTimeBlockDragStartOverride(null);
    setEditTimeBlockDragStaffOverride(null);
  }, [editingTimeBlock]);

  // Handle save time block from edit form
  const handleSaveTimeBlock = React.useCallback(
    (data: EditTimeBlockSaveData) => {
      updateTimeBlockMutation.mutate(data);
    },
    [updateTimeBlockMutation]
  );

  // Handle appointment card click - show detail panel instantly from cached calendar data.
  const handleAppointmentClick = React.useCallback(
    (appointment: Appointment) => {
      // Time block cards use "tb-" prefix — route to time block handler
      if (appointment.id.startsWith('tb-')) {
        const realId = appointment.id.replace('tb-', '');
        handleTimeBlockClick(realId);
        return;
      }

      // If new form (or an in-progress group add-member edit) is dirty, confirm discard first.
      if (
        (newAppointment && isNewFormDirty) ||
        (newGroupAppointment && isGroupFormDirty) ||
        (selectedGroupId && isGroupDetailDirty)
      ) {
        setShowDiscardModal(true);
        return;
      }
      const appointmentId = appointment.appointmentId || appointment.id;
      // A new explicit selection supersedes any detail still loading from the server.
      cancelPendingDetail();

      // Show selection instantly (complete) from cached calendar data, then silently refresh it.
      // A grouped block opens the whole group's panel (loaded fresh) instead of a single detail.
      const detail = buildDetailFromGridCache(appointmentId);
      setGridTarget({
        appointmentId,
        groupId: detail?.group?.id,
        staffId: appointment.staffId,
        at: appointment.startTime,
      });
      setGroupMemberBack(null);
      if (detail?.group) {
        setSelectedGroupId(detail.group.id);
        setSelectedAppointmentDetail(null);
      } else {
        setSelectedGroupId(null);
        if (detail) {
          setSelectedAppointmentDetail(detail);
          hydrateAppointmentDetail(appointmentId);
        }
      }
      // Close any open forms/panels immediately
      setNewAppointment(null);
      setPreviewData(null);
      setIsNewFormDirty(false);
      setNewGroupAppointment(null);
      setGroupPreviews(null);
      setIsGroupFormDirty(false);
      setSelectedTimeBlockDetail(null);
      setNewTimeBlock(null);
      setTimeBlockPreviewData(null);
      // Close checkout panel if open
      setCheckoutSale(null);
      setCheckoutAppointment(null);
      setIsPaymentView(false);
      setPaymentSaleNotes('');
      // Close sale details panel if open
      setCompletedSale(null);
      setSaleSourceAppointment(null);
    },
    [
      newAppointment,
      isNewFormDirty,
      newGroupAppointment,
      isGroupFormDirty,
      selectedGroupId,
      isGroupDetailDirty,
      handleTimeBlockClick,
      buildDetailFromGridCache,
      hydrateAppointmentDetail,
      cancelPendingDetail,
    ]
  );

  // Open one group member's appointment in "member mode": the detail panel with a back chevron to
  // the group and an ordinal title ("{First/Second/…} Client"). Only that member's block stays
  // selected (the group highlight clears). The ordinal comes from the cached group's member order.
  const handleViewGroupMember = React.useCallback(
    (appointmentId: string) => {
      const gid = selectedGroupId;
      if (!gid) return;
      const cached = queryClient.getQueryData<GetAppointmentGroupsGroupId200>(
        groupDetailQueryKey(gid)
      );
      const idx = cached?.data.members.findIndex((m) => m.appointmentId === appointmentId) ?? -1;
      setGroupMemberBack({ groupId: gid, title: idx >= 0 ? ordinalClientLabel(idx) : 'Appointment' });
      setSelectedGroupId(null);
      setGroupPreviews(null);
      const detail = buildDetailFromGridCache(appointmentId);
      if (detail) {
        // Seed the group's primary from the cached group so the primary-member cancel guard is
        // correct immediately (buildDetailFromGridCache leaves primaryAppointmentId null until hydrate).
        if (detail.group && cached) {
          detail.group = {
            ...detail.group,
            primaryAppointmentId: cached.data.primaryAppointmentId,
          };
        }
        setSelectedAppointmentDetail(detail);
        hydrateAppointmentDetail(appointmentId);
      } else {
        // A member outside the loaded window: skeleton until the full detail GET lands.
        openDetailFromServer(appointmentId);
      }
    },
    [selectedGroupId, queryClient, buildDetailFromGridCache, hydrateAppointmentDetail, openDetailFromServer]
  );

  // Back chevron in member mode: return to the group panel it was opened from.
  const handleBackToGroup = React.useCallback(() => {
    const back = groupMemberBack;
    if (!back) return;
    setSelectedAppointmentDetail(null);
    setGroupMemberBack(null);
    setSelectedGroupId(back.groupId);
  }, [groupMemberBack]);

  // A single appointment joined (or created) a group via the S7 "Add to Group" section: seed the
  // group-detail cache from the response and open the booked group panel (S6), closing the single
  // detail. Mirrors the post-Book transition (groupBooking.onCreated) so no extra GET is needed —
  // the membership hook already invalidated ['calendar'] before this runs, so re-invalidating here
  // would only mark the just-seeded group stale and force a redundant background refetch.
  const handleAppointmentGrouped = React.useCallback((group: AppointmentGroupDetail) => {
    seedGroupDetailCache(queryClient, group);
    setSelectedAppointmentDetail(null);
    setGroupMemberBack(null);
    setSelectedGroupId(group.id);
  }, [queryClient]);

  // Phase 10 — a member was detached ("Remove from Group"). When the group closed (last member) or
  // the removal happened from member mode, navigate; when it happened from the open group panel the
  // dialog already refreshed the group cache, so nothing to do.
  const handleMemberRemoved = React.useCallback(
    (closed: boolean) => {
      const target = removeMemberTarget;
      const inMemberMode = !!groupMemberBack;
      setRemoveMemberTarget(null);
      if (closed) {
        setSelectedAppointmentDetail(null);
        setGroupMemberBack(null);
        setSelectedGroupId(null);
      } else if (inMemberMode) {
        setSelectedAppointmentDetail(null);
        setGroupMemberBack(null);
        setSelectedGroupId(target?.groupId ?? null);
      }
    },
    [removeMemberTarget, groupMemberBack]
  );

  // Confirm a group-panel member Cancel / Delete: carry the group's freshest cached version so the
  // individual write is accepted (the member's own detail panel isn't open here).
  const handleMemberCancelConfirm = React.useCallback(
    (type: CancellationType) => {
      const target = memberCancelTarget;
      setMemberCancelTarget(null);
      if (!target) return;
      const cached = queryClient.getQueryData<GetAppointmentGroupsGroupId200>(
        groupDetailQueryKey(target.groupId)
      );
      const version = cached?.data.version;
      if (typeof version !== 'number') {
        toast.error('This group was just updated — please try again');
        return;
      }
      deleteAppointmentMutation.mutate({
        appointmentId: target.appointmentId,
        type,
        groupContext: { version },
      });
    },
    [memberCancelTarget, queryClient, deleteAppointmentMutation]
  );

  // Open an appointment's detail panel from the Waiting Clients FAB.
  // Uses cached calendar data when available (complete for the panel; a background refresh keeps it
  // fresh), else fetches the full detail.
  const handleWaitingClientSelect = React.useCallback(
    (appointmentId: string) => {
      // A dirty group draft or in-progress add-member edit (component-local, lost on unmount) first.
      if ((newGroupAppointment && isGroupFormDirty) || (selectedGroupId && isGroupDetailDirty)) {
        setShowDiscardModal(true);
        return;
      }
      // Close a non-dirty group draft / group panel so nothing leaks behind the detail panel.
      setNewGroupAppointment(null);
      setGroupPreviews(null);
      setIsGroupFormDirty(false);
      setGroupMemberBack(null);
      const detail = buildDetailFromGridCache(appointmentId);
      // A grouped appointment opens the whole group's panel (same as clicking its block), so
      // member-level actions (Remove from Group, etc.) run in their proper group context.
      if (detail?.group) {
        setSelectedAppointmentDetail(null);
        setSelectedGroupId(detail.group.id);
        return;
      }
      setSelectedGroupId(null);
      if (detail) {
        setSelectedAppointmentDetail(detail);
        hydrateAppointmentDetail(appointmentId);
        return;
      }
      openDetailFromServer(appointmentId);
    },
    [
      buildDetailFromGridCache,
      hydrateAppointmentDetail,
      openDetailFromServer,
      newGroupAppointment,
      isGroupFormDirty,
      selectedGroupId,
      isGroupDetailDirty,
    ]
  );

  const handleTimeSlotClick = (staffId: string, time: Date) => {
    if (!calendarAccess.canBook(staffId) && !calendarAccess.canManageTimeBlock(staffId)) return;
    // If sale details panel is open, close it
    if (completedSale) {
      handleCloseSaleDetails();
      return;
    }

    // If payment panel is open, check dirty state before closing
    if (isPaymentView && checkoutSale) {
      if (isPaymentDirty) {
        setShowDiscardModal(true);
        return;
      }
      handleCancelCheckout();
      return;
    }

    // If checkout panel is open, check dirty state before closing
    if (checkoutSale) {
      if (isCheckoutDirty) {
        setShowDiscardModal(true);
        return;
      }
      handleCancelCheckout();
      return;
    }

    // If edit time block form is open, check dirty state before closing
    if (editingTimeBlock) {
      if (isEditTimeBlockDirty) {
        setShowDiscardModal(true);
        return;
      }
      handleCancelEditTimeBlock();
      return;
    }

    // If edit form is open, check dirty state before closing
    if (editingAppointment) {
      if (isEditFormDirty) {
        setShowDiscardModal(true);
        return;
      }
      clearAppointmentEdit();
      return;
    }

    // If time block detail panel is open, close it first
    if (selectedTimeBlockDetail) {
      setSelectedTimeBlockDetail(null);
      return;
    }

    // If detail panel is open, close it first
    if (selectedAppointmentDetail) {
      setSelectedAppointmentDetail(null);
      setGroupMemberBack(null);
      return;
    }

    // If the group detail panel is open, close it first (confirm an unsaved add-member edit).
    if (selectedGroupId) {
      if (isGroupDetailDirty) {
        setShowDiscardModal(true);
        return;
      }
      setSelectedGroupId(null);
      setGroupPreviews(null);
      setGroupMemberBack(null);
      return;
    }

    // If time block form is open, update position (same interaction as appointment form)
    if (newTimeBlock) {
      if (isTimeBlockFormDirty) {
        setShowDiscardModal(true);
        return;
      }
      const staffMember = staff.find((s) => s.id === staffId);
      if (!staffMember) return;
      if (!calendarAccess.canManageTimeBlock(staffId)) return;
      setNewTimeBlock({ staffId, staffName: staffMember.name, dateTime: time });
      return;
    }

    // If group draft is open, clicking another slot tries to close it
    if (newGroupAppointment) {
      if (isGroupFormDirty) {
        setShowDiscardModal(true);
        return;
      }
      setNewGroupAppointment(null);
      setGroupPreviews(null);
      return;
    }

    // If form is already open, clicking another slot tries to close it
    if (newAppointment) {
      if (isNewFormDirty) {
        setShowDiscardModal(true);
        return;
      }
      // A form opened for a caller (client / number already in) moves to the clicked slot instead — the
      // desk is picking the caller's time, not starting over.
      const slotStaff = staff.find((s) => s.id === staffId);
      if (newAppointment.prefill && slotStaff && calendarAccess.canBook(staffId)) {
        setNewAppointment({ ...newAppointment, staffId, staffName: slotStaff.name, dateTime: time });
        return;
      }
      setNewAppointment(null);
      setPreviewData(null);
      return;
    }

    // Open new appointment form
    const staffMember = staff.find((s) => s.id === staffId);
    if (!staffMember) return;

    if (!calendarAccess.canBook(staffId)) {
      setNewTimeBlock({ staffId, staffName: staffMember.name, dateTime: time });
      return;
    }
    setNewAppointment({
      staffId,
      staffName: staffMember.name,
      dateTime: time,
    });
  };

  // Week view: adapt time slot click to use selectedWeekStaffId
  const handleWeekTimeSlotClick = (_dateStr: string, time: Date) => {
    if (!selectedWeekStaffId) return;
    handleTimeSlotClick(selectedWeekStaffId, time);
  };

  // Switch the single form into a group draft, carrying the current client/services (no loss).
  const handleSwitchToGroup = React.useCallback(
    (payload: GroupSwitchPayload) => {
      if (!newAppointment) return;
      const hasServices = payload.services.length > 0;
      // client + services → committed first member; client only → pre-filled first-member editor.
      const firstMember: GroupMemberInput | undefined =
        payload.client && hasServices
          ? {
              client: payload.client,
              services: payload.services,
              note: payload.note || undefined,
              defaultStaff: { id: newAppointment.staffId, name: newAppointment.staffName },
            }
          : undefined;
      const firstClient = payload.client && !hasServices ? payload.client : undefined;
      const { staffId, staffName, dateTime } = newAppointment;
      setNewGroupAppointment({ staffId, staffName, dateTime, firstMember, firstClient });
      setNewAppointment(null);
      setPreviewData(null);
      setIsNewFormDirty(false);
    },
    [newAppointment]
  );

  // Keep the day-view calendar on the group's start day so member previews stay visible
  // (mirrors handlePreviewChange for the single form).
  const handleGroupStartDateChange = React.useCallback(
    (date: Date) => {
      if (viewMode !== 'day') return;
      setSelectedDate((prev) => moveToSalonDay(prev, date, orgTimezone));
    },
    [viewMode, orgTimezone]
  );

  // Switch the group draft back to the single form (the form confirms discard first if dirty).
  const handleSwitchToSingle = React.useCallback(() => {
    if (!newGroupAppointment) return;
    const { staffId, staffName, dateTime } = newGroupAppointment;
    setNewAppointment({ staffId, staffName, dateTime });
    setNewGroupAppointment(null);
    setGroupPreviews(null);
    setIsGroupFormDirty(false);
  }, [newGroupAppointment]);

  // Handle cancel checkout - just close panel (no API call since no Sale was created yet)
  // NOTE: Checkout Panel is UI-only preview, Sale is only created when clicking "GO TO PAYMENTS"
  // NOTE: Must be defined before handleClosePanel which references it
  const handleCancelCheckout = React.useCallback(() => {
    setCheckoutSale(null);
    setCheckoutAppointment(null);
    setIsPaymentView(false);
    setPaymentSaleNotes('');
  }, []);

  // Unsaved work in any open panel or form — every "close/replace the panel" path asks before losing it.
  const isCheckoutUnsaved = Boolean(checkoutSale && ((isPaymentView && isPaymentDirty) || isCheckoutDirty));
  const hasUnsavedPanel =
    isCheckoutUnsaved ||
    Boolean(
      (newGroupAppointment && isGroupFormDirty) ||
        (selectedGroupId && isGroupDetailDirty) ||
        // A group reschedule with a new date/time picked (discard clears the group, and the reschedule exits with it).
        (isReschedulingGroup && groupReschedule.changed) ||
        (newAppointment && isNewFormDirty) ||
        (newTimeBlock && isTimeBlockFormDirty) ||
        (editingAppointment && isEditFormDirty) ||
        (editingTimeBlock && isEditTimeBlockDirty)
    );

  // Close every panel and form at once, without asking (callers check `hasUnsavedPanel` first).
  const closeAllPanels = React.useCallback(() => {
    setNewAppointment(null);
    setPreviewData(null);
    setNewGroupAppointment(null);
    setGroupPreviews(null);
    setIsGroupFormDirty(false);
    setNewTimeBlock(null);
    setTimeBlockPreviewData(null);
    setSelectedAppointmentDetail(null);
    cancelPendingDetail();
    setSelectedGroupId(null);
    setGroupMemberBack(null);
    setIsGroupDetailDirty(false);
    setSelectedTimeBlockDetail(null);
    setEditingAppointment(null);
    setEditPreviewData(null);
    setEditingTimeBlock(null);
    setEditTimeBlockPreview(null);
    setDragOverrides(null);
    setIsDragEditMode(false);
    setIsNewFormDirty(false);
    setIsEditFormDirty(false);
    setIsTimeBlockFormDirty(false);
    setIsEditTimeBlockDirty(false);
    setEditTimeBlockDragStartOverride(null);
    setEditTimeBlockDragStaffOverride(null);
    setCheckoutSale(null);
    setCheckoutAppointment(null);
    setIsCheckoutDirty(false);
    setIsPaymentDirty(false);
    setIsPaymentView(false);
    setPaymentSaleNotes('');
    setGridTarget(null);
  }, [cancelPendingDetail]);

  // Handle cancel/close - close any open panel (force = bypass dirty check)
  const handleClosePanel = React.useCallback(
    (force?: boolean) => {
      // An open checkout/payment layer closes on its own first — back to the group or visit it came from.
      if (checkoutSale) {
        if (!force && isCheckoutUnsaved) {
          setShowDiscardModal(true);
          return;
        }
        handleCancelCheckout();
        setIsCheckoutDirty(false);
        setIsPaymentDirty(false);
        return;
      }
      if (!force && hasUnsavedPanel) {
        setShowDiscardModal(true);
        return;
      }
      closeAllPanels();
    },
    [checkoutSale, isCheckoutUnsaved, handleCancelCheckout, hasUnsavedPanel, closeAllPanels]
  );

  // Deep links replace whatever panel is open, unless it holds unsaved work: then the discard prompt shows
  // and the link isn't queued behind it (same as a slot click).
  const linkBlockedByUnsavedPanel = () => {
    if (!hasUnsavedPanel) return false;
    setShowDiscardModal(true);
    return true;
  };
  const closePanelsForLink = () => {
    setCompletedSale(null);
    setSaleSourceAppointment(null);
    closeAllPanels();
  };

  // `?visit` (Turns › "Visit on Calendar", Caller card › Open appointment) opens that visit like a
  // waiting-list pick, on its own salon day (week view: in its tech's week). A visit outside the loaded grid
  // moves the day once its detail lands from the server.
  const linkedVisitDayPending = React.useRef<string | null>(null);
  const showVisitDay = (visit: Pick<AppointmentDetailData, 'startTime' | 'services'>) => {
    setSelectedDate((prev) => moveToSalonDay(prev, new Date(visit.startTime), orgTimezone));
    if (viewMode !== 'week') return;
    const techId = visit.services.map((line) => line.staff?.id).find((id) => id && calendarAccess.canViewStaff(id));
    if (techId) setSelectedWeekStaffId(techId);
  };
  const openLinkedVisit = (appointmentId: string) => {
    if (linkBlockedByUnsavedPanel()) return;
    closePanelsForLink();
    const cached = buildDetailFromGridCache(appointmentId);
    linkedVisitDayPending.current = cached ? null : appointmentId;
    if (cached) showVisitDay(cached);
    handleWaitingClientSelect(appointmentId);
  };

  // `?book=<callId>` (Caller card › New appointment): the New Appointment form opens for that caller at the
  // next quarter hour, in the viewer's own column when they book it, else a bookable tech working that day.
  // `?rebook=<appointmentId>` opens the same way with that visit's client and services, in its first tech's column.
  const prefilledBookingAllowed = () => {
    if (linkBlockedByUnsavedPanel()) return false;
    if (staff.some((member) => calendarAccess.canBook(member.id))) return true;
    toast.error(staff.length ? "You can't book appointments" : 'No staff to book with yet', READABLE_TOAST_OPTIONS);
    return false;
  };
  const openPrefilledBooking = ({ prefill, start, schedules, rebookFrom }: {
    prefill: NonNullable<NonNullable<typeof newAppointment>['prefill']>;
    start: Date;
    schedules: BatchScheduleData | undefined;
    /** Rebooking: the past visit whose services the form starts with. */
    rebookFrom?: RebookVisit;
  }) => {
    if (!prefilledBookingAllowed()) return;
    // Who works is read from that day's schedules, not the day on screen (or still loading).
    const slotDay = salonDateKey(start, orgTimezone);
    const columns = staff.map((member) => ({
      ...member,
      isWorking: findStaffScheduleDay(schedules, member.id, slotDay)?.isWorking ?? false,
    }));
    const bookableColumn = (staffId: string | undefined) =>
      columns.find((member) => member.id === staffId && calendarAccess.canBook(member.id));
    const target =
      (rebookFrom && bookableColumn(rebookFirstTechId(rebookFrom))) ??
      pickDefaultBookingStaff(columns, calendarAccess.canBook, calendarAccess.ownStaffId);
    if (!target) return;
    const services = rebookFrom
      ? rebookServices(rebookFrom, {
          start,
          timezone: orgTimezone,
          bookableStaff: (staffId) => {
            const member = bookableColumn(staffId);
            return member ? { id: member.id, name: member.name } : null;
          },
          fallbackStaff: { id: target.id, name: target.name },
        })
      : undefined;
    closePanelsForLink();
    setSelectedDate((prev) => moveToSalonDay(prev, start, orgTimezone));
    if (viewMode === 'week') setSelectedWeekStaffId(target.id);
    setNewAppointment({ staffId: target.id, staffName: target.name, dateTime: start, prefill: { ...prefill, services } });
  };

  const deepLinks = React.useRef({ openLinkedVisit, showVisitDay, prefilledBookingAllowed, openPrefilledBooking });
  deepLinks.current = { openLinkedVisit, showVisitDay, prefilledBookingAllowed, openPrefilledBooking };
  // Every position and "today" is on the salon clock — links wait for it.
  React.useEffect(() => {
    if (!linkedVisitId || isLoadingTimezone) return;
    deepLinks.current.openLinkedVisit(linkedVisitId);
    void navigate({ to: '.', search: {}, replace: true });
  }, [linkedVisitId, isLoadingTimezone, navigate]);
  React.useEffect(() => {
    if (!selectedAppointmentDetail) return;
    const wanted = linkedVisitDayPending.current;
    linkedVisitDayPending.current = null;
    if (selectedAppointmentDetail.id === wanted) deepLinks.current.showVisitDay(selectedAppointmentDetail);
  }, [selectedAppointmentDetail]);

  // The URL is cleaned on arrival (a refresh never books again); the booking waits for staff columns, the
  // viewer's booking rights and the salon clock.
  const [pendingBooking, setPendingBooking] = React.useState<{ callId: string } | { rebookVisitId: string } | null>(
    null
  );
  React.useEffect(() => {
    if (!bookCallId && !rebookVisitId) return;
    setPendingBooking(bookCallId ? { callId: bookCallId } : { rebookVisitId: rebookVisitId! });
    void navigate({ to: '.', search: {}, replace: true });
  }, [bookCallId, rebookVisitId, navigate]);
  const prefilledBookingSeq = React.useRef(0);
  const canOpenPrefilledBooking = calendarAccess.ready && !isLoadingStaff && !isLoadingTimezone;
  React.useEffect(() => {
    if (!pendingBooking || !canOpenPrefilledBooking) return;
    setPendingBooking(null);
    if (!deepLinks.current.prefilledBookingAllowed()) return;
    const seq = ++prefilledBookingSeq.current;
    const epoch = permissionEpoch.current;
    const start = nextBookingSlotStart(new Date());
    // A caller the form can't load still gets a blank form (the desk is on the phone); a visit that can't be
    // loaded books nothing — there is nothing to rebook.
    const resolvePrefill: Promise<{ prefill: CallerBookingPrefill; rebookFrom?: RebookVisit } | null> =
      'callId' in pendingBooking
        ? resolveCallerBookingPrefill(queryClient, clerkOrgId, pendingBooking.callId, {
            canAccessDirectory: clientAccess.canAccessDirectory,
          }).then((prefill) => ({ prefill }))
        : calendarRequest(apiClient.get(`/appointments/${pendingBooking.rebookVisitId}`))
            .then((response) => {
              const visit = response.data.data as AppointmentDetailData;
              return { prefill: { client: rebookClient(visit) }, rebookFrom: visit };
            })
            .catch(() => null);
    void Promise.all([
      resolvePrefill,
      // No schedules → nobody reads as working: the default falls back to the first bookable column.
      queryClient
        .fetchQuery(staffSchedulesBatchQueryOptions(salonDateKey(start, orgTimezone), calendarRequest))
        .catch(() => undefined),
    ]).then(([resolved, schedules]) => {
      // Superseded by another link's booking, an access change, or leaving the page meanwhile.
      if (seq !== prefilledBookingSeq.current || epoch !== permissionEpoch.current || !calendarMounted.current) return;
      if (!resolved) {
        toast.error("Couldn't load that visit to rebook", READABLE_TOAST_OPTIONS);
        return;
      }
      const { clientLoadFailed, ...prefill } = resolved.prefill;
      if (clientLoadFailed) toast.error("Couldn't load the caller's client details", READABLE_TOAST_OPTIONS);
      deepLinks.current.openPrefilledBooking({
        prefill: { ...prefill, key: seq },
        start,
        schedules,
        rebookFrom: resolved.rebookFrom,
      });
    });
  }, [
    pendingBooking,
    canOpenPrefilledBooking,
    queryClient,
    clerkOrgId,
    clientAccess.canAccessDirectory,
    orgTimezone,
    calendarRequest,
  ]);

  // Confirm discard: close the dirty form without saving
  const handleConfirmDiscard = React.useCallback(() => {
    setShowDiscardModal(false);
    if (checkoutSale) {
      // Discarding checkout drops only the checkout layer — like its ×, it returns to the group
      // (or appointment) it was started from.
      handleCancelCheckout();
      setIsCheckoutDirty(false);
      setIsPaymentDirty(false);
      return;
    }
    if (newAppointment) {
      setNewAppointment(null);
      setPreviewData(null);
      setIsNewFormDirty(false);
    }
    if (newGroupAppointment) {
      setNewGroupAppointment(null);
      setGroupPreviews(null);
      setIsGroupFormDirty(false);
    }
    if (selectedGroupId) {
      setSelectedGroupId(null);
      setGroupPreviews(null);
      setGroupMemberBack(null);
      setIsGroupDetailDirty(false);
    }
    if (newTimeBlock) {
      setNewTimeBlock(null);
      setTimeBlockPreviewData(null);
      setIsTimeBlockFormDirty(false);
    }
    if (editingAppointment) {
      setEditingAppointment(null);
      setEditPreviewData(null);
      setDragOverrides(null);
      setIsDragEditMode(false);
      setIsEditFormDirty(false);
    }
    if (editingTimeBlock) {
      setEditingTimeBlock(null);
      setEditTimeBlockPreview(null);
      setIsEditTimeBlockDirty(false);
      setEditTimeBlockDragStartOverride(null);
      setEditTimeBlockDragStaffOverride(null);
    }
  }, [
    checkoutSale,
    handleCancelCheckout,
    newAppointment,
    newGroupAppointment,
    selectedGroupId,
    newTimeBlock,
    editingAppointment,
    editingTimeBlock,
  ]);

  // Cancel discard: go back to the form
  const handleCancelDiscard = React.useCallback(() => {
    setShowDiscardModal(false);
  }, []);

  const handleFiltersClick = () => {
    setIsFilterPanelOpen((prev) => !prev);
  };

  // Wrap date change — filter will auto-update via the useEffect below
  const handleDateChange = React.useCallback((date: Date) => {
    setSelectedDate(date);
  }, []);

  // Filter staff based on visibleStaffIds
  const filteredStaff = React.useMemo(() => {
    if (visibleStaffIds === null) return staff;
    return staff.filter((s) => visibleStaffIds.has(s.id));
  }, [staff, visibleStaffIds]);

  // A jumped-to day (Today, the date picker) still loading: the last loaded day stays on screen — dimmed, inert, a
  // progress bar on top — never a blank grid or a skeleton. The grid keeps that day's date until the new one lands.
  const isDayGridRefreshing = viewMode === 'day' && (isSchedulesPlaceholder || isDayPlaceholder);
  // The grid's date is the day its cards are for: the previous day's while that day is the placeholder.
  const placeholderDayKey = isDayPlaceholder ? dayCalendarResponse?.date : undefined;
  const dayGridDate = React.useMemo(
    () => (placeholderDayKey ? anchorSalonDateKey(placeholderDayKey, orgTimezone) : selectedDate),
    [placeholderDayKey, orgTimezone, selectedDate]
  );

  // Draft previews shown on the grid. Group drafts (new group / open group / group
  // reschedule) win over the single preview; the day grid hides the single preview
  // while an appointment is being edited (its cards are adjusted in place instead).
  const gridGroupPreviews = groupReschedule.isActive
    ? groupReschedule.previews
    : newGroupAppointment || selectedGroupId
      ? groupPreviews
      : null;
  const gridSinglePreview =
    viewMode === 'day' && editingAppointment ? null : activePreviewAppointment;

  // "Match opening hours" sources for the displayed day/week: the salon's hours plus the
  // working shifts on the grid (visible techs by day, the selected tech by week).
  // undefined = still loading (incl. the previous date's placeholder schedules).
  const isWeekView = viewMode === 'week';
  const openingHoursSource = React.useMemo(
    () =>
      isWeekView
        ? {
            businessHours: weekDaysData.map((day) => day.businessHours),
            // A failed schedules request falls back to opening hours alone (like the day view).
            shifts: weekScheduleQuery.data
              ? weekDaysData.flatMap((day) => (day.isWorking ? (day.shifts ?? []) : []))
              : weekScheduleQuery.isError
                ? []
                : undefined,
          }
        : {
            businessHours: [dayCalendarData ? (dayCalendarData.businessHours ?? null) : undefined],
            shifts: isSchedulesLoading
              ? undefined
              : filteredStaff.flatMap((member) => (member.isWorking ? (member.shifts ?? []) : [])),
          },
    [
      isWeekView,
      weekDaysData,
      weekScheduleQuery.data,
      weekScheduleQuery.isError,
      dayCalendarData,
      isSchedulesLoading,
      filteredStaff,
    ]
  );
  const { window: gridWindow, isPending: isGridWindowPending } = useCalendarGridWindow({
    settings: calendarDisplay,
    isSettingsLoading: isLoadingCalendarSettings,
    source: openingHoursSource,
    isSourceLoading: isWeekView
      ? isLoadingWeekCalendar || weekScheduleQuery.isLoading
      : isLoadingDayCalendar || isSchedulesLoading,
  });

  // Grid rows: the visible hours window (fixed, or fit to opening hours), auto-extended so no
  // card or draft preview on the displayed day/week (visible staff only) is ever cut off. Minute
  // math mirrors each grid's positioning: the week grid falls back to UTC, the day grid to browser-local.
  const gridHours = React.useMemo(() => {
    const isWeek = viewMode === 'week';
    const minuteTimezone = isWeek ? orgTimezone || 'UTC' : orgTimezone;
    const visibleStaffIdSet = new Set(filteredStaff.map((member) => member.id));
    const cards = isWeek
      ? allWeekCards
      : effectiveDayCards.filter((card) => visibleStaffIdSet.has(card.staffId));
    const toMinutes = (date: Date) =>
      minuteTimezone
        ? getMinutesFromMidnightInTz(date, minuteTimezone)
        : date.getHours() * 60 + date.getMinutes();
    const previewSpans = resolvePreviewCards(
      gridGroupPreviews ?? gridSinglePreview,
      null,
      minuteTimezone
    ).map((card) => ({
      startMinutes: card.startMinutes,
      endMinutes: card.startMinutes + card.durationMinutes,
    }));
    // Prep/cleanup zones count too, so a 6:50 prep before a 7:00 booking is not clipped.
    const blocksBuffer = (card: Appointment) => !card.isFadedCancel && !card.isBreak;
    return resolveCalendarGridHours(gridWindow, [
      ...cards.map((card) => ({
        startMinutes:
          toMinutes(card.startTime) - (blocksBuffer(card) ? card.bufferBeforeMinutes ?? 0 : 0),
        endMinutes: toMinutes(card.endTime) + (blocksBuffer(card) ? card.bufferAfterMinutes ?? 0 : 0),
      })),
      ...previewSpans,
    ]);
  }, [
    viewMode,
    filteredStaff,
    allWeekCards,
    effectiveDayCards,
    gridGroupPreviews,
    gridSinglePreview,
    gridWindow,
    orgTimezone,
  ]);

  // Check if filter is actively reducing the staff list
  const hasActiveFilter = visibleStaffIds !== null && visibleStaffIds.size < staff.length;

  // Compute the currently selected card ID for highlighting (appointments or time blocks)
  // Include editing/checkout states so the selection border persists while panel mode changes.
  const selectedCardId =
    selectedAppointmentDetail?.id ??
    editingAppointment?.id ??
    checkoutAppointment?.id ??
    completedSale?.appointmentId ??
    (selectedTimeBlockDetail ? `tb-${selectedTimeBlockDetail.id}` : null) ??
    (editingTimeBlock ? `tb-${editingTimeBlock.id}` : null);

  // Handle go to payments from checkout panel
  // Creates/loads Sale, persists checkout draft items, opens Payment panel
  const handleGoToPayments = React.useCallback(
    async (draft: CheckoutDraftPayload) => {
      // Group sale: the sale already exists (started server-side) — persist item edits and advance to
      // the payment panel. Payment completion + per-recipient paid states land in Phase 17.
      if (checkoutSale && isGroupSale(checkoutSale)) {
        setIsGoingToPayments(true);
        try {
          const patchPayload = buildSaleItemsPatchPayload(checkoutSale, draft);
          let saleForPayment = checkoutSale;
          if (hasSaleItemsPatchChanges(patchPayload)) {
            saleForPayment = await saleMutations.updateItems(checkoutSale, patchPayload);
          }
          setCheckoutSale(saleForPayment);
          setPaymentSaleNotes(draft.notes || saleForPayment.notes || '');
          setIsPaymentView(true);
          setIsCheckoutDirty(false);
          scheduleCalendarRefresh(queryClient);
        } catch (error) {
          // A stale sale must re-adopt the refetched version so the next click reconfirms against it
          // (otherwise the panel keeps its stale version and every retry loses the same race).
          if (error instanceof SaleStaleError) {
            if (error.sale) setCheckoutSale(error.sale);
            return;
          }
          if (isHandledSaleMutationError(error)) return;
          toast.error(getApiErrorMessage(error, 'Failed to open payments'));
        } finally {
          setIsGoingToPayments(false);
        }
        return;
      }

      if (!checkoutAppointment) return;

      setIsGoingToPayments(true);
      try {
        // 1. Ensure OPEN sale exists (or load existing one)
        const canonicalSale = await saleMutations.startAppointmentCheckout(checkoutAppointment.id);

        // 2. Persist checkout draft (add/update/remove line items) before opening payment panel
        const patchPayload = buildSaleItemsPatchPayload(canonicalSale, draft);
        let saleForPayment = canonicalSale;

        if (hasSaleItemsPatchChanges(patchPayload)) {
          saleForPayment = await saleMutations.updateItems(canonicalSale, patchPayload);
        }

        // 3. Open Payment panel with updated sale values
        setCheckoutSale(saleForPayment);
        setPaymentSaleNotes(draft.notes || saleForPayment.notes || '');
        setIsPaymentView(true);
        setIsCheckoutDirty(false); // Reset dirty state when going to payments

        // 4. Refresh calendar to show CHECKING_OUT status
        scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
      } catch (error) {
        // The sale hook already surfaced stale/identity conflicts; don't double-toast.
        if (isHandledSaleMutationError(error)) return;
        const message = getApiErrorMessage(error, 'Failed to start checkout');
        toast.error(message);
      } finally {
        setIsGoingToPayments(false);
      }
    },
    [checkoutSale, checkoutAppointment, queryClient, saleMutations]
  );

  // Group checkout: open the panel with the server-started sale (start/resume happens in the group
  // panel, which owns the group version + members). checkoutAppointment stays null (payer comes from
  // the sale itself), and selectedGroupId is kept so closing/discarding returns to the group panel.
  const handleGroupCheckoutStarted = React.useCallback((groupSale: SaleData) => {
    setCheckoutSale(groupSale);
    setCheckoutAppointment(null);
    setPaymentSaleNotes(groupSale.notes || '');
    setIsPaymentView(false);
    setIsCheckoutDirty(false);
  }, []);

  // Individual checkout of one grouped member (member ••• Checkout): opens the SINGLE checkout panel
  // via the individual endpoint path — the resulting sale carries no sourceGroupId. Keeps the group
  // selected so closing returns to the group panel.
  const handleMemberCheckout = React.useCallback(async (appointmentId: string) => {
    try {
      const response = await apiClient.get(`/appointments/${appointmentId}`);
      const detail = response.data.data as AppointmentDetailData;
      setCheckoutSale(buildLocalCheckoutSalePreview(detail));
      setCheckoutAppointment(detail);
      setPaymentSaleNotes('');
      setIsPaymentView(false);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Failed to open checkout'));
    }
  }, []);

  // Group checkout recipient subset change (add/remove) → PUT /sales/:id/appointments with the exact
  // remaining ids. A stale sale adopts the refetched version; the hook already toasted.
  const handleGroupRecipientChange = React.useCallback(
    async (nextAppointmentIds: string[]) => {
      if (!checkoutSale) return;
      setIsMutatingRecipients(true);
      try {
        const updated = await saleMutations.updateAppointments(checkoutSale, nextAppointmentIds);
        setCheckoutSale(updated);
        scheduleCalendarRefresh(queryClient);
      } catch (error) {
        if (error instanceof SaleStaleError) {
          if (error.sale) setCheckoutSale(error.sale);
          return;
        }
        if (isHandledSaleMutationError(error)) return;
        toast.error(getApiErrorMessage(error, 'Could not update recipients'));
      } finally {
        setIsMutatingRecipients(false);
      }
    },
    [checkoutSale, saleMutations, queryClient]
  );

  // Group checkout payer change (change-client flow): PATCH /sales/:id/client with identity.
  const handleGroupPayerChange = React.useCallback(
    async (client: { id: string } | null) => {
      if (!checkoutSale) return;
      try {
        const updated = await saleMutations.updateClient(checkoutSale, client?.id ?? null);
        setCheckoutSale(updated);
      } catch (error) {
        if (error instanceof SaleStaleError) {
          if (error.sale) setCheckoutSale(error.sale);
          return;
        }
        if (isHandledSaleMutationError(error)) return;
        toast.error(getApiErrorMessage(error, 'Could not change payer'));
      }
    },
    [checkoutSale, saleMutations]
  );

  // Discard a group checkout (••• Discard): DELETE the sale (members return to their prior status),
  // then close back to the group panel. Distinct from a plain close, which leaves the sale OPEN.
  const handleDiscardGroupCheckout = React.useCallback(async () => {
    if (!checkoutSale) return;
    try {
      await saleMutations.deleteSale(checkoutSale);
    } catch (error) {
      if (isHandledSaleMutationError(error)) return;
      toast.error(getApiErrorMessage(error, 'Could not discard checkout'));
      return;
    }
    setCheckoutSale(null);
    setCheckoutAppointment(null);
    setIsCheckoutDirty(false);
    scheduleCalendarRefresh(queryClient);
  }, [checkoutSale, saleMutations, queryClient]);

  // Handle back from payment panel to checkout panel
  const handleBackToCheckout = React.useCallback(() => {
    setIsPaymentView(false);
  }, []);

  // Persist sale-level notes onto the OPEN sale. Notes belong to the sale record
  // (rendered at the top of the sale detail), so they must be saved before the
  // sale is completed — the notes endpoint rejects non-open sales. Fire-and-forget
  // with error logging so a note failure never blocks checkout/payment.
  const persistSaleNotes = React.useCallback(
    async (sale: SaleData, notes: string) => {
      try {
        await saleMutations.updateNotes(sale, notes);
      } catch (error) {
        console.error('Failed to save sale notes', error);
      }
    },
    [saleMutations]
  );

  // Handle payment method selection - supports split/partial payments
  const handlePaymentMethodSelect = React.useCallback(
    async (method: PaymentMethod, amountCents: number, tipCents: number, giftCardId?: string) => {
      // A group checkout has no single originating appointment (`checkoutAppointment` is null — the
      // payer comes from the sale itself), so gate on the sale alone. Requiring an appointment here
      // would silently drop every group payment. The sale-details panel already reads the client via
      // optional chaining, so a null appointment is safe downstream.
      if (!checkoutSale) return;

      setIsProcessingPayment(true);
      try {
        // 0. Persist any pending sale notes while the sale is still OPEN. This
        //    guarantees a note typed in the checkout panel (and never re-edited
        //    in the payment panel) survives to the completed sale detail.
        if (paymentSaleNotes !== (checkoutSale.notes ?? '')) {
          await persistSaleNotes(checkoutSale, paymentSaleNotes);
        }

        // 1. Process partial payment. Gift card redemptions must carry the chosen
        //    card id, otherwise the API rejects them with "Gift card is required".
        const saleAmountCents = amountCents - tipCents;
        const { sale: updatedSale } = await saleMutations.addPayment(checkoutSale, {
          paymentMethod: method,
          amountCents: saleAmountCents,
          tipCents,
          ...(method === 'GIFT_CARD' && giftCardId ? { giftCardId } : {}),
        });
        if (giftCardId) {
          invalidateGiftCardCachesForSale(queryClient, updatedSale, giftCardId);
        }

        // 2. Check if fully paid: sum of payment amountCents >= subtotal
        const subtotalCents = updatedSale.lineItems.reduce(
          (sum: number, item: { totalCents: number }) => sum + item.totalCents,
          0
        );
        const paidAmountCents = updatedSale.payments.reduce(
          (sum: number, p: { amountCents: number }) => sum + p.amountCents,
          0
        );

        if (paidAmountCents >= subtotalCents) {
          // Fully paid - complete the sale. Pass the post-payment sale so a grouped sale's
          // completion carries the current version (individual sales ignore version).
          const completion = await saleMutations.complete(updatedSale);
          const completedSaleData = completion.sale;
          invalidateGiftCardCachesForSale(queryClient, completedSaleData, giftCardId);

          setCompletedSale(completedSaleData);
          setCheckoutSale(null);
          setCheckoutAppointment(null);
          setIsPaymentView(false);

          // A shared group sale completes EVERY selected member at once; the response lists all of
          // them in `appointments[]` (the legacy singular `appointment` is null for multi). Refresh
          // the grid (the `['calendar']` prefix also refreshes the open group panel) and each
          // affected appointment's cached sale state so a just-paid member stops offering checkout.
          for (const key of saleCompletionInvalidationKeys(completion.appointments, completedSaleData.lineItems)) {
            queryClient.invalidateQueries({ queryKey: key });
          }
          if (completedSaleData.sourceGroupId) {
            queryClient.invalidateQueries({
              queryKey: groupDetailQueryKey(completedSaleData.sourceGroupId),
            });
          }
          queryClient.invalidateQueries({ queryKey: ['sales'] });

          toast.success('Payment completed successfully!');
        } else {
          // Partial payment - update sale state to show new transaction
          setCheckoutSale(updatedSale);
        }
      } catch (error) {
        // The sale hook already surfaced stale/identity conflicts; don't double-toast.
        if (isHandledSaleMutationError(error)) return;
        const message = getApiErrorMessage(error, 'Failed to process payment');
        toast.error(message);
      } finally {
        setIsProcessingPayment(false);
      }
    },
    [checkoutSale, queryClient, paymentSaleNotes, persistSaleNotes, saleMutations]
  );

  // Handle sale notes change from payment panel. Persist the edited note to the
  // open sale immediately (fire-and-forget) and mirror it onto local sale state so
  // the payment-time guard skips a redundant save.
  const handlePaymentSaleNotesChange = React.useCallback(
    (notes: string) => {
      setPaymentSaleNotes(notes);
      setCheckoutSale((prev) => (prev ? { ...prev, notes } : prev));
      if (checkoutSale) {
        void persistSaleNotes(checkoutSale, notes);
      }
    },
    [checkoutSale, persistSaleNotes]
  );

  // Handle "View Sale" from completed appointment detail panel
  const handleViewSale = React.useCallback(
    async (appointmentId: string) => {
      try {
        const response = await calendarRequest(
          apiClient.get(`/sales/by-appointment/${appointmentId}`)
        );
        const saleData = response.data.data as SaleData;
        setSaleSourceAppointment(selectedAppointmentDetail);
        setCompletedSale(saleData);
        setSelectedAppointmentDetail(null);
      } catch (error) {
        const message =
          getApiErrorMessage(error, 'No sale found for this appointment');
        toast.error(message);
      }
    },
    [selectedAppointmentDetail, calendarRequest]
  );

  // "Text a link" / "Resend" on the panel's Card row: paint the returned state at once (panel + grid row), then let
  // the grid reconcile in the background.
  const handleCardHoldChanged = React.useCallback(
    (appointmentId: string, cardHold: AppointmentCardHold | null) => {
      setSelectedAppointmentDetail((cur) => (cur && cur.id === appointmentId ? { ...cur, cardHold } : cur));
      for (const view of ['day', 'week'] as const) {
        queryClient.setQueriesData<CalendarCacheData>({ queryKey: ['calendar', view] }, (old) =>
          patchAppointmentInCache(old, appointmentId, { cardHold })
        );
      }
      scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
    },
    [queryClient]
  );

  // A fee action (charge, waive, refund…) or a settled "Charging…": paint the answer at once (panel + grid row), then
  // let the grid reconcile in the background (it also brings the client's "Owes" list up to date).
  const handleNoShowFeeChanged = React.useCallback(
    (appointmentId: string, noShowFee: NoShowFeeView) => {
      setSelectedAppointmentDetail((cur) => (cur && cur.id === appointmentId ? { ...cur, noShowFee } : cur));
      for (const view of ['day', 'week'] as const) {
        queryClient.setQueriesData<CalendarCacheData>({ queryKey: ['calendar', view] }, (old) =>
          patchAppointmentInCache(old, appointmentId, { noShowFee })
        );
      }
      scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
      // A decided fee leaves "Fees to review" (an Undo puts it back): the header chip follows.
      invalidateFeesReview(queryClient);
    },
    [queryClient]
  );

  // After the attribution select saves (or the Offer row undoes a redemption), re-read the detail so
  // the pill/block/revenue/offer update, and refresh the calendar so the card's markers follow.
  const refreshSelectedAppointmentDetail = React.useCallback(async () => {
    const current = selectedAppointmentDetail;
    if (!current) return;
    const epoch = permissionEpoch.current;
    try {
      const response = await calendarRequest(apiClient.get(`/appointments/${current.id}`));
      const full = response.data.data as AppointmentDetailData;
      // The desk may have opened another appointment (or lost access) while this re-read was in flight.
      if (epoch === permissionEpoch.current) {
        setSelectedAppointmentDetail((cur) => (cur && cur.id === full.id ? full : cur));
      }
    } catch {
      // A failed re-read leaves the last-known detail in place; the calendar still refreshes.
    }
    scheduleCalendarRefresh(queryClient, CALENDAR_GRID_KEYS);
  }, [selectedAppointmentDetail, queryClient, calendarRequest]);

  // Handle closing sale details panel
  const handleCloseSaleDetails = React.useCallback(() => {
    setCompletedSale(null);
    setSaleSourceAppointment(null);
  }, []);

  // Group checkout panel options — only present when a group-sourced sale is open. Add-recipient
  // chips come from the group's checkout-eligible members not already claimed on the sale; name
  // hints let the panel fall back to a member's first name when the sale's snapshot is redacted.
  const groupCheckoutOptions = React.useMemo(() => {
    if (!checkoutSale || !isGroupSale(checkoutSale)) return undefined;
    const claims = checkoutSale.appointments ?? [];
    // The authoritative current selection = every recipient id on the sale. A hidden recipient's
    // claim id is redacted (null), but its service lines still carry the real appointment id, so
    // union both to rebuild the full set — otherwise remove/add would silently drop hidden recipients
    // (PUT /sales/:id/appointments replaces the whole set).
    const claimIds = claims.map((a) => a.appointmentId).filter((id): id is string => !!id);
    const lineIds = checkoutSale.lineItems
      .map((li) => li.appointmentId)
      .filter((id): id is string => !!id);
    const currentSelectionIds = Array.from(new Set([...claimIds, ...lineIds]));
    const currentSet = new Set(currentSelectionIds);
    // A recipient claim we can't map to a concrete id (fully redacted, lineless hidden member) means
    // we can't safely rebuild the exact set — lock recipient editing rather than drop that member.
    const recipientsLockedReason =
      claims.length > currentSelectionIds.length
        ? "Some clients in this checkout aren't visible to you"
        : undefined;
    const members = checkoutGroup?.members ?? [];
    return {
      nameHints: members.map((m) => ({
        appointmentId: m.appointmentId,
        firstName: m.client?.firstName ?? null,
      })),
      addableMembers: recipientsLockedReason
        ? []
        : members
            .filter(
              (m) =>
                m.client && m.allowedActions.checkout.allowed && !currentSet.has(m.appointmentId)
            )
            .map((m) => ({
              appointmentId: m.appointmentId,
              name:
                [m.client?.firstName, m.client?.lastName].filter(Boolean).join(' ').trim() ||
                'Client',
            })),
      onRemoveRecipient: (appointmentId: string) =>
        handleGroupRecipientChange(currentSelectionIds.filter((id) => id !== appointmentId)),
      onAddRecipient: (appointmentId: string) =>
        handleGroupRecipientChange([...currentSelectionIds, appointmentId]),
      onDiscard: handleDiscardGroupCheckout,
      recipientsLockedReason,
      isMutatingRecipients,
    };
  }, [
    checkoutSale,
    checkoutGroup,
    handleGroupRecipientChange,
    handleDiscardGroupCheckout,
    isMutatingRecipients,
  ]);

  // The side panel exists only while something is open: Esc closes it (through the same dirty
  // guards as ×), and the column it's about is scrolled back into view as the grid narrows.
  const isSidePanelOpen = panelMode !== 'none';
  // A payment step in flight re-selects its sale when it lands: neither Esc nor the phone Back may close it first.
  const isPaymentStepInFlight = isGoingToPayments || isProcessingPayment;
  const isLgUp = useMediaQuery(BREAKPOINT_QUERY.lgUp);
  const handleEscapeClosePanel = React.useCallback(() => {
    // Sale details sits above everything and `handleClosePanel` doesn't clear it: close just that view.
    if (completedSale) handleCloseSaleDetails();
    else handleClosePanel();
  }, [completedSale, handleCloseSaleDetails, handleClosePanel]);
  useCalendarPanelEscapeClose(isSidePanelOpen && !isPaymentStepInFlight, handleEscapeClosePanel);
  useRestoreFocusOnCalendarPanelClose(isSidePanelOpen);

  // What the open panel is about (tech + time): its grid column is kept in view as the panel opens.
  const panelAnchor = resolveCalendarPanelAnchor({
    panelMode,
    newAppointment,
    newGroupAppointment,
    newTimeBlock,
    selectedTimeBlockDetail,
    editingTimeBlock,
    editTimeBlockDragStaffOverride,
    editTimeBlockDragStartOverride,
    selectedAppointmentDetail,
    editingAppointment,
    dragStartOverride: dragOverrides?.startTime,
    selectedGroupId,
    gridTarget,
  });
  // Below lg the panel covers the grid; Back returns to the grid exactly where it was, so nothing to reveal.
  useRevealCalendarPanelColumnOnOpen(
    isSidePanelOpen && isLgUp,
    calendarColumnKeyForAnchor(panelAnchor, viewMode, orgTimezone)
  );

  // One identity each for the page's lifetime: the memoized grids (and their cards) skip the page's re-renders.
  const onGridAppointmentClick = useStableHandler(handleAppointmentClick);
  const onGridTimeSlotClick = useStableHandler(handleTimeSlotClick);
  const onGridWeekTimeSlotClick = useStableHandler(handleWeekTimeSlotClick);
  const onGridNonWorkAreaClick = useStableHandler(() => handleClosePanel());
  const onGridAppointmentDragEnd = useStableHandler(handleAppointmentDragEnd);
  const onGridPreviewDragEnd = useStableHandler(handlePreviewDragEnd);

  // Loading state — wait for staff AND their schedules (fetched in parallel) so the first
  // paint is the real calendar, not a transient "Nobody to show here". Also wait for the salon
  // timezone: before it loads, days and times would be read on the viewer's clock.
  if (isLoadingStaff || isLoadingSchedules || isLoadingTimezone) {
    return (
      <>
        <Helmet defer={false}>
          <title>{formatDocumentTitle('Calendar')}</title>
        </Helmet>
        <SplitLayout>
          <PageCanvas className="flex h-full min-h-0 flex-col">
            <CalendarPageSkeleton
              startHour={gridHours.startHour}
              endHour={gridHours.endHour}
              hourHeight={calendarHourHeight}
            />
          </PageCanvas>
        </SplitLayout>
      </>
    );
  }

  // No salon timezone after loading (the locations request failed): every day and time on this page
  // is on the salon clock, so never fall back to the viewer's clock — that would save wrong times.
  if (!orgTimezone) {
    return (
      <>
        <Helmet defer={false}>
          <title>{formatDocumentTitle('Calendar')}</title>
        </Helmet>
        <SplitLayout>
          <MarketingPageState
            title="Couldn't load your salon's time zone"
            description="The calendar shows and books every time on your salon's clock. Check your connection and try again."
            onRetry={() => void refetchLocations()}
          />
        </SplitLayout>
      </>
    );
  }

  // Render side panel content based on panel mode
  let sidePanelContent: React.ReactNode = null;
  if (panelMode === 'sale-details' && completedSale) {
    // Completed sale view - slides in from right
    sidePanelContent = (
      <CalendarPanelCard className="animate-slide-in-right">
        <SaleDetailsPanel
          sale={completedSale}
          clientInfo={
            checkoutAppointment?.primaryClient || saleSourceAppointment?.primaryClient
              ? {
                  id: (checkoutAppointment?.primaryClient || saleSourceAppointment?.primaryClient)!
                    .id,
                  firstName: (checkoutAppointment?.primaryClient ||
                    saleSourceAppointment?.primaryClient)!.firstName,
                  lastName: (checkoutAppointment?.primaryClient ||
                    saleSourceAppointment?.primaryClient)!.lastName,
                  avatarUrl: (checkoutAppointment?.primaryClient ||
                    saleSourceAppointment?.primaryClient)!.avatarUrl,
                  createdAt: (checkoutAppointment?.primaryClient ||
                    saleSourceAppointment?.primaryClient)!.createdAt,
                }
              : undefined
          }
          onMoreOptions={handleCloseSaleDetails}
          onClientClick={(clientId) => setDrawerClientId(clientId)}
          timezone={orgTimezone}
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'payment' && checkoutSale) {
    // Payment panel view (after "Go to Payments") - slides in from right
    sidePanelContent = (
      <CalendarPanelCard className="animate-slide-in-right">
        <PaymentPanel
          sale={checkoutSale}
          saleNotes={paymentSaleNotes}
          onSaleNotesChange={handlePaymentSaleNotesChange}
          onBack={handleBackToCheckout}
          onPaymentMethodSelect={handlePaymentMethodSelect}
          isProcessing={isProcessingPayment}
          timezone={orgTimezone}
          onDirtyStateChange={setIsPaymentDirty}
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'checkout' && checkoutSale) {
    // Transform staff list for dropdown format (firstName/lastName instead of name)
    const dropdownStaffList = staff.map((s) => {
      const nameParts = s.name.split(' ');
      return {
        id: s.id,
        firstName: nameParts[0] || '',
        lastName: nameParts.slice(1).join(' ') || '',
        avatarUrl: s.avatarUrl,
        canBeAssignedToProductSales: staffList.find((staffMember) => staffMember.id === s.id)
          ?.canBeAssignedToProductSales,
      };
    });

    sidePanelContent = (
      <CalendarPanelCard>
        <CheckoutPanel
          sale={checkoutSale}
          clientInfo={
            checkoutAppointment?.primaryClient
              ? {
                  id: checkoutAppointment.primaryClient.id,
                  firstName: checkoutAppointment.primaryClient.firstName,
                  lastName: checkoutAppointment.primaryClient.lastName,
                  avatarUrl: checkoutAppointment.primaryClient.avatarUrl,
                  createdAt: checkoutAppointment.primaryClient.createdAt,
                  notes: checkoutAppointment.primaryClient.notes,
                }
              : undefined
          }
          onClose={() => handleClosePanel()}
          onGoToPayments={handleGoToPayments}
          onCancelCheckout={handleCancelCheckout}
          showCloseButton
          onClientClick={(clientId) => setDrawerClientId(clientId)}
          onClientChange={groupCheckoutOptions ? handleGroupPayerChange : undefined}
          onHasChangesChange={setIsCheckoutDirty}
          staffList={dropdownStaffList}
          isLoading={isGoingToPayments}
          timezone={orgTimezone}
          groupCheckout={groupCheckoutOptions}
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'edit' && editingAppointment) {
    sidePanelContent = (
      <CalendarPanelCard>
        <EditAppointmentForm
          key={isDragEditMode ? dragEditKey : undefined}
          appointment={editingAppointment}
          onSave={handleSaveAppointment}
          onCancel={handleCancelEdit}
          onPreviewChange={handleEditPreviewChange}
          isSaving={updateAppointmentMutation.isPending}
          onDirtyChange={setIsEditFormDirty}
          initialServiceOverrides={dragOverrides?.services}
          initialStartTimeOverride={dragOverrides?.startTime}
          onClientClick={(clientId) => setDrawerClientId(clientId)}
          timezone={orgTimezone}
          // Member mode (opened from the group panel): "Update {Ordinal} Client" + client-notify toggle.
          titleOverride={groupMemberBack ? `Update ${groupMemberBack.title}` : undefined}
          showNotifyToggle={!!editingAppointment.group}
          sameStaffOverlapError={sameStaffOverlapMessage(updateAppointmentMutation.error)}
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'group-detail' && selectedGroupId) {
    sidePanelContent = (
      <CalendarPanelCard>
        <GroupAppointmentDetailPanel
          groupId={selectedGroupId}
          timezone={orgTimezone}
          canWrite={canWriteOperational}
          lateCancellationThresholdHours={lateCancellationThresholdHours}
          onClose={() => handleClosePanel(true)}
          onRequestClose={() => handleClosePanel()}
          onViewMember={handleViewGroupMember}
          onMemberCancelDelete={
            canWriteOperational
              ? (member) =>
                  setMemberCancelTarget({
                    groupId: selectedGroupId,
                    appointmentId: member.appointmentId,
                    name: member.name,
                  })
              : undefined
          }
          onClientClick={(clientId) => setDrawerClientId(clientId)}
          onMessageClient={hasOperationalAccess ? (client) => setChatClient(client) : undefined}
          onPreviewsChange={setGroupPreviews}
          onDirtyChange={setIsGroupDetailDirty}
          onReschedule={
            canWriteOperational ? () => groupReschedule.startFromMenu(selectedGroupId) : undefined
          }
          onResend={
            canWriteOperational
              ? () => setResendTarget({ groupId: selectedGroupId, appointmentId: null })
              : undefined
          }
          onCheckoutStarted={canWriteOperational ? handleGroupCheckoutStarted : undefined}
          onMemberCheckout={canWriteOperational ? handleMemberCheckout : undefined}
          onHighlightMember={highlightGroupMember}
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'group-reschedule') {
    sidePanelContent = (
      <CalendarPanelCard>
        {groupReschedule.chosenStartISO ? (
          <GroupRescheduleForm
            timezone={groupReschedule.timezone}
            chosenStartISO={groupReschedule.chosenStartISO}
            headerTimeMinutes={groupReschedule.headerTimeMinutes}
            memberRows={groupReschedule.memberRows}
            changed={groupReschedule.changed}
            canReschedule={groupReschedule.canReschedule}
            rescheduleReason={groupReschedule.rescheduleReason}
            sendNotifications={groupReschedule.sendNotifications}
            onToggleNotifications={groupReschedule.setSendNotifications}
            conflicts={groupReschedule.conflicts}
            isSubmitting={groupReschedule.isSubmitting}
            onDateChange={groupReschedule.setDate}
            onTimeChange={groupReschedule.setTime}
            onSave={groupReschedule.submit}
            onConfirmConflict={groupReschedule.confirmConflict}
            onDismissConflict={groupReschedule.dismissConflict}
            onCancel={groupReschedule.cancel}
          />
        ) : (
          <div className="flex h-full items-center justify-center bg-clovie-canvas text-[15px] text-clovie-muted">
            Loading group…
          </div>
        )}
      </CalendarPanelCard>
    );
  } else if (panelMode === 'detail' && selectedAppointmentDetail) {
    sidePanelContent = (
      <CalendarPanelCard>
        <AppointmentDetailPanel
          appointment={selectedAppointmentDetail}
          onBack={groupMemberBack ? handleBackToGroup : undefined}
          title={groupMemberBack?.title}
          // `hasSale` is intentionally omitted: /calendar/day (the detail source here) projects no
          // sale field, so a sale claim on an active-status appointment is caught server-side
          // (APPOINTMENT_HAS_SALE_CLAIM → toast, fails closed). The rule keeps the `hasSale` lever
          // ready to wire once that field is exposed.
          canAddToGroup={
            canWriteOperational &&
            canAddToGroup({
              enabled: groupBookingEnabled,
              isGrouped: !!selectedAppointmentDetail.group,
              status: selectedAppointmentDetail.status,
            })
          }
          // Single-location salon: scope the open-groups list by the org's primary location + tz.
          // Multi-location follow-up: derive both from the appointment's own location so the local
          // service date is correct across differing-tz locations at day boundaries.
          addToGroupContext={{
            locationId: resolvedLocationId,
            serviceDate: orgTimezone
              ? formatDateStrInTz(new Date(selectedAppointmentDetail.startTime), orgTimezone)
              : undefined,
          }}
          onAddedToGroup={handleAppointmentGrouped}
          onRemoveFromGroup={
            canWriteOperational && selectedAppointmentDetail.group
              ? () =>
                  setRemoveMemberTarget({
                    groupId: selectedAppointmentDetail.group!.id,
                    appointmentId: selectedAppointmentDetail.id,
                  })
              : undefined
          }
          onResend={
            canWriteOperational && selectedAppointmentDetail.group
              ? () =>
                  setResendTarget({
                    groupId: selectedAppointmentDetail.group!.id,
                    appointmentId: selectedAppointmentDetail.id,
                  })
              : undefined
          }
          onClose={() => handleClosePanel()}
          onEdit={
            calendarAccess.canEditAppointment(selectedAppointmentDetail)
              ? handleEditAppointment
              : undefined
          }
          onDeleteAppointment={
            calendarAccess.canEditAppointment(selectedAppointmentDetail) ||
            calendarAccess.canChangeStatus(selectedAppointmentDetail, 'NO_SHOW')
              ? (id, type, options) =>
                  deleteAppointmentMutation.mutate({
                    appointmentId: id,
                    type,
                    undoable: options?.undoable,
                  })
              : undefined
          }
          onStatusChange={canWriteOperational ? handleStatusChange : undefined}
          onUncancel={
            // The restore the server makes (what the visit held; In service for one cancelled mid-checkout alongside a
            // POS), as a restore (`force`): the same check as the panel's and the app's.
            calendarAccess.canChangeStatus(
              selectedAppointmentDetail,
              uncancelRestoreStatus(selectedAppointmentDetail, { alongsidePos: calendarAccess.alongsidePos }),
              true
            )
              ? (id) => uncancelMutation.mutate(id)
              : undefined
          }
          onNotify={
            calendarAccess.canNotify(selectedAppointmentDetail)
              ? handleNotifyAppointment
              : undefined
          }
          onSplitServices={
            calendarAccess.canEditAppointment(selectedAppointmentDetail)
              ? handleSplitServices
              : undefined
          }
          onViewSale={
            calendarAccess.canViewSale(selectedAppointmentDetail) ? handleViewSale : undefined
          }
          onClientClick={(clientId) => setDrawerClientId(clientId)}
          onMessageClient={hasOperationalAccess ? (client) => setChatClient(client) : undefined}
          canNotify={canNotifyWaitingClient}
          isUpdatingStatus={updateStatusMutation.isPending || isClosingLegacyCheckout}
          isUncancelling={uncancelMutation.isPending}
          isNotifying={notifyAppointmentMutation.isPending}
          isDeleting={deleteAppointmentMutation.isPending}
          isSplitting={splitAppointmentMutation.isPending}
          timezone={orgTimezone}
          lateCancellationThresholdHours={lateCancellationThresholdHours}
          readOnly={!canWriteOperational}
          onAttributionSaved={refreshSelectedAppointmentDetail}
          onOfferChanged={refreshSelectedAppointmentDetail}
          onPrizeChanged={refreshSelectedAppointmentDetail}
          onReferralChanged={refreshSelectedAppointmentDetail}
          cardRequest={calendarSideRequest}
          onCardHoldChanged={(cardHold) => handleCardHoldChanged(selectedAppointmentDetail.id, cardHold)}
          onNoShowFeeChanged={handleNoShowFeeChanged}
          onVisitChanged={refreshSelectedAppointmentDetail}
          // Send back removed the walk-in visit: back to its group when opened from one, else close.
          onVisitRemoved={
            groupMemberBack ? handleBackToGroup : () => setSelectedAppointmentDetail(null)
          }
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'new-form' && newAppointment) {
    sidePanelContent = (
      <CalendarPanelCard>
        <NewAppointmentForm
          key={newAppointment.prefill?.key}
          initialClient={newAppointment.prefill?.client}
          initialClientPhone={newAppointment.prefill?.phone}
          initialServices={newAppointment.prefill?.services}
          staffId={newAppointment.staffId}
          staffName={newAppointment.staffName}
          dateTime={newAppointment.dateTime}
          onCancel={() => handleClosePanel()}
          onBook={handleBookAppointment}
          onCreateTimeBlock={handleSwitchToTimeBlock}
          onPreviewChange={handlePreviewChange}
          isBooking={createAppointmentMutation.isPending}
          onDirtyChange={setIsNewFormDirty}
          previewDragUpdate={previewDragUpdate}
          onPreviewDragConsumed={() => setPreviewDragUpdate(null)}
          onClientClick={(clientId) => setDrawerClientId(clientId)}
          timezone={orgTimezone}
          groupBookingEnabled={groupBookingEnabled && viewMode === 'day'}
          onSwitchToGroup={handleSwitchToGroup}
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'new-group-form' && newGroupAppointment) {
    sidePanelContent = (
      <CalendarPanelCard>
        <GroupAppointmentForm
          staffId={newGroupAppointment.staffId}
          staffName={newGroupAppointment.staffName}
          dateTime={newGroupAppointment.dateTime}
          firstMember={newGroupAppointment.firstMember}
          firstClient={newGroupAppointment.firstClient}
          onStartDateChange={handleGroupStartDateChange}
          locationId={resolvedLocationId}
          timezone={orgTimezone}
          onCancel={() => handleClosePanel()}
          onSwitchToSingle={handleSwitchToSingle}
          onBook={groupBooking.book}
          isBooking={groupBooking.isBooking}
          onPreviewsChange={setGroupPreviews}
          onDirtyChange={setIsGroupFormDirty}
          onClientClick={(clientId) => setDrawerClientId(clientId)}
          previewDragUpdate={previewDragUpdate}
          onPreviewDragConsumed={() => setPreviewDragUpdate(null)}
        />
      </CalendarPanelCard>
    );
  } else if (panelMode === 'edit-time-block' && editingTimeBlock) {
    sidePanelContent = (
      <EditTimeBlockForm
        timeBlock={editingTimeBlock}
        onSave={handleSaveTimeBlock}
        onCancel={handleCancelEditTimeBlock}
        isSaving={updateTimeBlockMutation.isPending}
        onDirtyChange={setIsEditTimeBlockDirty}
        onPreviewChange={setEditTimeBlockPreview}
        dragStartTimeOverride={editTimeBlockDragStartOverride}
        dragStaffIdOverride={editTimeBlockDragStaffOverride}
        timezone={orgTimezone}
      />
    );
  } else if (panelMode === 'time-block-detail' && selectedTimeBlockDetail) {
    sidePanelContent = (
      <TimeBlockDetailPanel
        timeBlock={selectedTimeBlockDetail}
        onEdit={
          calendarAccess.canManageTimeBlock(selectedTimeBlockDetail.staffId)
            ? handleEditTimeBlock
            : undefined
        }
        onDelete={
          calendarAccess.canManageTimeBlock(selectedTimeBlockDetail.staffId)
            ? (id) => deleteTimeBlockMutation.mutate(id)
            : undefined
        }
        isDeleting={deleteTimeBlockMutation.isPending}
        onClose={() => handleClosePanel()}
        timezone={orgTimezone}
      />
    );
  } else if (panelMode === 'new-time-block' && newTimeBlock) {
    sidePanelContent = (
      <NewTimeBlockForm
        staffId={newTimeBlock.staffId}
        staffName={newTimeBlock.staffName}
        dateTime={newTimeBlock.dateTime}
        onCancel={() => handleClosePanel()}
        onCreateTimeBlock={handleBookTimeBlock}
        onPreviewChange={handleTimeBlockPreviewChange}
        isCreating={createTimeBlockMutation.isPending}
        onDirtyChange={setIsTimeBlockFormDirty}
        timezone={orgTimezone}
      />
    );
  } else if (panelMode === 'detail-loading') {
    sidePanelContent = (
      <CalendarPanelCard>
        <AppointmentDetailSkeleton
          title={groupMemberBack?.title}
          onBack={groupMemberBack ? handleBackToGroup : undefined}
          onClose={() => handleClosePanel()}
        />
      </CalendarPanelCard>
    );
  }

  return (
    <HighlightedAppointmentProvider value={highlightStore}>
      <Helmet defer={false}>
        <title>{formatDocumentTitle('Calendar')}</title>
      </Helmet>
      {/* "Jenna just checked in" — kiosk / QR / text-link arrivals (check-in salons only) */}
      <ArrivalToastHost organizationId={clerkOrgId} timezone={orgTimezone} />
      <SplitLayout
        sidePanel={sidePanelContent}
        sidePanelWidth="md"
        // Push layout: the grid gives up the panel's width only while it's open; enter is a short slide.
        sidePanelClassName="animate-calendar-panel-enter motion-reduce:animate-none"
        // Below lg an open panel takes the whole screen; Back closes it (same dirty guards as × / Esc) and the
        // grid comes back at the scroll it had.
        mobileDetailOpen={isSidePanelOpen}
        onMobileBack={handleEscapeClosePanel}
        mobileBackLabel="Calendar"
        mobileBackDisabled={isPaymentStepInFlight}
      >
        <PageCanvas className="flex h-full min-h-0 flex-col">
          <div className="relative flex min-h-0 flex-1 flex-col overflow-hidden rounded-[var(--r-2xl)] border border-clovie-border bg-clovie-surface shadow-soft-lg">
            <OperationsViewOnlyBanner />
            {/* Calendar Header - clicking closes any open panel */}
            <div onClick={() => handleClosePanel()}>
              <CalendarHeader
                selectedDate={selectedDate}
                viewMode={viewMode}
                onDateChange={handleDateChange}
                onViewModeChange={handleViewModeChange}
                onFiltersClick={handleFiltersClick}
                hasActiveFilter={hasActiveFilter}
                filterPanel={
                  isFilterPanelOpen ? (
                    <StaffFilterPanel
                      staff={staff}
                      visibleStaffIds={visibleStaffIds}
                      onFilterChange={setVisibleStaffIds}
                      onClose={() => setIsFilterPanelOpen(false)}
                    />
                  ) : null
                }
                selectedStaffId={selectedWeekStaffId ?? undefined}
                staffList={headerStaffList}
                onStaffChange={setSelectedWeekStaffId}
                timezone={orgTimezone}
                actions={
                  checkInOff ? (
                    <NewOnlineBookingsPill
                      organizationId={clerkOrgId}
                      timezone={orgTimezone}
                      request={calendarSideRequest}
                      onOpenBooking={(appointmentId) => deepLinks.current.openLinkedVisit(appointmentId)}
                    />
                  ) : salonSetup.checkIn ? (
                    // No-show fees need check-in (No-show protection): the chip only where it runs.
                    <CalendarFeesReview
                      organizationId={clerkOrgId}
                      timezone={orgTimezone}
                      request={calendarSideRequest}
                      onFeeChanged={handleNoShowFeeChanged}
                      onOpenVisit={handleWaitingClientSelect}
                    />
                  ) : null
                }
              />
            </div>

            {viewMode === 'week' && isGridWindowPending ? (
              /* First week load: rows not sized yet */
              <CalendarGridSkeleton
                startHour={gridHours.startHour}
                endHour={gridHours.endHour}
                hourHeight={calendarHourHeight}
              />
            ) : viewMode === 'week' ? (
              /* ---- WEEK VIEW ---- */
              <div
                // Touch screens swipe the grid: no scrollbar track taking width or showing.
                className="calendar-scrollbar flex-1 overflow-auto bg-clovie-surface focus:outline-none touch:scrollbar-none"
                data-calendar-scroll
                tabIndex={-1}
              >
                {/* Day column headers (Sat-Fri) */}
                <div onClick={() => handleClosePanel()}>
                  <WeekDayColumnHeader weekDays={weekDayHeaders} />
                </div>

                {/* Week time grid */}
                <WeekTimeGrid
                  weekDays={weekDaysData}
                  appointments={allWeekCards}
                  previewAppointment={gridSinglePreview}
                  previewAppointments={gridGroupPreviews}
                  startHour={gridHours.startHour}
                  endHour={gridHours.endHour}
                  hourHeight={calendarHourHeight}
                  showCurrentTime={true}
                  selectedAppointmentId={selectedCardId ?? null}
                  selectedGroupId={selectedGroupId}
                  dimmedGroupId={
                    groupReschedule.isActive && groupReschedule.changed
                      ? groupReschedule.groupId
                      : null
                  }
                  editingAppointmentId={editingAppointment?.id ?? null}
                  onAppointmentClick={onGridAppointmentClick}
                  onTimeSlotClick={canWriteOperational ? onGridWeekTimeSlotClick : undefined}
                  onNonWorkAreaClick={onGridNonWorkAreaClick}
                  timezone={orgTimezone}
                />
              </div>
            ) : (
              /* ---- DAY VIEW ---- */
              <>
                {isLoadingSchedules || isLoadingDayCalendar || isGridWindowPending ? (
                  /* First load: working staff or the day's cards not known yet — which columns show isn't known
                     (off-day staff with appointments stay) — or the opening hours that size the rows. A jump keeps
                     the previous day on screen, dimmed, instead. */
                  <CalendarGridSkeleton
                    startHour={gridHours.startHour}
                    endHour={gridHours.endHour}
                    hourHeight={calendarHourHeight}
                  />
                ) : filteredStaff.length > 0 ? (
                  <div className="relative flex min-h-0 flex-1 flex-col" aria-busy={isDayGridRefreshing || undefined}>
                  {isDayGridRefreshing && <CalendarGridRefreshBar />}
                  <div
                    ref={dayGridGeometry.scrollerRef}
                    className={cn(
                      'calendar-scrollbar flex-1 overflow-auto bg-clovie-surface focus:outline-none touch:scrollbar-none',
                      CALENDAR_DAY_SCROLLER_PHONE_SNAP_CLASS,
                      // Room to scroll the last slots out from under the Waiting Clients button.
                      'max-lg:pb-20',
                      // The previous day, still on screen while the new one loads: dimmed and inert (a click
                      // there would book on the wrong day).
                      'transition-opacity duration-200 motion-reduce:transition-none',
                      isDayGridRefreshing && 'pointer-events-none opacity-60'
                    )}
                    // Snapped columns land just past the sticky time gutter.
                    style={{ scrollPaddingLeft: dayGridGeometry.timeLabelWidth }}
                    data-calendar-scroll
                    tabIndex={-1}
                    // Keyboard too: nothing on the previous day can be opened or booked while it is dimmed.
                    inert={isDayGridRefreshing}
                  >
                    {/* Staff Column Headers with dropdown menu */}
                    <StaffColumnHeader
                      staff={filteredStaff}
                      timeLabelWidth={dayGridGeometry.timeLabelWidth}
                      minColumnWidth={dayGridGeometry.minColumnWidth}
                      onMenuAction={hasOperationalAccess ? handleStaffMenuAction : undefined}
                    />

                    {/* Calendar Grid */}
                    <CalendarTimeGrid
                      staff={filteredStaff}
                      appointments={effectiveDayCards}
                      selectedDate={dayGridDate}
                      previewAppointment={gridSinglePreview}
                      previewAppointments={gridGroupPreviews}
                      startHour={gridHours.startHour}
                      endHour={gridHours.endHour}
                      hourHeight={calendarHourHeight}
                      timeLabelWidth={dayGridGeometry.timeLabelWidth}
                      minColumnWidth={dayGridGeometry.minColumnWidth}
                      businessHours={dayCalendarData?.businessHours}
                      showCurrentTime={true}
                      selectedAppointmentId={selectedCardId ?? null}
                      selectedGroupId={selectedGroupId}
                      dimmedGroupId={
                        groupReschedule.isActive && groupReschedule.changed
                          ? groupReschedule.groupId
                          : null
                      }
                      editingAppointmentId={null}
                      onAppointmentClick={onGridAppointmentClick}
                      onTimeSlotClick={canWriteOperational ? onGridTimeSlotClick : undefined}
                      onNonWorkAreaClick={onGridNonWorkAreaClick}
                      onAppointmentDragEnd={
                        canWriteOperational ? onGridAppointmentDragEnd : undefined
                      }
                      onPreviewDragEnd={canWriteOperational ? onGridPreviewDragEnd : undefined}
                      timezone={orgTimezone}
                    />
                  </div>
                  </div>
                ) : staff.length > 0 ? (
                  /* All staff filtered out - show empty message */
                  <div className="flex flex-1 flex-col items-center justify-center bg-clovie-surface-2 px-6 text-center">
                    <p className="text-base font-medium text-clovie-muted">Nobody to show here.</p>
                    <p className="mt-1 text-base text-clovie-muted">
                      Choose a different view or{' '}
                      <Link
                        to="/$orgId/settings/staff"
                        params={{ orgId }}
                        className="font-medium text-clovie-brand underline hover:text-clovie-brand-dark"
                      >
                        change work schedules
                      </Link>
                      .
                    </p>
                  </div>
                ) : (
                  <div className="flex flex-1 items-center justify-center text-clovie-muted">
                    No service providers found. Add staff members in Settings.
                  </div>
                )}
              </>
            )}

            {/* Waiting Clients FAB - bottom-left badge + popover inside the grid card, scoped to viewed date */}
            {!checkInOff && <WaitingClientsFab date={dateStr} onSelectAppointment={handleWaitingClientSelect} />}
          </div>
        </PageCanvas>
      </SplitLayout>

      {/* Client Detail Drawer - opened from appointment detail or new appointment form */}
      <ClientDetailDrawer
        open={clientDetailDrawerClient !== null}
        onClose={() => setClientDetailDrawerClient(null)}
        client={clientDetailDrawerClient}
      />

      {/* Discard unsaved changes confirmation modal */}
      <DiscardChangesModal
        open={showDiscardModal}
        onDiscard={handleConfirmDiscard}
        onCancel={handleCancelDiscard}
      />

      {/* Staff double-booking confirmation ("Book anyway") */}
      <AppointmentConflictModal
        open={!!conflictPrompt}
        conflicts={conflictPrompt?.conflicts ?? []}
        isBooking={createAppointmentMutation.isPending}
        timezone={orgTimezone}
        onClose={() => setConflictPrompt(null)}
        onConfirm={() => {
          if (conflictPrompt) {
            createAppointmentMutation.mutate({
              formData: conflictPrompt.formData,
              allowConflict: true,
            });
          }
        }}
      />

      {/* Staff double-booking confirmation for a group Book ("Book anyway" → allowConflict) */}
      <AppointmentConflictModal
        open={groupBooking.conflicts !== null}
        conflicts={groupBooking.conflicts ?? []}
        isBooking={groupBooking.isBooking}
        timezone={orgTimezone}
        onClose={groupBooking.dismissConflict}
        onConfirm={groupBooking.confirmConflict}
      />

      {/* Staff double-booking confirmation for edits/drag-drop ("Book anyway") */}
      <AppointmentConflictModal
        open={!!editConflictPrompt}
        conflicts={editConflictPrompt?.conflicts ?? []}
        isBooking={updateAppointmentMutation.isPending}
        timezone={orgTimezone}
        onClose={() => setEditConflictPrompt(null)}
        onConfirm={() => {
          if (editConflictPrompt) {
            updateAppointmentMutation.mutate({
              ...editConflictPrompt.data,
              allowConflict: true,
            });
          }
        }}
      />

      {/* Remove a member from its group — opened from a member's detail panel (member mode) */}
      {removeMemberTarget && (
        <RemoveFromGroupDialog
          open
          groupId={removeMemberTarget.groupId}
          appointmentId={removeMemberTarget.appointmentId}
          onClose={() => setRemoveMemberTarget(null)}
          onRemoved={handleMemberRemoved}
        />
      )}

      {/* Resend notifications (S12) — group header (whole group) or a member's menu (single member) */}
      {resendTarget && (
        <ResendNotificationsDialog
          open
          groupId={resendTarget.groupId}
          targetAppointmentId={resendTarget.appointmentId}
          onClose={() => setResendTarget(null)}
        />
      )}

      {/* Cancel / Delete a non-primary member — opened from the group panel's member card menu */}
      <CancelDeleteAppointmentModal
        open={!!memberCancelTarget}
        onClose={() => setMemberCancelTarget(null)}
        onConfirm={handleMemberCancelConfirm}
        isLoading={deleteAppointmentMutation.isPending}
      />

      {/* Confirm "Set to Not Working" modal */}
      <Modal
        open={!!notWorkingConfirm}
        onClose={() => setNotWorkingConfirm(null)}
        title="Set to Not Working"
        titleAlign="center"
        size="sm"
        closeOnOverlayClick={false}
      >
        <p className="mb-8 text-center text-base text-clovie-muted">
          Do you want to override {notWorkingConfirm?.name}&apos;s schedule for{' '}
          {selectedDate.toLocaleDateString('en-US', {
            month: 'long',
            day: 'numeric',
            ...(orgTimezone ? { timeZone: orgTimezone } : {}),
          })}{' '}
          and set it to &quot;not working&quot;?
        </p>
        <ModalFooter align="center">
          <Button
            type="button"
            variant="cancel"
            shape="rounded"
            className="min-w-[120px] h-11"
            onClick={() => setNotWorkingConfirm(null)}
          >
            CANCEL
          </Button>
          <Button
            type="button"
            variant="action"
            shape="rounded"
            className="min-w-[120px] h-11"
            onClick={() => {
              if (notWorkingConfirm && calendarAccess.canEditSchedule(notWorkingConfirm.id)) {
                setNotWorkingMutation.mutate({
                  staffId: notWorkingConfirm.id,
                  data: { overrideDate: dateStr, isWorking: false },
                });
              }
              setNotWorkingConfirm(null);
            }}
          >
            CONFIRM
          </Button>
        </ModalFooter>
      </Modal>

      {/* Client Detail Drawer - opened when clicking client name */}
      <ClientDetailDrawerById clientId={drawerClientId} onClose={() => setDrawerClientId(null)} />

      {/* Client Message Drawer - opened from the appointment panel message icon */}
      <ClientQuickChatDrawer
        open={!!chatClient}
        onClose={() => setChatClient(null)}
        client={chatClient}
        timezone={orgTimezone}
      />
    </HighlightedAppointmentProvider>
  );
}

/** Wrapper that fetches client by ID and renders ClientDetailDrawer */
function ClientDetailDrawerById({
  clientId,
  onClose,
}: {
  clientId: string | null;
  onClose: () => void;
}) {
  const clientAccess = useClientPermissionAccess();
  const queryClient = useQueryClient();
  const drawerClientQueryKey = React.useMemo(
    () => ['client-for-drawer', clientId, clientAccess.scopeKey] as const,
    [clientId, clientAccess.scopeKey]
  );
  React.useEffect(
    () => () => {
      void queryClient.cancelQueries({ queryKey: drawerClientQueryKey, exact: true });
      queryClient.removeQueries({ queryKey: drawerClientQueryKey, exact: true });
    },
    [queryClient, drawerClientQueryKey]
  );
  const { data: client } = useQuery({
    queryKey: drawerClientQueryKey,
    queryFn: async ({ signal }) => {
      const res = await apiClient.get(`/clients/${clientId}`, { signal });
      return res.data.data;
    },
    enabled: !!clientId && clientAccess.ready,
    retry: false,
  });

  return (
    <ClientDetailDrawer
      open={!!clientId}
      onClose={onClose}
      client={client ?? null}
      onClientUpdate={(updatedClient) => {
        updateCachedClientLists(queryClient, updatedClient, clientAccess.scopeKey);
        queryClient.invalidateQueries({ queryKey: ['appointments'] });
      }}
    />
  );
}
